/**
 * "Book din Myselfie" product selector (sections/myselfie-product-selector.liquid).
 *
 * <myselfie-booking-calendar>  inline month grid; each day shows the selection's total
 *                              for that date and is disabled when it is sold out
 * <myselfie-product-selector>  duration, printer + prints, add-ons, occasion and the
 *                              "Vi anbefaler" package; "Book nu" adds every chosen
 *                              product with one POST /cart/add.js and opens /cart
 *
 * Contract shared with assets/myselfie-header.js, assets/api-dates.js and the cart page:
 *   - availability  window.myselfie.availability (load() / statusFor(iso, components)),
 *                   fails open; when the header script is absent every date is bookable
 *   - storage       sessionStorage "wdn_bookingdate" = "DD/MM/YYYY"
 *   - event         document "myselfie:date" {detail:{iso, display, source}}, dispatched
 *                   on select and listened to (nav picker <-> calendar stay in sync)
 *   - cart lines    properties Date (DD/MM/YYYY, same on every rental line - the cart page
 *                   empties a cart with two dates), _reason, _reason_andet
 *   - live cart     (data-cart-sync, setting "Hent varer fra kurven") the cart is the selection's
 *                   state: /cart.js is read into the selection on load and on focus/pageshow/Upcart
 *                   updates; every choice - the booth (M-, as soon as a date is chosen), printer,
 *                   logo, rolls, add-ons, packages, catalogue extras - is written to the cart at once
 *                   (liveSync: serialised passes, change.js per line, quiet add.js that does not open
 *                   the Upcart drawer). A line sold out on the chosen date is held at its old date and
 *                   an alert asks to remove it or pick another date. "Gå til betaling" finalises:
 *                   anything still different is removed/re-added with a snapshot (sessionStorage
 *                   myselfie_ps_txn, restored on failure), then attributes and checkout.
 *                   Design: _qa/cart-sync/live-spec.md
 *   - cart event    document "myselfie:cart" {detail:{item_count, items_subtotal_price, paid_count, cart}} after the
 *                   first cart read and after every cart write of the module (header CTA)
 *   - date + cart   (owner 2026-10-04) the booking date is remembered only while the cart has something in
 *                   it: when the cart is emptied ("Tøm kurven", the last line removed here, on /cart or in
 *                   another window) the date is forgotten - wdn_bookingdate, the draft's iso, the calendar -
 *                   and "myselfie:date" {iso:'', display:'', source, cleared:true} tells the header and the
 *                   product page. A page load with an empty cart is handled by snippets/myselfie-date-cart.
 *   - "fra" prices  without a date the durations show the lowest price the calendar can sell them for
 *                   (boothPriceSpan); the summary's booth line uses it too ("I alt fra").
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('myselfie-booking-calendar')) return;
  /* What "Gå til betaling" writes (attributes, note, discount codes, free props) and the cart API plumbing
     are shared with the DK cart page: assets/myselfie-checkout.js, loaded before this file. */
  var KIT = window.myselfie && window.myselfie.checkoutKit;
  if (!KIT) {
    if (window.console) console.error('[myselfie] assets/myselfie-checkout.js must load before myselfie-booking-calendar.js');
    return;
  }

  var STORAGE_KEY = 'wdn_bookingdate';
  var SOURCE = 'booking-calendar';
  var PRINTS_PER_ROLL = 400;
  // Booth (M-), printer (MYP) and Mygreetings (MYG) can be booked more than once; the cap matches
  // the per-cart limits in assets/api-dates.js (PRODUCT_MAP).
  var QTY_MAX = 5;
  function hasQty(variant) {
    return !!variant && /^(M-|MYP|MYG)/i.test(variant.sku || '');
  }
  /* Print type offered when the printer product has no print-type option. The choice is
     sent as the line item property "print_mode", which Myselfie.Orders reads before the
     SKU (ShopifyOrderExtensions.GetPrintMode): Polaroid -> CRM 2, Strip -> CRM 3. */
  /* ---------- i18n (window.myselfie.i18n, snippets/myselfie-i18n-*.liquid) ----------
     Every customer-facing text goes through tr(); the fallback is the Danish original, so DK renders
     the same even without the i18n snippet. Placeholders: [name]. */
  function tr(ns, key, fallback, vars) {
    var i = window.myselfie && window.myselfie.i18n;
    var s = i && i[ns] && i[ns][key];
    if (s == null || s === '') s = fallback;
    if (vars) for (var k in vars) s = String(s).split('[' + k + ']').join(vars[k]);
    return s;
  }
  /** booking.* key */
  function B(key, fallback, vars) {
    return tr('booking', key, fallback, vars);
  }
  function listOf(ns, key, fallback) {
    var v = String(tr(ns, key, fallback.join(','))).split(',').map(function (x) { return x.trim(); });
    return v.length === fallback.length ? v : fallback;
  }
  /* Print type offered when the printer product has no print-type option (the shown labels; the print_mode
     contract value is always Polaroid / Strip). */
  function printTypesDefault() {
    return [B('print_type_polaroid', 'Polaroid'), B('print_type_strip', 'Fotostribe')];
  }
  /** 'P' | 'S' | '' for a print type name in any store language (Fotostribe / Fotoremsa / Remsa / Streifen = strip). */
  function printKeyOf(value) {
    var k = KIT && KIT.printKey ? KIT.printKey(value) : '';
    if (k) return k;
    var v = String(value || '');
    return /polaroid/i.test(v) ? 'P' : /strip|stribe|remsa|streifen/i.test(v) ? 'S' : '';
  }
  function printModeFor(type) {
    var k = printKeyOf(type);
    return k === 'P' ? 'Polaroid' : k === 'S' ? 'Strip' : '';
  }
  function dayNames() {
    return listOf('booking', 'cal_weekdays', ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn']);
  }
  function monthNames() {
    return listOf('date', 'months', [
      'januar', 'februar', 'marts', 'april', 'maj', 'juni',
      'juli', 'august', 'september', 'oktober', 'november', 'december',
    ]);
  }
  /* low = little capacity left (incl. the shipping window); popular = many bookings for events on the day */
  function statusText(status) {
    var map = {
      available: B('status_available', 'Ledig'),
      popular: B('status_popular', 'Populær dato'),
      low: B('status_low', 'Få tilbage'),
      sold_out: B('status_sold_out', 'Udsolgt'),
      printer_only: B('status_printer_only', 'Kun med printer'),
    };
    return Object.prototype.hasOwnProperty.call(map, status) ? map[status] : '';
  }
  function statusTitle(status) {
    var map = {
      low: B('title_low', 'Få tilbage på denne dato'),
      popular: B('title_popular', 'Populær dato – mange har booket denne dag'),
      printer_only: B('title_printer_only', 'Myselfie er kun ledig sammen med printer denne dag'),
    };
    return Object.prototype.hasOwnProperty.call(map, status) ? map[status] : '';
  }

  function pad(n) {
    return (n < 10 ? '0' : '') + n;
  }
  function toIso(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }
  function isoToDate(iso) {
    var p = iso.split('-');
    return new Date(+p[0], +p[1] - 1, +p[2]);
  }
  function isoToDisplay(iso) {
    var p = iso.split('-');
    return p[2] + '/' + p[1] + '/' + p[0];
  }
  function displayToIso(s) {
    var m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || '').trim());
    return m ? m[3] + '-' + m[2] + '-' + m[1] : '';
  }
  function longDate(iso) {
    var d = isoToDate(iso);
    return tr('date', 'day_month_year', '[day]. [month] [year]', { day: d.getDate(), month: monthNames()[d.getMonth()], year: d.getFullYear() });
  }
  /** "14. november" this year, "9. januar 2027" otherwise (sold-out alerts, owner wording). */
  function alertDate(iso) {
    var d = isoToDate(iso);
    if (d.getFullYear() !== new Date().getFullYear()) return longDate(iso);
    return tr('date', 'day_month', '[day]. [month]', { day: d.getDate(), month: monthNames()[d.getMonth()] });
  }
  function shortDate(iso) {
    var d = isoToDate(iso);
    return B('date_short', '[day]. [mon].', { day: d.getDate(), mon: monthNames()[d.getMonth()].slice(0, 3) });
  }
  /** "1 måned" / "3 måneder" */
  function monthsText(n) {
    return n === 1 ? B('n_month', '[n] måned', { n: n }) : B('n_months', '[n] måneder', { n: n });
  }
  function tomorrowIso() {
    var t = new Date();
    return toIso(new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1));
  }
  /** Last bookable day (the calendar's max: today + maxYears). */
  function maxIso() {
    var t = new Date();
    var years = Number(window.myselfie && window.myselfie.maxYears) || 3;
    return toIso(new Date(t.getFullYear() + years, t.getMonth(), t.getDate()));
  }
  function nextIso(iso) {
    var d = isoToDate(iso);
    return toIso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
  }
  /** Number locale of the store (common.number_locale: da-DK, sv-SE, de-DE). */
  function numLocale() {
    return tr('common', 'number_locale', 'da-DK');
  }
  /** A formatted amount with the store's currency (common.money: "[amount] kr"). */
  function withCurrency(text) {
    return tr('common', 'money', '[amount] kr', { amount: text });
  }
  /** øre -> "1.499" (DK) / "1 499" (SE) */
  function amount(ore) {
    return Math.round(ore / 100).toLocaleString(numLocale());
  }
  function kr(ore) {
    return withCurrency(amount(ore));
  }
  var VAT_RATE = 0.25; // Danish moms (Swedish moms is 25% too); Shopify prices are VAT-inclusive
  var VAT_PCT = Math.round(VAT_RATE * 100);
  var CUSTOMER_KEY = 'myselfie_customer_type';
  /** VAT-inclusive øre -> "1.039,20 kr" excl. VAT (decimals only when needed). */
  function krExVat(ore) {
    return krDecimal(Math.round(ore / (1 + VAT_RATE)));
  }
  function krDecimal(ore) {
    var value = ore / 100;
    var whole = Math.abs(value - Math.round(value)) < 0.005;
    return withCurrency(value.toLocaleString(numLocale(), { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }));
  }
  function escapeHtml(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function readStored() {
    try {
      return displayToIso(sessionStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return '';
    }
  }
  function store(display) {
    try {
      sessionStorage.setItem(STORAGE_KEY, display);
    } catch (e) {
      /* storage blocked */
    }
  }
  /*
   * The selector's choices survive leaving the page: localStorage, 6 hours after the last change.
   * Consent boxes are never remembered.
   */
  var DRAFT_KEY = 'myselfie_ps_draft';
  var CARRY_KEY = 'myselfie_date_carry'; // snippets/myselfie-date-cart: a date picked on the previous page
  var DRAFT_TTL = 6 * 60 * 60 * 1000;
  function readDraft() {
    try {
      var d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (d && d.v === 1 && d.expires > Date.now()) return d;
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* storage blocked or unreadable */
    }
    return null;
  }
  function writeDraft(data) {
    try {
      data.v = 1;
      data.expires = Date.now() + DRAFT_TTL;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
    } catch (e) {
      /* storage blocked or full */
    }
  }
  function clearDraft() {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch (e) {
      /* storage blocked */
    }
  }
  function availability() {
    var a = window.myselfie && window.myselfie.availability;
    return a && typeof a.load === 'function' ? a : null;
  }
  function statusFor(iso, components) {
    var a = availability();
    if (!a || !a.data) return 'available';
    return a.statusFor(iso, components && components.length ? components : undefined);
  }
  /**
   * Lead block (shipping lead time, backend field "lead"): window.myselfie.availability.isLead(iso, components).
   * Such days are rendered like days before the first bookable date and never count for a legend item.
   */
  function isLead(iso, components) {
    var a = availability();
    if (!a || !a.data || typeof a.isLead !== 'function') return false;
    return a.isLead(iso, components && components.length ? components : undefined);
  }
  function leadText() {
    return B('lead_aria', 'kan ikke leveres');
  }
  var rootUrl = KIT.rootUrl;
  /** Rewrites an element only when its markup changed (keeps focus and CSS animations stable). */
  function setHtml(el, html) {
    if (el._html === html) return;
    el._html = html;
    el.innerHTML = html;
  }
  /* Cart API (assets/myselfie-checkout.js): JSON or a Danish error, never a parser error; 429s retried
     within the store's budget (1, 2, 4, 8, 15 s or Retry-After). */
  var cartJson = KIT.cartJson;
  var cartPost = KIT.cartPost;
  var cartGet = KIT.cartGet;
  var wait = KIT.wait;

  /* ---------- cart sync helpers (pure) ---------- */
  var CART_SOURCE = 'cart-sync';
  /* Adds from the module must not open the Upcart drawer: Upcart reads these two query
     parameters (initiator "never opens"), still refreshes its own cart and runs its rewards. */
  var QUIET_ADD = KIT.QUIET_ADD;
  var LIVE_KINDS = /^(printer|logo|rolls|addon|extra)$/;
  var TXN_KEY = 'myselfie_ps_txn';
  var TXN_MAX_AGE = 30 * 60 * 1000;
  var DATE_FROM_DRAFT = false; // true while the stored date only comes from the draft (see the IIFE below)
  /* Season variants may carry a suffix after the codes (e.g. M-W-HIGH, MYP-1-P-ML-HIGH): the codes are read from the
     front, as assets/api-dates.js does with its prefixes (_qa/seasons/spec.md). */
  var BUNDLE_PRINTER = /^MYP-(\d+)-([PS])-(ML|NL|EL)(?:-[A-Z0-9]+)*$/i;
  var BOOTH_SKU = /^M-[A-Z0-9]+(?:-[A-Z0-9]+)*$/i;
  /* "add to order" line (assets/script.js): never touched. Theme setting mys_add_to_order_variant, passed in the
     selector config (data-ps-config "addToOrderVariant"); null/0 = none. */
  var ADD_TO_ORDER_VARIANT = 0;
  /** print_mode (contract) -> the shown print type label */
  function printModeType(mode) {
    return mode === 'Polaroid' ? printTypesDefault()[0] : mode === 'Strip' ? printTypesDefault()[1] : '';
  }
  /*
   * Rental periods are recognised by the booth SKU (identical in every store): M-W weekend, M-1 / M-2 weekdays,
   * M-7 week, M-93 / M-185 / M-365 / M-730 lease (days). The option words ("Weekend", "Helg", "3 Måneder") are only
   * a fallback for a variant without SKU.
   */
  var WEEKEND_WORD = /weekend|helg|wochenende/i;
  var LEASE_WORD = /m[åa]ned|månad|monat|month/i;
  /** "W", "1", "93", … for a booth variant (season suffixes ignored), '' when the SKU is not a booth SKU. */
  function boothCode(v) {
    var sku = String((v && v.sku) || '').trim();
    return BOOTH_SKU.test(sku) ? sku.split('-')[1].toUpperCase() : '';
  }
  /** The booth SKU code of a rental period (option value) of a product, '' when no variant of it has one. */
  function durationCode(p, d) {
    if (!p || !p.variants) return '';
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (durOf(v, p) !== d) continue;
      var c = boothCode(v);
      if (c) return c;
    }
    return '';
  }
  function isWeekendDuration(p, d) {
    var c = durationCode(p, d);
    return c ? c === 'W' : WEEKEND_WORD.test(String(d || ''));
  }
  /** Lease days of a rental period (93, 185, …), -1 = a lease without a SKU, 0 = an event rental. */
  function leaseDays(p, d) {
    var c = durationCode(p, d);
    if (c) return /^\d+$/.test(c) && Number(c) >= 28 ? Number(c) : 0;
    return LEASE_WORD.test(String(d || '')) ? -1 : 0;
  }

  function sget(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function sset(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage blocked */
    }
  }
  function sdel(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      /* storage blocked */
    }
  }
  /** Free props, the prop holder and app rewards (Upcart) are never the customer's choice. */
  function isAutomaticLine(item, checkout) {
    var c = checkout || {};
    if (c.freeProps && item.variant_id === c.freeProps) return true;
    if (c.propHolder && item.variant_id === c.propHolder.id) return true;
    var p = item.properties || {};
    return Object.keys(p).some(function (k) { return k.indexOf('__upcart') === 0; });
  }
  /** Line item properties without the app-private ones (keys starting with "__"). */
  function cleanProps(props) {
    var out = {};
    if (!props || typeof props !== 'object') return out;
    Object.keys(props).forEach(function (k) {
      if (k.indexOf('__') !== 0) out[k] = props[k];
    });
    return out;
  }
  /** Print info properties ("Inkluderet: 400 prints") are informative only: never part of a line's identity. */
  function noInfo(props) {
    return KIT && KIT.stripPrintInfo ? KIT.stripPrintInfo(props) : props;
  }
  function withInfo(props, sku) {
    return KIT && KIT.withPrintInfo ? KIT.withPrintInfo(props, sku) : props;
  }
  function sameProps(a, b) {
    var x = noInfo(cleanProps(a));
    var y = noInfo(cleanProps(b));
    var kx = Object.keys(x);
    if (kx.length !== Object.keys(y).length) return false;
    return kx.every(function (k) { return Object.prototype.hasOwnProperty.call(y, k) && String(x[k]) === String(y[k]); });
  }
  function lineFingerprint(items) {
    return (items || []).map(function (it) { return it.key + ':' + it.quantity; }).sort().join('|');
  }
  function lineSnapshot(item) {
    return { id: item.variant_id, quantity: item.quantity, properties: cleanProps(item.properties) };
  }
  function snapshotToAdd(snap) {
    var out = { id: snap.id, quantity: snap.quantity };
    if (snap.properties && Object.keys(snap.properties).length) out.properties = snap.properties;
    return out;
  }
  /** True when the cart holds a line for every snapshot (same variant, same properties, enough quantity). */
  function cartHasAll(cart, snaps) {
    var pool = (cart.items || []).slice();
    return snaps.every(function (sn) {
      for (var i = 0; i < pool.length; i++) {
        var it = pool[i];
        if (it.variant_id === sn.id && it.quantity >= sn.quantity && sameProps(it.properties, sn.properties)) {
          pool.splice(i, 1);
          return true;
        }
      }
      return false;
    });
  }
  /**
   * Header contract: the cart's count and subtotal after every read/write of the module (KIT.cartDetail:
   * item_count, items_subtotal_price, paid_count = the count shown, cart = the full cart for the global cart
   * rules, assets/myselfie-cart-rules.js).
   * Cart page (data-page="cart"): the detail also carries the kind of every line
   * (booth|live|kept|auto|replace|bundle) and the sync status (assets/mys-cart.js renders from it).
   */
  function announceCart(cart, source) {
    if (!cart || typeof cart.item_count !== 'number') return;
    var detail = KIT.cartDetail ? KIT.cartDetail(cart) : { item_count: cart.item_count, items_subtotal_price: cart.items_subtotal_price };
    var page = document.querySelector('myselfie-product-selector[data-page="cart"]');
    if (page && page.cartSync && typeof page.cartKinds === 'function') {
      detail.cart = cart.items ? cart : null;
      detail.lines = cart.items ? page.cartKinds(cart) : [];
      detail.status = page.cartSync.status;
      detail.source = source || '';
    }
    try {
      document.dispatchEvent(new CustomEvent('myselfie:cart', {
        detail: detail,
      }));
    } catch (e) {
      /* old browser */
    }
  }
  /** Properties for change.js: the new ones, plus the line's app-private ("__") ones. */
  function withPrivateProps(current, props) {
    var out = {};
    Object.keys(current || {}).forEach(function (k) {
      if (k.indexOf('__') === 0) out[k] = current[k];
    });
    Object.keys(props || {}).forEach(function (k) { out[k] = props[k]; });
    return out;
  }
  /**
   * Variants offered as "desired" vs lines in the cart: keeps what is identical (variant, quantity,
   * properties), removes the rest, adds what is missing. Merging identical lines is Shopify's
   * behaviour, so an identical line must never be re-added.
   */
  function diffCart(managed, desired) {
    var keep = [];
    var add = [];
    var pool = managed.slice();
    desired.forEach(function (d) {
      var found = -1;
      for (var i = 0; i < pool.length; i++) {
        var m = pool[i];
        if (m.variant_id === d.id && m.quantity === d.quantity && sameProps(m.properties, d.properties)) {
          found = i;
          break;
        }
      }
      if (found > -1) keep.push(pool.splice(found, 1)[0]);
      else add.push(d);
    });
    return { keep: keep, remove: pool, add: add };
  }

  /** Shopify CDN image at a given width ("//cdn…" -> "https://cdn…?width=…"). */
  function img(src, width) {
    if (!src) return '';
    var url = String(src).replace(/^\/\//, 'https://');
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }
  /** /products/<handle>.js -> common product shape (prices in øre). */
  function productFromJs(j) {
    return {
      handle: j.handle,
      title: j.title,
      body: j.description || '',
      images: (j.images || []).slice(),
      tags: (j.tags || []).map(function (t) { return String(t).toLowerCase(); }),
      type: j.type || '',
      options: (j.options || []).map(function (o) { return typeof o === 'string' ? o : (o && o.name) || ''; }),
      variants: (j.variants || []).map(function (v) {
        return { id: v.id, title: v.title, price: v.price, available: v.available, options: v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }) };
      }),
    };
  }
  /** /collections/<handle>/products.json item -> common product shape (prices in øre). */
  function productFromJson(p) {
    return {
      handle: p.handle,
      title: p.title,
      body: p.body_html || '',
      images: (p.images || []).map(function (i) { return i.src; }),
      tags: (p.tags || []).map(function (t) { return String(t).toLowerCase(); }),
      type: p.product_type || '',
      options: (p.options || []).map(function (o) { return typeof o === 'string' ? o : (o && o.name) || ''; }),
      variants: (p.variants || []).map(function (v) {
        return { id: v.id, title: v.title, price: Math.round(parseFloat(v.price) * 100), available: v.available !== false,
          options: [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }) };
      }),
    };
  }
  /** Short pitch: a list metafield (array), a multi-line text, or nothing. */
  function pitchLines() {
    for (var i = 0; i < arguments.length; i++) {
      var v = arguments[i];
      if (!v) continue;
      var list = Array.isArray(v) ? v : String(v).split(/\r?\n/);
      list = list.map(function (x) { return String(x).trim(); }).filter(Boolean);
      if (list.length) return list;
    }
    return [];
  }
  /* ---------- season pricing (assets/myselfie-seasons.js, _qa/seasons/spec.md) ---------- */
  /** The season kit, or null (then every product is treated as having no "Sæson" option). */
  function sk() {
    return (window.myselfie && window.myselfie.seasonKit) || null;
  }
  function seasonIdx(p) {
    var k = sk();
    return k && p ? k.seasonIndex(p) : -1;
  }
  /** Rental period of a variant ("Weekend"): its "Varighed" option, never the season. */
  function durOf(v, p) {
    var k = sk();
    if (!v) return '';
    return k && p ? k.durationOf(v, p) : String((v.options && v.options[0]) || '');
  }
  function seasonOn(iso) {
    var k = sk();
    return k ? k.seasonFor(iso || '') : '';
  }
  function seasonKey(iso) {
    var k = sk();
    return k ? k.norm(k.seasonFor(iso || '')) : '';
  }
  /**
   * The variant to sell (want: {duration, type, iso, others, strict}): the kit's pick (the date's season); without
   * the kit the first variant with the duration (and type), as before seasons.
   */
  function pickVariant(p, want) {
    if (!p || !p.variants || !p.variants.length) return null;
    var k = sk();
    if (k) return k.pick(p, want);
    var has = function (v, x) { return (v.options || []).indexOf(x) > -1; };
    var c = p.variants;
    if (want.duration) {
      var d = c.filter(function (v) { return has(v, want.duration); });
      if (d.length) c = d;
      else if (want.strict) return null;
    }
    if (want.type) {
      var t = c.filter(function (v) { return has(v, want.type); });
      if (t.length) c = t;
    }
    return c[0];
  }
  /** The same variant (all other options) in the season of a date; unchanged for a product without seasons. */
  function inSeason(p, v, iso) {
    if (!p || !v) return v;
    var si = seasonIdx(p);
    if (si < 0) return v;
    var full = null;
    for (var i = 0; i < p.variants.length; i++) if (String(p.variants[i].id) === String(v.id)) full = p.variants[i];
    if (!full || !full.options) return v;
    var others = {};
    full.options.forEach(function (o, j) { if (j !== si) others[j] = o; });
    return pickVariant(p, { others: others, iso: iso }) || v;
  }
  /** Variant title for customers: without the season ("Weekend / High" -> "Weekend"). */
  function shownTitle(v, p) {
    var k = sk();
    if (k && p && v && v.options) return k.visibleTitle(v, p);
    return v && v.title && v.title !== 'Default Title' ? v.title : '';
  }
  /** Representative days (one per stretch that prices the same) between two dates: keyFn(iso) -> price key. */
  function distinctDays(from, to, keyFn) {
    var out = [];
    var seen = {};
    var d = isoToDate(from);
    for (var n = 0; n < 4000; n++) {
      var iso = toIso(d);
      if (iso > to) break;
      var key = keyFn(iso);
      if (!Object.prototype.hasOwnProperty.call(seen, key)) {
        seen[key] = true;
        out.push(iso);
      }
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    return out;
  }
  function variantLabel(v, p) {
    return shownTitle(v, p);
  }
  function scrollBehavior() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }
  var ICON_ALERT = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.5"/><path d="M12 7.5v5.5M12 16.5v.01"/></svg>';
  var ICON_BACK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function defineElement(name, proto) {
    function El() {
      return Reflect.construct(HTMLElement, [], El);
    }
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.setPrototypeOf(El, HTMLElement);
    Object.keys(proto).forEach(function (k) {
      El.prototype[k] = proto[k];
    });
    window.customElements.define(name, El);
  }

  /* ------------------------------------------------------------------ */
  /* <myselfie-booking-calendar>                                         */
  /* ------------------------------------------------------------------ */
  (function () {
    var d = readDraft();
    if (d && d.iso && d.iso >= tomorrowIso() && !readStored()) {
      store(isoToDisplay(d.iso));
      DATE_FROM_DRAFT = true; // a remembered date, not one picked this session: the cart date wins over it
    }
  })();

  defineElement('myselfie-booking-calendar', {
    connectedCallback: function () {
      if (this._ready) return;
      this._ready = true;

      var t = new Date();
      this.min = new Date(t.getFullYear(), t.getMonth(), t.getDate() + 1); // tomorrow
      var years = Number(window.myselfie && window.myselfie.maxYears) || 3;
      this.max = new Date(t.getFullYear() + years, t.getMonth(), t.getDate());
      this.components = [];
      this.pricer = null;
      this.selected = readStored();
      if (this.selected && isoToDate(this.selected) < this.min) this.selected = '';
      var anchor = this.selected ? isoToDate(this.selected) : this.min;
      // Open on next month when fewer than a week of the current one is left
      if (!this.selected && new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate() - anchor.getDate() < 6) {
        anchor = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
      }
      this.view = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      this.loaded = !availability();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onExternal = this.onExternal.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('myselfie:date', this.onExternal);

      this.render();
      var self = this;
      var a = availability();
      if (a) {
        a.load().then(function () {
          self.loaded = true;
          // a remembered date inside the lead block is not accepted (same as a date in the past)
          if (self.selected && self.leadOf(self.selected)) {
            self.selected = '';
            sdel(STORAGE_KEY);
            document.dispatchEvent(new CustomEvent('myselfie:date', { detail: { iso: '', display: '', source: SOURCE, cleared: true } }));
          }
          self.render();
          self.dispatchEvent(new CustomEvent('myselfie:calendar-ready', { bubbles: true }));
        });
      }
    },

    disconnectedCallback: function () {
      document.removeEventListener('myselfie:date', this.onExternal);
    },

    /**
     * @param {string[]} components  availability products the selection needs
     * @param {function(string):string} pricer  iso -> price label shown on the day
     */
    /**
     * @param {string[]|function(string):string} components  availability products, or a
     *        function iso -> 'available'|'low'|'printer_only'|'sold_out' for the selection
     */
    configure: function (components, pricer, priceNote, leadFn) {
      this.statusFn = typeof components === 'function' ? components : null;
      this.leadFn = typeof leadFn === 'function' ? leadFn : null;
      this.components = Array.isArray(components) ? components : [];
      this.pricer = pricer || null;
      this.priceNote = priceNote || B('price_note', 'Samlet pris i kr.');
      if (!this._ready) return;
      var active = document.activeElement;
      var focusIso = active && this.contains(active) ? active.getAttribute('data-iso') : null;
      this.render();
      var again = focusIso && this.querySelector('[data-iso="' + focusIso + '"]');
      if (again && !again.disabled) again.focus({ preventScroll: true });
    },

    /** Lead-block day for the current selection (leadFn from the product selector, else the calendar's components). */
    leadOf: function (iso) {
      return this.leadFn ? !!this.leadFn(iso) : isLead(iso, this.components);
    },

    statusOf: function (iso) {
      return this.statusFn ? this.statusFn(iso) : statusFor(iso, this.components);
    },

    canGo: function (delta) {
      var target = new Date(this.view.getFullYear(), this.view.getMonth() + delta, 1);
      var minMonth = new Date(this.min.getFullYear(), this.min.getMonth(), 1);
      var maxMonth = new Date(this.max.getFullYear(), this.max.getMonth(), 1);
      return target >= minMonth && target <= maxMonth;
    },

    render: function () {
      var y = this.view.getFullYear();
      var m = this.view.getMonth();
      var lead = (new Date(y, m, 1).getDay() + 6) % 7; // Monday first
      var days = new Date(y, m + 1, 0).getDate();
      var cells = [];
      var hasFocusable = false;
      var anyPrinterOnly = false;
      var anyPopular = false;
      var anyLow = false;
      var anySoldOut = false;
      var i;

      for (i = 0; i < lead; i++) cells.push('<span class="myselfie-bc__cell myselfie-bc__cell--pad" aria-hidden="true"></span>');
      for (i = 1; i <= days; i++) {
        var date = new Date(y, m, i);
        var iso = toIso(date);
        var outside = date < this.min || date > this.max;
        var leadDay = !outside && this.loaded && this.leadOf(iso);
        var status = outside || leadDay ? 'past' : this.loaded ? this.statusOf(iso) : 'loading';
        var disabled = outside || leadDay || status === 'sold_out' || status === 'loading';
        if (status === 'printer_only') anyPrinterOnly = true;
        if (status === 'popular') anyPopular = true;
        if (status === 'low') anyLow = true;
        if (status === 'sold_out') anySoldOut = true;
        var isSel = iso === this.selected;
        var focusable = !disabled && (isSel || (!this.selected && !hasFocusable));
        if (focusable) hasFocusable = true;
        var price = !outside && !leadDay && this.pricer ? this.pricer(iso) : '';
        var sub = status === 'sold_out' ? escapeHtml(statusText('sold_out')) : price ? escapeHtml(price) : '';
        cells.push(
          '<button type="button" class="myselfie-bc__cell myselfie-bc__day is-' + status + (leadDay ? ' is-lead' : '') + (isSel ? ' is-selected' : '') + '"' +
            ' data-iso="' + iso + '"' +
            (disabled ? ' disabled' : '') +
            ' tabindex="' + (focusable ? '0' : '-1') + '"' +
            ' aria-pressed="' + (isSel ? 'true' : 'false') + '"' +
            ' aria-label="' + escapeHtml(longDate(iso) + (leadDay ? ', ' + leadText() : statusText(status) ? ', ' + statusText(status) : '') + (price && status !== 'sold_out' ? ', ' + withCurrency(price) : '')) + '"' +
            (statusTitle(status) ? ' title="' + escapeHtml(statusTitle(status)) + '"' : '') +
            '>' +
            '<span class="myselfie-bc__num">' + i + '</span>' +
            (sub ? '<span class="myselfie-bc__price">' + sub + '</span>' : '') +
            (status === 'low' || status === 'printer_only' || status === 'popular' ? '<span class="myselfie-bc__dot" aria-hidden="true"></span>' : '') +
            '</button>'
        );
      }

      var picker = this.picker ? this.pickerHtml() : '';
      var pickerOpen = !!picker;
      this.innerHTML =
        '<div class="myselfie-bc__nav">' +
          '<button type="button" class="myselfie-bc__arrow" data-step="-1" aria-label="' + escapeHtml(B('prev_month', 'Forrige måned')) + '"' + (this.canGo(-1) ? '' : ' disabled') + '>' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg></button>' +
          '<div class="myselfie-bc__month" aria-live="polite"><button type="button" class="myselfie-bc__heading" data-ps-bc="picker" aria-haspopup="true" aria-expanded="' + pickerOpen + '" aria-controls="' + this.pickerId() + '" aria-label="' + escapeHtml(B('cal_heading_aria', '[title] – vælg måned og år', { title: monthNames()[m] + ' ' + y })) + '">' + monthNames()[m] + ' ' + y +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10l5 5 5-5"/></svg></button></div>' +
          '<button type="button" class="myselfie-bc__arrow" data-step="1" aria-label="' + escapeHtml(B('next_month', 'Næste måned')) + '"' + (this.canGo(1) ? '' : ' disabled') + '>' +
            '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg></button>' +
        '</div>' +
        picker +
        '<div class="myselfie-bc__grid myselfie-bc__grid--head" aria-hidden="true"' + (pickerOpen ? ' hidden' : '') + '>' +
          dayNames().map(function (d) { return '<span>' + d + '</span>'; }).join('') +
        '</div>' +
        '<div class="myselfie-bc__grid' + (this.loaded ? '' : ' is-loading') + '" role="group" aria-label="' + monthNames()[m] + ' ' + y + '"' + (pickerOpen ? ' hidden' : '') + '>' +
          cells.join('') +
        '</div>' +
        '<ul class="myselfie-bc__legend"' + (pickerOpen ? ' hidden' : '') + '>' +
          '<li><i class="is-available"></i>' + escapeHtml(statusText('available')) + '</li>' +
          (anyPopular ? '<li><i class="is-popular"></i>' + escapeHtml(statusText('popular')) + '</li>' : '') +
          (anyLow ? '<li><i class="is-low"></i>' + escapeHtml(statusText('low')) + '</li>' : '') +
          (anySoldOut ? '<li><i class="is-sold_out"></i>' + escapeHtml(statusText('sold_out')) + '</li>' : '') +
          (anyPrinterOnly ? '<li><i class="is-printer_only"></i>' + escapeHtml(statusText('printer_only')) + '</li>' : '') +
          (this.pricer ? '<li class="myselfie-bc__legend-note">' + escapeHtml(this.priceNote || B('price_note', 'Samlet pris i kr.')) + '</li>' : '') +
        '</ul>';
    },

    move: function (delta) {
      if (!this.canGo(delta)) return;
      this.picker = null;
      this.view = new Date(this.view.getFullYear(), this.view.getMonth() + delta, 1);
      this.render();
    },

    /* ---------- month / year picker (feedback 2026-10-07: jump to next year without many clicks) ---------- */

    pickerId: function () {
      if (!this._pid) this._pid = 'myselfie-bc-picker-' + Math.random().toString(36).slice(2, 8);
      return this._pid;
    },

    /** Years the calendar sells (this year .. max date's year, at most +3) and the 12 months of the picked year. */
    pickerHtml: function () {
      var p = this.picker;
      if (!p) return '';
      var firstYear = this.min.getFullYear();
      var lastYear = Math.min(this.max.getFullYear(), firstYear + 3);
      var years = '';
      for (var yr = firstYear; yr <= lastYear; yr++) {
        var on = yr === p.year;
        years += '<button type="button" class="myselfie-bc__pick' + (on ? ' is-on' : '') + '" data-ps-bc="year" data-year="' + yr + '" aria-pressed="' + on + '">' + yr + '</button>';
      }
      var months = '';
      for (var mm = 0; mm < 12; mm++) {
        var first = new Date(p.year, mm, 1);
        var last = new Date(p.year, mm + 1, 0);
        var out = last < this.min || first > this.max;
        var current = p.year === this.view.getFullYear() && mm === this.view.getMonth();
        months += '<button type="button" class="myselfie-bc__pick myselfie-bc__pick--month' + (current ? ' is-current' : '') + '" data-ps-bc="month" data-month="' + mm + '"' +
          (out ? ' disabled' : '') + (current ? ' aria-current="true"' : '') + '>' + monthNames()[mm].slice(0, 3) + '</button>';
      }
      return '<div class="myselfie-bc__picker" id="' + this.pickerId() + '" role="group" aria-label="' + escapeHtml(B('cal_pick_month_year', 'Vælg måned og år')) + '">' +
        '<div class="myselfie-bc__picker-years" role="group" aria-label="' + escapeHtml(B('cal_years', 'År')) + '">' + years + '</div>' +
        '<div class="myselfie-bc__picker-months" role="group" aria-label="' + escapeHtml(B('cal_months_in', 'Måned i [year]', { year: p.year })) + '">' + months + '</div>' +
        '</div>';
    },

    openPicker: function () {
      this.picker = { year: this.view.getFullYear() };
      this.render();
      var on = this.querySelector('.myselfie-bc__pick--month.is-current') || this.querySelector('.myselfie-bc__pick.is-on');
      if (on) on.focus({ preventScroll: true });
    },

    closePicker: function (focusTitle) {
      if (!this.picker) return;
      this.picker = null;
      this.render();
      if (focusTitle) {
        var t = this.querySelector('.myselfie-bc__heading');
        if (t) t.focus({ preventScroll: true });
      }
    },

    onPicker: function (btn) {
      var kind = btn.getAttribute('data-ps-bc');
      if (kind === 'picker') {
        if (this.picker) this.closePicker(true);
        else this.openPicker();
        return;
      }
      if (!this.picker) return;
      if (kind === 'year') {
        this.picker.year = Number(btn.getAttribute('data-year'));
        this.render();
        var again = this.querySelector('.myselfie-bc__pick[data-year="' + this.picker.year + '"]');
        if (again) again.focus({ preventScroll: true });
        return;
      }
      if (kind === 'month' && !btn.disabled) {
        var target = new Date(this.picker.year, Number(btn.getAttribute('data-month')), 1);
        var minMonth = new Date(this.min.getFullYear(), this.min.getMonth(), 1);
        var maxMonth = new Date(this.max.getFullYear(), this.max.getMonth(), 1);
        if (target < minMonth) target = minMonth;
        if (target > maxMonth) target = maxMonth;
        this.view = target;
        this.picker = null;
        this.render();
        var title = this.querySelector('.myselfie-bc__heading');
        if (title) title.focus({ preventScroll: true });
      }
    },

    select: function (iso) {
      this.selected = iso;
      var display = isoToDisplay(iso);
      store(display);
      this.render();
      var btn = this.querySelector('.myselfie-bc__day.is-selected');
      if (btn) btn.focus({ preventScroll: true });
      document.dispatchEvent(
        new CustomEvent('myselfie:date', { detail: { iso: iso, display: display, source: SOURCE, picker: this } })
      );
    },

    onClick: function (e) {
      var pick = e.target.closest('[data-ps-bc]');
      if (pick && this.contains(pick)) return this.onPicker(pick);
      var step = e.target.closest('[data-step]');
      if (step) return this.move(Number(step.getAttribute('data-step')));
      var day = e.target.closest('.myselfie-bc__day');
      if (day && !day.disabled) this.select(day.getAttribute('data-iso'));
    },

    onKeydown: function (e) {
      if (this.picker && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        return this.closePicker(true);
      }
      var day = e.target.closest && e.target.closest('.myselfie-bc__day');
      if (!day) return;
      var map = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      if (!(e.key in map)) return;
      e.preventDefault();
      var d = isoToDate(day.getAttribute('data-iso'));
      d.setDate(d.getDate() + map[e.key]);
      if (d < this.min || d > this.max) return;
      if (d.getMonth() !== this.view.getMonth()) {
        this.view = new Date(d.getFullYear(), d.getMonth(), 1);
        this.render();
      }
      var next = this.querySelector('[data-iso="' + toIso(d) + '"]');
      if (next) next.focus();
    },

    onExternal: function (e) {
      var d = (e && e.detail) || {};
      if (d.cleared) {
        if (!this.selected) return;
        this.selected = '';
        this.render();
        return;
      }
      if (d.source === SOURCE) return;
      var iso = d.iso || displayToIso(d.display);
      if (!iso || iso === this.selected) return;
      this.selected = iso;
      var date = isoToDate(iso);
      this.view = new Date(date.getFullYear(), date.getMonth(), 1);
      this.render();
    },
  });

  /* ------------------------------------------------------------------ */
  /* <myselfie-product-selector>                                         */
  /* ------------------------------------------------------------------ */
  defineElement('myselfie-product-selector', {
    connectedCallback: function () {
      if (this._ready) return;
      this._ready = true;

      try {
        this.cfg = JSON.parse(this.querySelector('[data-ps-config]').textContent);
      } catch (e) {
        if (window.console) console.error('[myselfie] product selector config is not valid JSON', e);
        return;
      }
      if (this.cfg.addToOrderVariant) ADD_TO_ORDER_VARIANT = Number(this.cfg.addToOrderVariant) || 0;
      this.calendar = this.querySelector('myselfie-booking-calendar');
      this.el = {
        durations: this.querySelector('[data-ps-durations]'),
        addons: this.querySelector('[data-ps-addons]'),
        reason: this.querySelector('[data-ps-reason]'),
        other: this.querySelector('[data-ps-reason-other]'),
        recommend: this.querySelector('[data-ps-recommend]'),
        summary: this.querySelector('[data-ps-summary]'),
        error: this.querySelector('[data-ps-error]'),
        book: this.querySelector('[data-ps-book]'),
        billing: this.querySelector('[data-ps-billing]'),
        payment: this.querySelector('[data-ps-payment]'),
        invoice: this.querySelector('[data-ps-invoice]'),
        ean: this.querySelector('[data-ps-ean]'),
        att: this.querySelector('[data-ps-att]'),
        vat: this.querySelector('[data-ps-vat]'),
        catalogue: this.querySelector('[data-ps-catalogue]'),
        terms: this.querySelector('[data-ps-terms]'),
        marketing: this.querySelector('[data-ps-marketing]'),
        cartnote: this.querySelector('[data-ps-cartnote]'),
        fromcart: this.querySelector('[data-ps-fromcart]'),
        livecart: this.querySelector('[data-ps-livecart]'),
        viabill: this.querySelector('[data-ps-viabill]'),
        modal: this.querySelector('[data-ps-modal]'),
      };
      this.uid = 'ps' + Math.random().toString(36).slice(2, 8);
      this.pageMode = this.getAttribute('data-page') || 'booking'; // 'cart' = the DK cart page (_qa/cartpage/spec.md)
      this.products = {}; // handle -> product (modal details, catalogue)
      this.catalogueItems = null; // null | 'loading' | 'error' | product[]
      this.modal = { open: false, view: 'catalogue', kind: '', value: '', from: '', filter: '', query: '', image: 0, variant: '' };

      var durations = this.durations();
      var printTypes = this.printTypes();
      var startBooth = this.boothProduct(); // the product durations() read (no state yet)
      var weekendDur = durations.filter(function (d) { return isWeekendDuration(startBooth, d); })[0];
      this.state = {
        iso: readStored(),
        duration: weekendDur || durations[0] || '',
        printer: false,
        printType: printTypes.values[0] || '',
        prints: PRINTS_PER_ROLL,
        qty: { booth: 1, printer: 1, addons: {} },
        plan: 'full',
        logo: '',
        customer: (function () {
          try {
            return sessionStorage.getItem(CUSTOMER_KEY) === 'erhverv' ? 'erhverv' : 'privat';
          } catch (e) {
            return 'privat';
          }
        })(),
        pay: 'standard',
        fresh: {}, // payment fields just revealed: stay quiet until left or the next "Book nu"
        addons: {},
        addonVar: {}, // chosen variant id per add-on that offers a choice (block setting "Vælg variant")
        leadText: {}, // consent text per lead-collection add-on
        /* catalogue items and recommended packages, by variant id:
           { id, title, variantTitle, price, available, qty, source: 'rec'|'catalogue' } */
        extras: {},
        busy: false,
      };
      this.cartSync = {
        enabled: this.hasAttribute('data-cart-sync'), // live cart (see the header comment)
        keep: (this.getAttribute('data-cart-keep') || '').split(',').map(function (h) { return h.trim(); }).filter(Boolean),
        status: 'idle', // idle|loading|imported|none|blocked (blocked = live off for this page view)
        plan: null, // last plan (planCartImport)
        fingerprint: '', // fingerprint of the last known cart (customer lines only)
        cart: null, // last known /cart.js
        ready: false, // the first cart read is done
        reading: false,
        loadFailedAt: 0,
        found: false, // the first read found something in the cart (banner)
        replaceKeys: {}, // line keys of another booking date: removed at "Gå til betaling", never managed
        touched: false, // customer changed products before the first read finished
        chain: Promise.resolve(), // serialised cart jobs (live passes, re-syncs)
        jobs: 0,
        timer: null, // scheduled live pass
        sig: '', // selection signature the last pass was scheduled for
        lastWrite: 0,
        error: '', // live sync error (red), cleared by the next good pass or action
        notices: [], // automatic changes the customer should know about (mint)
        noticeSeq: 0,
        splitNote: '',
        clearing: false,
        message: '', // info line (stale cart at "Gå til betaling"), never red
        seq: 0,
      };
      this.restoreDraft();
      var presetCustomer = this.getAttribute('data-preset-customer');
      if (presetCustomer === 'privat' || presetCustomer === 'erhverv') this.state.customer = presetCustomer;
      if (this.state.iso && this.state.iso < tomorrowIso()) this.state.iso = '';

      this.onDate = this.onDate.bind(this);
      document.addEventListener('myselfie:date', this.onDate);
      this.addEventListener('click', this.onClick.bind(this));
      this.el.reason.addEventListener('change', this.onReason.bind(this));
      this.el.other.addEventListener('input', this.clearError.bind(this));
      this.el.other.addEventListener('input', function () { this.liveCheck(1200); }.bind(this));
      this.el.other.addEventListener('blur', function () { this.liveCheck(50); }.bind(this));
      [this.el.invoice, this.el.ean, this.el.att].forEach(function (input) {
        if (input) input.addEventListener('input', this.clearError.bind(this));
      }, this);
      [this.el.other, this.el.invoice, this.el.ean, this.el.att].forEach(function (input) {
        if (input) input.addEventListener('input', this.saveDraft.bind(this));
      }, this);
      if (this.el.ean) {
        this.el.ean.addEventListener('blur', function () {
          this.state.eanTouched = true;
          delete this.state.fresh.ean;
          this.renderValidation();
        }.bind(this));
      }
      if (this.el.invoice) {
        this.el.invoice.addEventListener('blur', function () {
          delete this.state.fresh.invoice;
          this.renderValidation();
        }.bind(this));
      }
      this.addEventListener('input', function (e) {
        var t = e.target.closest && e.target.closest('[data-ps-lead]');
        if (!t || !this.contains(t)) return;
        this.state.leadText[t.getAttribute('data-ps-lead')] = t.value;
        this.saveDraft();
        this.clearError();
        this.liveCheck(1200); // the consent text is a line property: written after a pause in typing
      }.bind(this));
      this.addEventListener('focusout', function (e) {
        var t = e.target.closest && e.target.closest('[data-ps-lead]');
        if (t && this.contains(t)) this.liveCheck(50);
      }.bind(this));
      this.addEventListener('myselfie:calendar-ready', function () {
        // a remembered date inside the lead block is dropped like a past date
        if (this.state.iso && this.dayLead(this.state.iso)) return this.forgetDate();
        this.update();
      }.bind(this));
      Array.prototype.forEach.call(this.querySelectorAll('.myselfie-ps__consent a'), function (a) {
        a.target = '_blank';
        a.rel = 'noopener';
      });
      if (this.el.terms) this.el.terms.addEventListener('change', this.clearError.bind(this));
      this.existingItems = [];
      if (this.cartSync.enabled) this.startCartSync();
      else this.loadExistingCart();
      this.scrollToAnchor();
      var how = this.querySelector('[data-ps-how]');
      if (how && window.matchMedia && window.matchMedia('(min-width: 901px)').matches) how.open = true;
      this.updateBilling();
      this.update();
    },

    disconnectedCallback: function () {
      document.removeEventListener('myselfie:date', this.onDate);
      if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
      if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
    },

    /* ---------- product data ---------- */

    /** The configured booth product (date_fallback_product / booth_product). */
    primaryBoothHandle: function () {
      var cfg = this.cfg;
      return cfg.booth[cfg.boothFallback] ? cfg.boothFallback : Object.keys(cfg.booth)[0];
    },

    /**
     * Season mode: the booth product has a "Sæson" option. Then it sells every date (the season of the date picks
     * the variant) and the old date-range price products (boothRanges) are not used (_qa/seasons/spec.md).
     */
    seasonMode: function () {
      return seasonIdx(this.cfg.booth[this.primaryBoothHandle()]) > -1;
    },

    boothHandle: function (iso) {
      var cfg = this.cfg;
      var day = iso || tomorrowIso();
      if (this.seasonMode()) return this.primaryBoothHandle();
      for (var i = 0; i < cfg.boothRanges.length; i++) {
        var r = cfg.boothRanges[i];
        if (day >= r.from && day <= r.to && cfg.booth[r.handle]) return r.handle;
      }
      return cfg.booth[cfg.boothFallback] ? cfg.boothFallback : Object.keys(cfg.booth)[0];
    },

    boothProduct: function (iso) {
      return this.cfg.booth[this.boothHandle(iso)] || null;
    },

    /** True when the event date is Monday–Thursday: the 'Weekend' rental is not offered then. */
    weekendBlocked: function (iso) {
      if (!iso) return false;
      var wd = isoToDate(iso).getDay();
      return wd >= 1 && wd <= 4;
    },

    /** Duration names = the booth's first option ("Weekend", "1 Hverdag", …). */
    durations: function () {
      var p = this.boothProduct(this.state && this.state.iso);
      if (!p) return [];
      var seen = [];
      var self = this;
      p.variants.forEach(function (v) {
        var d = durOf(v, p);
        if (seen.indexOf(d) < 0 && self.durationAllowed(d)) seen.push(d);
      });
      return seen;
    },

    /** The duration is the weekend rental (booth SKU M-W; without SKU the word). */
    isWeekend: function (d) {
      return isWeekendDuration(this.boothProduct(this.state && this.state.iso), d);
    },

    /** The duration is a lease (booth SKU M-93/185/365/730; without SKU the word "Måned"/"Månad"/"Monat"). */
    isLease: function (d) {
      return leaseDays(this.boothProduct(this.state && this.state.iso), d) !== 0;
    },

    /**
     * Section setting "Vis kun lejeperioder med" (lease page: "Måned"). Empty = every duration. A filter that names
     * months in any store language ("Måned", "Månad", "Monat", "Month") selects the lease durations by SKU.
     */
    durationAllowed: function (name) {
      var f = (this.getAttribute('data-duration-filter') || '').trim().toLowerCase();
      // without a filter the lease months ("3 Måneder") are not event rentals: they belong to the lease page only (the cart
      // import refuses them here too). With seasons the booth product (which has the months) sells every date.
      if (!f) return !this.isLease(name) || this.hasPlans();
      if (LEASE_WORD.test(f) && this.isLease(name)) return true;
      return String(name || '').toLowerCase().indexOf(f) > -1;
    },

    /** Lease page: "Betalingsplan" step (full payment with discount, or monthly invoice). */
    hasPlans: function () {
      return this.hasAttribute('data-lease-plans');
    },

    /** Months in the chosen duration ("6 Måneder" -> 6), at least 1. */
    leaseMonths: function () {
      var days = leaseDays(this.boothProduct(this.state.iso), this.state.duration);
      if (days > 0) return Math.max(1, Math.round(days / 30.44)); // M-93 -> 3, M-185 -> 6, M-365 -> 12, M-730 -> 24
      var m = /(\d+)/.exec(this.state.duration || '');
      return m ? Math.max(1, Number(m[1])) : 1;
    },

    /** Lease page with the monthly (invoice) plan chosen. */
    monthlyPlan: function () {
      return this.hasPlans() && this.state.plan === 'monthly';
    },

    leaseFullPct: function () {
      return Math.max(0, Math.min(100, Number(this.getAttribute('data-lease-full-pct')) || 0));
    },

    /** Total after the full-payment discount (applied by the discount code at checkout). */
    planTotal: function (total) {
      if (!this.hasPlans() || this.state.plan !== 'full') return total;
      return Math.round(total * (1 - this.leaseFullPct() / 100));
    },

    /** Booth variant for a date and duration; with a "Sæson" option the variant of the date's season. */
    boothVariant: function (iso, duration) {
      var p = this.boothProduct(iso);
      if (!p) return null;
      var d = duration || this.state.duration;
      var v = pickVariant(p, { duration: d, iso: iso, strict: true });
      if (v) return v;
      for (var j = 0; j < p.variants.length; j++) {
        var dj = durOf(p.variants[j], p);
        if (this.durationAllowed(dj)) return pickVariant(p, { duration: dj, iso: iso, strict: true }) || p.variants[j];
      }
      // with a duration filter (lease page) never fall back to a short rental
      return this.getAttribute('data-duration-filter') ? null : pickVariant(p, { duration: durOf(p.variants[0], p), iso: iso }) || p.variants[0];
    },

    /** A booth variant (any booth product, any season) by id: {product, variant} or null. */
    boothById: function (id) {
      var cfg = this.cfg;
      var hs = Object.keys(cfg.booth);
      for (var i = 0; i < hs.length; i++) {
        var p = cfg.booth[hs[i]];
        for (var j = 0; j < p.variants.length; j++) if (p.variants[j].id === id) return { product: p, variant: p.variants[j] };
      }
      return null;
    },

    /**
     * One day per stretch of days between tomorrow and the calendar's last day that price the same (booth product
     * of the date + season of the date). min/max over these days = min/max over every bookable day ("fra").
     */
    priceDays: function () {
      var self = this;
      var k = sk();
      var key = tomorrowIso() + '|' + maxIso() + '|' + (k ? JSON.stringify(k.config()) : '');
      if (this._priceDays && this._priceDays.key === key) return this._priceDays.days;
      var days = distinctDays(tomorrowIso(), maxIso(), function (day) { return self.boothHandle(day) + '|' + seasonKey(day); });
      this._priceDays = { key: key, days: days };
      return days;
    },

    /** {min, max, minDay, varies} of amountFn(day) over priceDays (amountFn may return null = not sellable). */
    spanOf: function (amountFn) {
      var out = { min: null, max: null, minDay: '', varies: false };
      this.priceDays().forEach(function (day) {
        var a = amountFn(day);
        if (a == null) return;
        if (out.min === null || a < out.min) { out.min = a; out.minDay = day; }
        if (out.max === null || a > out.max) out.max = a;
      });
      out.varies = out.min !== null && out.max !== out.min;
      return out;
    },

    /**
     * Cheapest and dearest booth variant of a duration over every date the calendar can sell
     * ({min, max, varies}): the "fra" price while no date is chosen. Date ranges and seasons alike (priceDays).
     */
    boothPriceSpan: function (duration) {
      var self = this;
      var d = duration || this.state.duration;
      var min = null;
      var max = null;
      this.priceDays().forEach(function (day) {
        var p = self.boothProduct(day);
        var v = p && pickVariant(p, { duration: d, iso: day, strict: true });
        if (!v) return;
        if (!min || v.price < min.price) min = v;
        if (!max || v.price > max.price) max = v;
      });
      return { min: min, max: max, varies: !!(min && max && min.price !== max.price) };
    },

    /** The printer option that is not the duration (e.g. "Print type"). */
    printTypes: function () {
      var printer = this.cfg.printer;
      // every duration name of the booth, ignoring the lease filter, so a printer "Varighed" option is never taken for a print type
      var booth = this.boothProduct(this.state && this.state.iso);
      var durations = booth ? booth.variants.map(function (v) { return durOf(v, booth); }) : [];
      var out = { index: -1, name: '', values: [] };
      if (!printer) return out;
      var si = seasonIdx(printer);
      // an option whose values are print types (Polaroid / Strip / Fotostribe) - added to the printer product later
      var pti = KIT && KIT.printTypeIndex ? KIT.printTypeIndex(printer) : -1;
      if (pti > -1 && pti !== si) {
        printer.variants.forEach(function (v) { if (out.values.indexOf(v.options[pti]) < 0) out.values.push(v.options[pti]); });
        return { index: pti, name: printer.options[pti], values: out.values };
      }
      printer.options.forEach(function (name, idx) {
        if (out.index > -1 || idx === si) return; // the season is never a print type
        var values = [];
        printer.variants.forEach(function (v) {
          if (values.indexOf(v.options[idx]) < 0) values.push(v.options[idx]);
        });
        var isDuration = /varighed|duration|lejeperiode|hyresperiod|hyrperiod|mietdauer|mietzeit/i.test(name) || values.some(function (val) { return durations.indexOf(val) > -1; });
        if (!isDuration && values.length > 1) out = { index: idx, name: name, values: values };
      });
      if (out.index < 0) out = { index: -1, name: B('print_type_label', 'Print type'), values: printTypesDefault() };
      return out;
    },

    /** The shown print type value that means the same as t ("Fotostribe" = "Strip"), else the first one. */
    printTypeValue: function (t) {
      var values = this.printTypes().values;
      if (values.indexOf(t) > -1) return t;
      var key = printKeyOf(t);
      for (var i = 0; i < values.length; i++) if (key && printKeyOf(values[i]) === key) return values[i];
      return values[0] || t || '';
    },

    /**
     * Printer variant: the duration (and print type) - and with a "Sæson" option the season of the date.
     * iso: the date (undefined = the selection's date, '' = no date: the default season).
     */
    printerVariant: function (duration, iso) {
      var printer = this.cfg.printer;
      if (!printer) return null;
      var d = duration || this.state.duration;
      if (this.getAttribute('data-duration-filter') && !this.durationAllowed(d)) return null;
      var pt = this.printTypes();
      return pickVariant(printer, { duration: d, type: pt.index > -1 ? this.printTypeValue(this.state.printType) : '', iso: iso === undefined ? this.state.iso : iso });
    },

    rollVariant: function (iso) {
      var roll = this.cfg.printRoll;
      return roll ? pickVariant(roll, { iso: iso === undefined ? this.state.iso : iso }) : null;
    },

    /**
     * The chosen logo: '' (Myselfie Logo) | 'NL' (Ingen logo) | 'EL' (Eget logo). state.logo may still hold a variant
     * id (a draft from before 2026-10-05): it is read as that variant's logo.
     */
    logoCode: function () {
      var v = String(this.state.logo || '');
      if (!v || v === 'ML') return '';
      if (v === 'NL' || v === 'EL') return v;
      var logo = this.cfg.logo;
      if (logo && KIT && KIT.logoCode) {
        for (var i = 0; i < logo.variants.length; i++) {
          if (String(logo.variants[i].id) === v) { var c = KIT.logoCode(logo.variants[i], logo); return c === 'ML' ? '' : c; }
        }
      }
      return '';
    },
    /** "Logo på print" variant for a logo code and print type in the season of a date (owner 2026-10-05). */
    logoVariantFor: function (code, type, iso) {
      var logo = this.cfg.logo;
      if (!logo || !KIT || !KIT.pickLogo) return null;
      return KIT.pickLogo(logo, code || 'ML', type, iso);
    },
    /**
     * The logo line of the printer: the chosen logo for the chosen print type, in the season of the date. With a
     * printer the standard "Myselfie Logo" is a 0 kr line too (when the product has that variant).
     */
    logoVariant: function (iso) {
      return this.logoVariantFor(this.logoCode() || 'ML', this.state.printType, iso === undefined ? this.state.iso : iso);
    },

    /** Logo choices shown: Myselfie Logo (gratis), Ingen logo, Eget logo - the admin names, prices for the print type. */
    logoChoices: function () {
      var logo = this.cfg.logo;
      if (!logo) return [];
      if (KIT && KIT.pickLogo) {
        var self = this;
        return ['ML', 'NL', 'EL'].map(function (code) {
          var v = self.logoVariantFor(code, self.state.printType, self.state.iso);
          if (code !== 'ML' && (!v || !v.available)) return null;
          return { id: code === 'ML' ? '' : code, code: code, title: KIT.logoName(logo, code), price: v ? v.price : 0 };
        }).filter(Boolean);
      }
      var si = seasonIdx(logo);
      var iso = this.state.iso;
      if (si < 0) return logo.variants.filter(function (v) { return v.available; });
      var seen = {};
      var out = [];
      logo.variants.forEach(function (v) {
        var t = shownTitle(v, logo);
        if (seen[t]) return;
        seen[t] = true;
        var x = inSeason(logo, v, iso);
        if (x && x.available) out.push(Object.assign({}, x, { title: shownTitle(x, logo) }));
      });
      return out;
    },

    /** Add-on block variant (the add-on's product, in the season of the date). */
    addonVariant: function (a, iso) {
      if (a && a.product && a.chooseVariant) {
        var self = this;
        var chosen = this.addonChoices(a).filter(function (v) { return String(v.id) === String(self.state.addonVar[a.key]); })[0];
        return chosen || this.addonDefaultVariant(a);
      }
      return a && a.product ? pickVariant(a.product, { iso: iso === undefined ? this.state.iso : iso }) : null;
    },

    /** Add-on with a variant choice (e.g. gallery extension 12 mdr. / 2 år / 5 år): the variants on offer. */
    addonChoices: function (a) {
      return a && a.product && a.chooseVariant ? a.product.variants.filter(function (v) { return v.available; }) : [];
    },

    /** The preselected variant: the one-year variant ("1 år" / "12 mdr."), else the first. */
    addonDefaultVariant: function (a) {
      var list = this.addonChoices(a);
      return list.filter(function (v) { return /(^|\s)(1 år|12 m(dr|åned|ån))/i.test(v.title || ''); })[0] || list[0] || null;
    },

    /** Recommended package variant (in the season of the date). */
    recVariant: function (r, iso) {
      return r && r.product ? pickVariant(r.product, { iso: iso === undefined ? this.state.iso : iso }) : null;
    },

    /** True when the variant id is any variant of the product (any season). */
    productHas: function (p, id) {
      return !!p && p.variants.some(function (v) { return v.id === id; });
    },

    extraRolls: function () {
      return Math.max(0, Math.round((this.state.prints - this.includedPrints()) / PRINTS_PER_ROLL));
    },

    maxPrints: function () {
      var max = Number(this.getAttribute('data-max-rolls')) || 5;
      return this.rollVariant() ? this.includedPrints() + PRINTS_PER_ROLL * max : this.includedPrints();
    },

    /** Quantity of a line. Booths and Mygreetings 1–5; printers 1 up to the number of booths. */
    qtyFor: function (key) {
      var q = this.state.qty;
      if (key === 'booth') return q.booth;
      if (key === 'printer') return Math.min(q.printer, q.booth);
      return q.addons[key] || 1;
    },

    /** Each printer comes with its own first roll. */
    includedPrints: function () {
      return PRINTS_PER_ROLL * (this.state.printer ? this.qtyFor('printer') : 1);
    },

    changeQty: function (key, delta) {
      var s = this.state;
      var q = s.qty;
      var clamp = function (n, max) { return Math.max(1, Math.min(max, n)); };
      var printersBefore = this.qtyFor('printer');
      if (key === 'booth') q.booth = clamp(q.booth + delta, QTY_MAX);
      else if (key === 'printer') q.printer = clamp(this.qtyFor('printer') + delta, Math.min(QTY_MAX, q.booth));
      else q.addons[key] = clamp((q.addons[key] || 1) + delta, QTY_MAX);
      q.printer = Math.min(q.printer, q.booth);
      // keep the same number of extra rolls when printers (and their included rolls) change
      s.prints += (this.qtyFor('printer') - printersBefore) * PRINTS_PER_ROLL;
      s.prints = Math.min(this.maxPrints(), Math.max(this.includedPrints(), s.prints));
    },

    qtyStepperHtml: function (key, label, n, max, what) {
      return '<div class="myselfie-ps__row"><span class="myselfie-ps__row-label">' + escapeHtml(label) + '</span>' +
        '<div class="myselfie-ps__stepper">' +
          '<button type="button" data-ps-action="qty" data-value="' + escapeHtml(key) + ':-1" data-ps-focus="q-' + escapeHtml(key) + '-minus" aria-label="' + escapeHtml(B('qty_fewer', 'Færre [what]', { what: what })) + '"' + (n <= 1 ? ' disabled' : '') + '>−</button>' +
          '<output aria-live="polite">' + n + '</output>' +
          '<button type="button" data-ps-action="qty" data-value="' + escapeHtml(key) + ':1" data-ps-focus="q-' + escapeHtml(key) + '-plus" aria-label="' + escapeHtml(B('qty_more', 'Flere [what]', { what: what })) + '"' + (n >= max ? ' disabled' : '') + '>+</button>' +
        '</div></div>';
    },

    recommendations: function () {
      if (this.hasAttribute('data-hide-reason')) return []; // preset occasion: no reason step, so no recommendation box
      var key = this.reasonKey();
      if (!key) return [];
      return (this.cfg.recommendations || []).filter(function (r) {
        return r.reasons.indexOf(key) > -1;
      });
    },

    reasonKey: function () {
      var opt = this.el.reason.options[this.el.reason.selectedIndex];
      return opt && opt.value ? opt.getAttribute('data-key') : '';
    },

    /** Everything that goes into the cart for a date, with prices for that date. */
    lines: function (iso) {
      var self = this;
      var s = this.state;
      var out = [];
      var booth = this.boothVariant(iso);
      if (booth) {
        var boothQty = hasQty(booth) ? this.qtyFor('booth') : 1;
        out.push({ key: 'booth', variant: booth, qty: boothQty, dated: true, reason: true,
          props: withInfo(null, booth.sku), // a bundle with a printer says what is included
          label: B('line_booth', 'Myselfie photo booth'), detail: (boothQty > 1 ? boothQty + ' × ' : '') + s.duration });
      }
      if (s.printer) {
        var printer = this.printerVariant(null, iso);
        if (printer) {
          var printerQty = hasQty(printer) ? this.qtyFor('printer') : 1;
          out.push({ key: 'printer', variant: printer, qty: printerQty, dated: true,
            props: withInfo(printModeFor(s.printType) ? { print_mode: printModeFor(s.printType) } : null, printer.sku),
            label: B('line_printer', 'Printer'), detail: (printerQty > 1 ? printerQty + ' × ' : '') + [s.duration, s.printType, B('n_prints', '[n] prints', { n: s.prints })].filter(Boolean).join(' · ') });
        }
        var logo = this.logoVariant(iso);
        if (logo) {
          // one line (qty 1) whatever the number of printers; shown under the printer in the summary
          out.push({ key: 'logo', variant: logo, qty: 1, dated: false, label: this.cfg.logo.title,
            detail: [this.printTypeValue(s.printType), KIT && KIT.logoName ? KIT.logoName(this.cfg.logo, (KIT.logoCode(logo, this.cfg.logo) || 'ML')) : shownTitle(logo, this.cfg.logo)].filter(Boolean).join(' · ') });
        }
        var rolls = this.extraRolls();
        var roll = this.rollVariant(iso);
        if (rolls && roll) {
          out.push({ key: 'rolls', variant: roll, qty: rolls, dated: false, props: withInfo(null, roll.sku),
            label: B('line_extra_prints', 'Ekstra prints'), detail: rolls + ' × ' + PRINTS_PER_ROLL });
        }
      }
      (this.cfg.addons || []).forEach(function (a) {
        var av = self.addonVariant(a, iso);
        if (s.addons[a.key] && av) {
          var addonQty = hasQty(av) || (s.qty.addons[a.key] || 1) > 1 ? self.qtyFor(a.key) : 1;
          out.push({ key: a.key, variant: av, qty: addonQty, dated: a.dated, label: a.label, detail: addonQty > 1 ? B('n_pcs', '[n] stk.', { n: addonQty }) : '',
            props: a.lead ? { Samtykketekst: (s.leadText[a.key] || '').trim() } : null });
        }
      });
      Object.keys(s.extras).forEach(function (id) {
        var x = s.extras[id];
        out.push({ key: 'extra-' + id, variant: { id: x.id, price: x.price, available: x.available, title: x.variantTitle },
          qty: x.qty, dated: false, label: x.title, detail: x.variantTitle });
      });
      return out;
    },

    total: function (lines) {
      return lines.reduce(function (sum, l) {
        return sum + l.variant.price * l.qty;
      }, 0);
    },

    /**
     * Status per product on a date. Printer bookings use the booths earmarked for printers
     * (PRODUCT_MAP in api-dates.js: Printer ignores Myselfie's sold-out), so a date where
     * Myselfie is sold out can still be booked together with a printer ("printer_only").
     */
    /**
     * Lease page ("Tjek ledighed de første N dage"): dates more than N days ahead are always
     * bookable; inside the window a blocked date also blocks every date before it.
     */
    leaseWindow: function () {
      return this.hasPlans() ? Math.max(0, Number(this.getAttribute('data-lease-window')) || 0) : 0;
    },

    /** Last date inside the lease window that is blocked for the current selection ('' = none). */
    leaseBlockedUntil: function () {
      var key = JSON.stringify([this.state.printer, this.state.printerForced, this.state.addons, !!(availability() && availability().data)]);
      if (this._leaseCache && this._leaseCache.key === key) return this._leaseCache.until;
      var until = '';
      var t = new Date();
      for (var i = 1; i <= this.leaseWindow(); i++) {
        var day = toIso(new Date(t.getFullYear(), t.getMonth(), t.getDate() + i));
        if (this.statusFrom(this.rawAvailability(day)) === 'sold_out') until = day;
      }
      this._leaseCache = { key: key, until: until };
      return until;
    },

    /** Lead-block day for the current selection: the booth cannot be delivered (Myselfie, or the printer if chosen) or a chosen add-on cannot. */
    dayLead: function (iso) {
      var s = this.state;
      var cfg = this.cfg;
      var boothLead = isLead(iso, ['Myselfie']);
      if (cfg.printer) boothLead = boothLead && isLead(iso, ['Printer']);
      var printerChosen = s.printer && !s.printerForced;
      var lead = printerChosen ? isLead(iso, ['Printer']) : boothLead;
      (cfg.addons || []).forEach(function (x) {
        if (s.addons[x.key] && x.component && isLead(iso, [x.component])) lead = true;
      });
      return lead;
    },

    dayAvailability: function (iso) {
      var a = this.rawAvailability(iso);
      var n = this.leaseWindow();
      if (!n) return a;
      var t = new Date();
      var last = toIso(new Date(t.getFullYear(), t.getMonth(), t.getDate() + n));
      var all = function (status) {
        var out = { myselfie: status, printer: status, addons: {}, booth: status };
        Object.keys(a.addons).forEach(function (k) { out.addons[k] = status; });
        return out;
      };
      if (iso > last) return all('available');
      var until = this.leaseBlockedUntil();
      if (until && iso <= until) return all('sold_out');
      return a;
    },

    rawAvailability: function (iso) {
      var a = {
        myselfie: statusFor(iso, ['Myselfie']),
        printer: this.cfg.printer ? statusFor(iso, ['Printer']) : 'sold_out',
        addons: {},
      };
      (this.cfg.addons || []).forEach(function (x) {
        if (x.component) a.addons[x.key] = statusFor(iso, [x.component]);
      });
      a.booth = a.myselfie !== 'sold_out' ? a.myselfie : a.printer !== 'sold_out' ? 'printer_only' : 'sold_out';
      return a;
    },

    /** A day's status for the current selection: calendar colours, summary, "Book nu". */
    dayStatus: function (iso) {
      return this.statusFrom(this.dayAvailability(iso));
    },

    statusFrom: function (a) {
      var s = this.state;
      var printerChosen = s.printer && !s.printerForced;
      var status = printerChosen ? a.printer : a.booth;
      if (status === 'sold_out') return 'sold_out';
      var low = status === 'low';
      var popular = status === 'popular';
      var addonSoldOut = false;
      (this.cfg.addons || []).forEach(function (x) {
        if (!s.addons[x.key] || !a.addons[x.key]) return;
        if (a.addons[x.key] === 'sold_out') addonSoldOut = true;
        if (a.addons[x.key] === 'low') low = true;
        if (a.addons[x.key] === 'popular') popular = true;
      });
      if (addonSoldOut) return 'sold_out';
      if (status === 'printer_only') return 'printer_only';
      return low ? 'low' : popular ? 'popular' : 'available';
    },

    /**
     * Earmarking: on a date where Myselfie is only available with a printer, the printer is
     * added and locked. If it was added automatically it leaves again on a normal date.
     */
    applyEarmark: function () {
      var s = this.state;
      var a = s.iso && this.cfg.printer ? this.dayAvailability(s.iso) : null;
      s.printerLocked = !!(a && a.myselfie === 'sold_out' && a.printer !== 'sold_out');
      if (s.printerLocked && !s.printer) {
        s.printer = true;
        s.printerForced = true;
      } else if (!s.printerLocked && s.printerForced) {
        s.printer = false;
        s.printerForced = false;
      }
    },

    /** Re-reads availability (no cache) right before booking. Fails open like the rest. */
    refreshAvailability: function () {
      var self = this;
      var a = availability();
      if (!a || typeof a._index !== 'function') return Promise.resolve();
      var url = a.url();
      return fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + '_=' + Date.now(), { headers: { Accept: 'application/json' }, cache: 'no-store' })
        .then(function (r) {
          if (!r.ok) throw new Error('availability ' + r.status);
          return r.json();
        })
        .then(function (json) {
          a.data = a._index(json);
          self._leaseCache = null;
        })
        .catch(function () {
          /* keep the data we have */
        });
    },

    components: function () {
      var list = ['Myselfie'];
      if (this.state.printer) list.push('Printer');
      var s = this.state;
      (this.cfg.addons || []).forEach(function (a) {
        if (s.addons[a.key] && a.component) list.push(a.component);
      });
      return list;
    },

    /* ---------- events ---------- */

    onDate: function (e) {
      var d = (e && e.detail) || {};
      if (d.cleared) {
        if (this.state.iso) {
          this.state.iso = '';
          this.update();
        }
        return;
      }
      var iso = d.iso || displayToIso(d.display);
      if (!iso) return;
      if (d.source !== CART_SOURCE) {
        DATE_FROM_DRAFT = false; // the customer picked it: it now outranks the cart date
        this.markTouched();
        if (iso !== this.state.iso) this.clearNotices();
      }
      this.state.iso = iso;
      this.clearError();
      this.update(); // live: drops what is sold out on the new date, then re-dates the cart lines
      if (d.source === SOURCE && d.picker && this.contains(d.picker)) this.scrollToNextStep();
    },

    /** Mobile: after the customer picks a date in this module's calendar, bring the next visible step into view. */
    scrollToNextStep: function () {
      if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
      var self = this;
      window.setTimeout(function () {
        var steps = self.querySelectorAll('[data-ps-step]');
        var next = null;
        for (var i = 0; i < steps.length; i++) {
          var st = steps[i];
          if (st.getAttribute('data-ps-step') === 'date' || st.hidden || st.offsetParent === null) continue;
          next = st;
          break;
        }
        if (!next) return;
        // leave room for whatever is fixed/sticky at the top of the screen (the mobile header)
        var offset = 12;
        var el = document.elementFromPoint(Math.round(window.innerWidth / 2), 2);
        while (el && el !== document.body && el !== document.documentElement) {
          var pos = window.getComputedStyle(el).position;
          if (pos === 'fixed' || pos === 'sticky') { offset += Math.max(0, el.getBoundingClientRect().bottom); break; }
          el = el.parentElement;
        }
        var top = next.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: Math.max(0, top), behavior: scrollBehavior() });
      }, 120);
    },

    onReason: function () {
      var isOther = this.el.reason.value === 'andet';
      this.el.other.hidden = !isOther;
      if (isOther) this.el.other.focus();
      this.pruneRecExtras();
      this.clearError();
      this.update();
    },

    onClick: function (e) {
      var go = e.target.closest('[data-ps-goto]');
      if (go && this.contains(go)) return this.focusIssue(go.getAttribute('data-ps-goto'));
      var plan = e.target.closest('[data-ps-plan]');
      if (plan && this.contains(plan)) {
        if (plan.disabled) return;
        this.state.plan = plan.getAttribute('data-ps-plan') === 'monthly' ? 'monthly' : 'full';
        // monthly is paid by invoice: standard payment is not allowed
        if (this.state.plan === 'monthly' && this.state.pay === 'standard') {
          this.state.pay = 'faktura';
          this.state.fresh = { ean: true, invoice: true };
        }
        this.updateBilling();
        this.update('plan-' + this.state.plan);
        return;
      }
      var billing = e.target.closest('[data-ps-customer], [data-ps-pay]');
      if (billing && this.contains(billing)) {
        if (billing.hasAttribute('data-ps-customer')) {
          this.state.customer = billing.getAttribute('data-ps-customer') === 'erhverv' ? 'erhverv' : 'privat';
          try {
            sessionStorage.setItem(CUSTOMER_KEY, this.state.customer);
          } catch (err) {
            /* storage blocked */
          }
        } else {
          if (billing.disabled) return;
          this.state.pay = billing.getAttribute('data-ps-pay');
        }
        // Privat has no invoice payment, so the monthly plan falls back to full payment
        if (this.monthlyPlan() && this.state.customer !== 'erhverv') this.state.plan = 'full';
        this.state.fresh = { ean: true, invoice: true };
        this.clearError();
        this.updateBilling();
        this.update();
        return;
      }
      var t = e.target.closest('[data-ps-action]');
      if (!t || !this.contains(t)) return;
      var s = this.state;
      var action = t.getAttribute('data-ps-action');
      var value = t.getAttribute('data-value');
      // live cart: a dated product needs the date before it can go into the cart
      if ((action === 'printer' && !s.printer && this.needsDate(true)) || (action === 'addon' && !s.addons[value] && this.needsDate(this.addonDated(value)))) return;
      if (/^(duration|printer|print-type|logo|prints|qty|addon|addon-variant|rec|extra|extra-remove)$/.test(action) && !(action === 'printer' && s.printerLocked)) {
        this.markTouched();
        this.clearNotices();
      }
      if (action === 'duration') { s.duration = value; this._weekendAuto = null; }
      else if (action === 'printer') {
        if (s.printerLocked) return;
        s.printer = !s.printer;
        s.printerForced = false;
        s.prints = Math.min(this.maxPrints(), Math.max(this.includedPrints(), s.prints));
      }
      else if (action === 'print-type') s.printType = value;
      else if (action === 'logo') s.logo = value;
      else if (action === 'prints') s.prints = Math.min(this.maxPrints(), Math.max(this.includedPrints(), s.prints + Number(value)));
      else if (action === 'qty') this.changeQty(value.split(':')[0], Number(value.split(':')[1]));
      else if (action === 'addon') s.addons[value] = !s.addons[value];
      else if (action === 'addon-variant') s.addonVar[t.getAttribute('data-key')] = Number(value);
      else if (action === 'rec') this.toggleRec(value);
      else if (action === 'extra') this.toggleExtra(value, t.getAttribute('data-handle'));
      else if (action === 'extra-remove') delete s.extras[value];
      else if (action === 'more') return this.openDetail(t.getAttribute('data-kind'), value, t.getAttribute('data-from') || '');
      else if (action === 'catalogue') return this.openCatalogue();
      else if (action === 'about') return this.openAbout();
      else if (action === 'cart-clear') return this.cartSync.enabled ? this.forgetCart() : this.clearExistingCart();
      else if (action === 'cart-forget') return this.forgetCart();
      else if (action === 'cart-use-date') return this.syncFromCart('manual', value);
      else if (action === 'draft-reset') return this.cartSync.enabled ? this.forgetCart() : this.resetDraft();
      else if (action === 'modal-close') return this.closeModal();
      else if (action === 'modal-back') return this.openCatalogue(true);
      else if (action === 'filter') {
        this.modal.filter = value;
        return this.renderModal('f-' + value);
      } else if (action === 'image') {
        this.modal.image = Number(value) || 0;
        return this.renderModal('i-' + value);
      } else if (action === 'book') return this.book();
      else return;
      this.clearError();
      var key = t.getAttribute('data-ps-focus');
      this.update(this.modal.open ? null : key);
      if (this.modal.open) this.renderModal(key);
    },

    /* ---------- rendering ---------- */

    update: function (focusKey) {
      if (!this.cfg) return;
      this._leaseCache = null; // availability or selection may have changed
      var s = this.state;
      var self = this;
      var iso = s.iso;
      var durations = this.durations();
      if (durations.indexOf(s.duration) < 0) s.duration = durations[0] || '';
      // 'Weekend' only for events on a Friday, Saturday or Sunday: a Monday–Thursday date switches to a weekday rental
      var weekendOff = this.weekendBlocked(iso);
      var weekendSwitched = false;
      if (weekendOff && this.isWeekend(s.duration)) {
        var alt = durations.filter(function (d) { return !self.isWeekend(d); })[0];
        if (alt) { s.duration = alt; weekendSwitched = true; this._weekendAuto = alt; }
      } else if (!weekendOff && this._weekendAuto && s.duration === this._weekendAuto) {
        // back on a Friday–Sunday date and the customer kept our automatic choice: Weekend again
        var wk = durations.filter(function (d) { return self.isWeekend(d); })[0];
        if (wk) s.duration = wk;
        this._weekendAuto = null;
      }
      this.applyEarmark();
      this.reseason();
      var avail = iso ? this.dayAvailability(iso) : null;

      // 2. duration
      setHtml(this.el.durations, durations
        .map(function (d) {
          var v = self.boothVariant(iso, d);
          // no date yet: the lowest price the duration can be booked for ("fra"), when it depends on the date
          var span = iso ? null : self.boothPriceSpan(d);
          var from = !!(span && span.varies);
          if (from) v = span.min;
          var on = d === s.duration;
          var isWk = self.isWeekend(d);
          var wkOff = weekendOff && isWk;
          return (
            '<button type="button" role="radio" class="myselfie-ps__duration' + (on ? ' is-on' : '') + '"' +
              ' aria-checked="' + on + '" data-ps-action="duration" data-value="' + escapeHtml(d) + '" data-ps-focus="d-' + escapeHtml(d) + '"' +
              (v && !v.available || wkOff ? ' disabled' : '') + (wkOff ? ' title="' + escapeHtml(B('weekend_only_title', 'Weekend kan kun vælges, når eventet er fredag, lørdag eller søndag')) + '"' : '') + '>' +
              (isWk ? '<span class="myselfie-ps__flag">' + escapeHtml(wkOff ? B('weekend_only_flag', 'Kun fre–søn') : B('most_popular', 'Mest populær')) + '</span>' : '') +
              '<span class="myselfie-ps__duration-name">' + escapeHtml(d) + '</span>' +
              '<span class="myselfie-ps__duration-price">' + (v ? (from ? '<span class="myselfie-ps__from">' + escapeHtml(B('from', 'fra')) + ' </span>' : '') + self.money(v.price, true) : '') + '</span>' +
            '</button>'
          );
        })
        .join(''));
      if (!this.el.weekendNote) {
        this.el.weekendNote = document.createElement('p');
        this.el.weekendNote.className = 'myselfie-ps__duration-note';
        this.el.weekendNote.setAttribute('role', 'status');
        this.el.durations.parentNode.insertBefore(this.el.weekendNote, this.el.durations.nextSibling);
      }
      if (weekendSwitched) this._weekendNotice = true;
      if (!weekendOff) this._weekendNotice = false;
      var showWkNote = weekendOff && durations.some(function (d) { return self.isWeekend(d); });
      this.el.weekendNote.hidden = !showWkNote;
      setHtml(this.el.weekendNote, showWkNote
        ? (this._weekendNotice ? escapeHtml(B('weekend_switched', 'Weekend er kun for events fredag–søndag, så vi har valgt [duration] i stedet.', { duration: s.duration })) : escapeHtml(B('weekend_only_note', 'Weekend kan kun vælges, når eventet er fredag, lørdag eller søndag.')))
        : '');

      // booth quantity, right under the rental periods
      var boothV = this.boothVariant(iso);
      if (!this.el.boothQty) {
        this.el.boothQty = document.createElement('div');
        this.el.boothQty.setAttribute('data-ps-qty-booth', '');
        this.el.durations.parentNode.insertBefore(this.el.boothQty, this.el.durations.nextSibling);
      }
      setHtml(this.el.boothQty, hasQty(boothV) ? this.qtyStepperHtml('booth', B('qty_booth', 'Antal Myselfie'), this.qtyFor('booth'), QTY_MAX, 'Myselfie') : '');

      // 3. add-ons
      var html = '';
      var printer = this.printerVariant();
      if (printer) {
        var roll = this.rollVariant();
        var types = this.printTypes();
        // no date: the printer's lowest price over the dates the calendar sells ("fra"), when it depends on the season
        var pFrom = !iso && seasonIdx(this.cfg.printer) > -1 ? this.spanOf(function (day) { var pv = self.printerVariant(null, day); return pv ? pv.price : null; }) : null;
        html +=
          '<div class="myselfie-ps__addon' + (s.printer ? ' is-on' : '') + this.cardState(s.printer, avail && avail.printer, s.printerLocked) + '">' +
            this.toggleHtml({ action: 'printer', value: '', on: s.printer, price: pFrom && pFrom.min !== null ? pFrom.min : printer.price, from: !!(pFrom && pFrom.varies), kind: 'printer', more: this.cfg.printer.handle,
              iso: iso, status: avail ? avail.printer : '', locked: s.printerLocked, needsDate: this.needsDate(true),
              lockNote: s.printerLocked ? B('printer_lock_note', 'Myselfie er udsolgt som enkeltstående d. [date], men kan stadig bookes sammen med printer. Printeren er derfor lagt i bookingen.', { date: longDate(iso) }) : '',
              title: (this.cfg.printerCard && this.cfg.printerCard.title) || B('line_printer', 'Printer'),
              text: this.cfg.printerCard && this.cfg.printerCard.text,
              image: this.cfg.printerCard && this.cfg.printerCard.image }) +
            (s.printer
              ? '<div class="myselfie-ps__addon-body">' +
                  (hasQty(printer) && this.qtyFor('booth') > 1
                    ? this.qtyStepperHtml('printer', B('qty_printers', 'Antal printere'), this.qtyFor('printer'), Math.min(QTY_MAX, this.qtyFor('booth')), B('printers', 'printere'))
                    : '') +
                  (types.values.length > 1
                    ? '<div class="myselfie-ps__row"><span class="myselfie-ps__row-label">' + escapeHtml(types.name) + '</span>' +
                        '<div class="myselfie-ps__seg">' +
                          types.values.map(function (t) {
                            var on = t === self.printTypeValue(s.printType);
                            return '<button type="button" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '" data-ps-action="print-type" data-value="' + escapeHtml(t) + '" data-ps-focus="pt-' + escapeHtml(t) + '">' + escapeHtml(t) + '</button>';
                          }).join('') +
                        '</div></div>'
                    : '') +
                  (this.cfg.logo
                    ? '<div class="myselfie-ps__row myselfie-ps__row--stack"><span class="myselfie-ps__row-label">' + escapeHtml(B('logo_on_print', 'Logo på print')) + '</span>' +
                        '<div class="myselfie-ps__seg myselfie-ps__seg--wrap">' +
                          this.logoChoices()
                            .map(function (v) {
                              var id = String(v.id);
                              var on = id === self.logoCode();
                              return '<button type="button" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '" data-ps-action="logo" data-value="' + escapeHtml(id) + '" data-ps-focus="l-' + escapeHtml(id) + '">' +
                                escapeHtml(v.title) + ' <small>' + (v.price ? '+' + self.money(v.price) : escapeHtml(B('free_lower', 'gratis'))) + '</small></button>';
                            }).join('') +
                        '</div></div>'
                    : '') +
                  '<div class="myselfie-ps__row"><span class="myselfie-ps__row-label">' + escapeHtml(B('qty_prints', 'Antal prints')) + '</span>' +
                    '<div class="myselfie-ps__stepper">' +
                      '<button type="button" data-ps-action="prints" data-value="-' + PRINTS_PER_ROLL + '" data-ps-focus="p-minus" aria-label="' + escapeHtml(B('prints_fewer', '[n] færre prints', { n: PRINTS_PER_ROLL })) + '"' + (s.prints <= PRINTS_PER_ROLL ? ' disabled' : '') + '>−</button>' +
                      '<output aria-live="polite">' + s.prints + '</output>' +
                      '<button type="button" data-ps-action="prints" data-value="' + PRINTS_PER_ROLL + '" data-ps-focus="p-plus" aria-label="' + escapeHtml(B('prints_more', '[n] flere prints', { n: PRINTS_PER_ROLL })) + '"' + (s.prints >= this.maxPrints() ? ' disabled' : '') + '>+</button>' +
                    '</div></div>' +
                  '<p class="myselfie-ps__note">' +
                    (roll
                      ? (this.qtyFor('printer') > 1 ? B('roll_included_each', 'Første rulle pr. printer ([n] prints) er inkluderet.', { n: this.includedPrints() }) + ' ' : B('roll_included', 'Første rulle ([n] prints) er inkluderet.', { n: PRINTS_PER_ROLL }) + ' ') + B('extra_rolls', 'Ekstra ruller: [price] pr. [n] prints', { price: this.money(roll.price), n: PRINTS_PER_ROLL }) + (this.isB2b() ? ' ' + B('ex_vat', 'ekskl. moms') : '') + '.' +
                        (this.extraRolls() ? ' <strong>+' + this.money(roll.price * this.extraRolls()) + '</strong>' : '')
                      : B('prints_included', '[n] prints er inkluderet.', { n: PRINTS_PER_ROLL })) +
                  '</p>' +
                '</div>'
              : '') +
          '</div>';
      }
      (this.cfg.addons || []).forEach(function (a) {
        var v = self.addonVariant(a, iso);
        if (!v) return;
        var on = !!s.addons[a.key];
        var st = avail && avail.addons[a.key];
        var aFrom = !iso && seasonIdx(a.product) > -1 ? self.spanOf(function (day) { var x = self.addonVariant(a, day); return x ? x.price : null; }) : null;
        html +=
          '<div class="myselfie-ps__addon' + (on ? ' is-on' : '') + self.cardState(on, st, false) + '">' +
            self.toggleHtml({ action: 'addon', value: a.key, on: on, price: aFrom && aFrom.min !== null ? aFrom.min : v.price, from: !!(aFrom && aFrom.varies), title: a.label, text: a.text, note: a.note, image: a.image, kind: 'addon', more: a.key, iso: iso, status: st, needsDate: self.needsDate(a.dated) }) +
            (on && (hasQty(v) || (s.qty.addons[a.key] || 1) > 1) ? '<div class="myselfie-ps__addon-body">' + self.qtyStepperHtml(a.key, B('qty', 'Antal'), self.qtyFor(a.key), QTY_MAX, a.label) + '</div>' : '') +
            (on && a.chooseVariant && self.addonChoices(a).length > 1
              ? '<div class="myselfie-ps__addon-body"><div class="myselfie-ps__row myselfie-ps__row--stack"><span class="myselfie-ps__row-label">' + escapeHtml(B('addon_how_long', 'Hvor længe?')) + '</span>' +
                  '<div class="myselfie-ps__seg myselfie-ps__seg--wrap">' +
                    self.addonChoices(a).map(function (cv) {
                      var chosen = String(cv.id) === String(v.id);
                      return '<button type="button" class="' + (chosen ? 'is-on' : '') + '" aria-pressed="' + chosen + '" data-ps-action="addon-variant" data-key="' + escapeHtml(a.key) + '" data-value="' + escapeHtml(String(cv.id)) + '" data-ps-focus="av-' + escapeHtml(String(cv.id)) + '">' +
                        escapeHtml(cv.title) + ' <small>' + self.money(cv.price) + '</small></button>';
                    }).join('') +
                  '</div></div></div>'
              : '') +
            (on && a.lead ? '<div class="myselfie-ps__addon-body">' + self.leadFieldHtml(a) + '</div>' : '') +
          '</div>';
      });
      // a re-render (e.g. after a cart write) must not take the cursor out of the consent text
      var active = document.activeElement;
      var typing = active && active.matches && active.matches('textarea[data-ps-lead]') && this.el.addons.contains(active)
        ? { key: active.getAttribute('data-ps-lead'), from: active.selectionStart, to: active.selectionEnd } : null;
      setHtml(this.el.addons, html);
      if (typing && !this.el.addons.contains(active)) {
        var again = this.el.addons.querySelector('textarea[data-ps-lead="' + typing.key + '"]');
        if (again) {
          again.focus({ preventScroll: true });
          try { again.setSelectionRange(typing.from, typing.to); } catch (err) { /* not a text field */ }
        }
      }
      if (this.el.catalogue) setHtml(this.el.catalogue, this.catalogueCardHtml());

      // 4. recommendation for the occasion
      var recs = this.recommendations();
      setHtml(this.el.recommend, recs
        .map(function (r) {
          var v = self.recVariant(r, iso);
          var on = !!(v && s.extras[v.id]);
          return (
            '<div class="myselfie-ps__rec' + (on ? ' is-on' : '') + '">' +
              (r.image ? '<img src="' + escapeHtml(r.image) + '" alt="" width="72" height="72" loading="lazy">' : '') +
              '<div class="myselfie-ps__rec-body">' +
                '<span class="myselfie-ps__rec-kicker">' + escapeHtml(B('we_recommend', 'Vi anbefaler')) + '</span>' +
                '<strong>' + escapeHtml(r.title || (r.product && r.product.title) || '') + (v ? ' <span>' + self.money(v.price, true) + '</span>' : '') + '</strong>' +
                (r.text ? '<p>' + escapeHtml(r.text) + '</p>' : '') +
                '<div class="myselfie-ps__rec-actions">' +
                  (v ? '<button type="button" class="myselfie-ps__rec-add" aria-pressed="' + on + '" data-ps-action="rec" data-value="' + escapeHtml(r.id) + '" data-ps-focus="r-' + escapeHtml(r.id) + '">' + escapeHtml(on ? B('added', '✓ Tilføjet') : B('add', '+ Tilføj')) + '</button>' : '') +
                  '<button type="button" class="myselfie-ps__more" data-ps-action="more" data-kind="rec" data-value="' + escapeHtml(r.id) + '">' + escapeHtml(r.linkLabel || B('read_more', 'Læs mere')) + '</button>' +
                '</div>' +
              '</div>' +
            '</div>'
          );
        })
        .join(''));

      // summary (cart page without a date: only what is really in the cart, no "fra" booth line)
      var lines = this.lines(iso);
      if (this.pageMode === 'cart' && !iso) lines = lines.filter(function (l) { return l.key !== 'booth'; });
      if (!iso) {
        // no date: the selection on its cheapest bookable day ("I alt fra") - date ranges and seasons alike
        var cartPage = this.pageMode === 'cart';
        var shown = function (day) {
          var ls = self.lines(day);
          return cartPage ? ls.filter(function (l) { return l.key !== 'booth'; }) : ls;
        };
        var fromSpan = this.spanOf(function (day) { return self.total(shown(day)); });
        if (fromSpan.varies && fromSpan.minDay) lines = shown(fromSpan.minDay);
      }
      var status = iso ? this.dayStatus(iso) : '';
      var emptyCart = this.pageMode === 'cart' && !lines.length;
      setHtml(this.el.summary,
        '<div class="myselfie-ps__summary-date' + (status ? ' is-' + status : '') + '">' +
          '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15.5" rx="3"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>' +
          (iso
            ? '<span><strong>' + longDate(iso) + '</strong> · ' + (status === 'printer_only' || (s.printerLocked && status !== 'sold_out') ? escapeHtml(B('status_with_printer', 'Ledig med printer')) : escapeHtml(statusText(status) || '')) + '</span>'
            : '<span>' + escapeHtml(B('pick_date_in_calendar', 'Vælg en dato i kalenderen')) + '</span>') +
        '</div>' +
        (emptyCart ? '' : '<ul class="myselfie-ps__lines">' +
          lines.map(function (l) {
            if (l.key === 'logo') {
              return '<li class="myselfie-ps__subline"><span><small>' + escapeHtml(l.label + (l.detail ? ': ' + l.detail : '')) + '</small></span><span>' + (l.variant.price ? self.money(l.variant.price * l.qty) : escapeHtml(B('free', 'Gratis'))) + '</span></li>';
            }
            return '<li><span>' + escapeHtml(l.label) + (l.detail ? ' <small>' + escapeHtml(l.detail) + '</small>' : '') + (self.lineInCart(l) ? '<small class="myselfie-ps__cart-chip">' + escapeHtml(B('in_cart', 'I kurven')) + '</small>' : '') + '</span><span>' + self.money(l.variant.price * l.qty) + '</span></li>';
          }).join('') +
          (this.hasPlans() && s.plan === 'full' && this.leaseFullPct()
            ? '<li><span>' + escapeHtml(B('full_pay_discount', 'Rabat ved fuld betaling')) + ' <small>' + escapeHtml(B('full_pay_discount_note', '[pct]% – trækkes i kassen', { pct: this.leaseFullPct() })) + '</small></span><span>−' + this.money(this.total(lines) - this.planTotal(this.total(lines))) + '</span></li>'
            : '') +
        '</ul>' +
        this.totalsHtml(this.planTotal(this.total(lines)), !iso && this.pageMode !== 'cart')) +
        (this.hasPlans() && s.plan === 'monthly'
          ? '<div class="myselfie-ps__vat-row"><span>' + escapeHtml(B('per_month', 'Pr. måned')) + ' <small>(' + escapeHtml(B('months_invoice', '[months], faktura hver måned', { months: monthsText(this.leaseMonths()) })) + ')</small></span><span>' + this.money(Math.round(this.total(lines) / this.leaseMonths())) + '</span></div>'
          : ''));
      this.renderPlans(lines);

      var blocked = status === 'sold_out';
      this.el.book.disabled = s.busy;
      this.el.book.textContent = s.busy ? B('adding_to_cart', 'Lægger i kurven…') : blocked ? B('sold_out_on_date', 'Udsolgt på denne dato') : this.el.book.getAttribute('data-label');
      this.classList.toggle('has-date', !!iso);
      this.renderCheckoutExtras(this.planTotal(this.total(lines)));
      this.renderCartBanner();
      this.renderLiveStatus();
      this.renderValidation();
      this.saveDraft();
      this.liveCheck();

      // calendar: availability of the whole selection + its total per day
      if (this.calendar && this.calendar.configure) {
        var b2b = this.isB2b();
        // lease page: the price is the same on every date, so the calendar shows no day prices
        this.calendar.configure(function (day) { return self.dayStatus(day); }, this.hasPlans() ? null : function (day) {
          var total = self.total(self.lines(day));
          // price as it would be on that day: earmarked days include the printer
          var pv = self.printerVariant(null, day);
          if (pv) {
            var da = self.dayAvailability(day);
            var needsPrinter = da.myselfie === 'sold_out' && da.printer !== 'sold_out';
            if (needsPrinter && !s.printer) total += pv.price;
            if (!needsPrinter && s.printerForced) {
              total -= self.lines(day).filter(function (l) { return /^(printer|logo|rolls)$/.test(l.key); })
                .reduce(function (sum, l) { return sum + l.variant.price * l.qty; }, 0);
            }
          }
          return amount(b2b ? total / (1 + VAT_RATE) : total);
        }, b2b ? B('price_note_ex_vat', 'Samlet pris i kr. ekskl. moms') : B('price_note_inc_vat', 'Samlet pris i kr. inkl. moms'), function (day) { return self.dayLead(day); });
      }

      if (focusKey) {
        var target = this.querySelector('[data-ps-focus="' + focusKey + '"]');
        if (target && !target.disabled) target.focus({ preventScroll: true });
      }
    },

    /** Add-on card: photo, title + price, text, optional note and a Tilføj / Tilføjet pill. */
    renderPlans: function (lines) {
      if (!this.hasPlans()) return;
      if (!this.el.plans) {
        this.el.plans = document.createElement('div');
        this.el.plans.className = 'myselfie-ps__block';
        this.el.plans.setAttribute('data-ps-step', 'plan');
        this.el.summary.parentNode.insertBefore(this.el.plans, this.el.summary);
      }
      var s = this.state;
      var total = this.total(lines);
      var pct = this.leaseFullPct();
      var months = this.leaseMonths();
      var btn = function (key, title, text, disabled) {
        var on = s.plan === key;
        return '<button type="button" role="radio" class="myselfie-ps__pay' + (on ? ' is-on' : '') + '" aria-checked="' + on + '" data-ps-plan="' + key + '" data-ps-focus="plan-' + key + '"' + (disabled ? ' disabled' : '') + '>' +
          '<strong>' + escapeHtml(title) + '</strong><small>' + text + '</small></button>';
      };
      var b2b = s.customer === 'erhverv';
      setHtml(this.el.plans,
        '<p class="myselfie-bc__step-label">' + escapeHtml(B('plan_title', 'Betalingsplan')) + '</p>' +
        '<div class="myselfie-ps__payment" role="radiogroup" aria-label="' + escapeHtml(B('plan_title', 'Betalingsplan')) + '">' +
          btn('full', B('plan_full', 'Fuld betaling') + (pct ? ' ' + B('plan_save', '– spar [pct]%', { pct: pct }) : ''),
            escapeHtml(B('plan_full_text', 'Betal hele lejeperioden nu')) + (pct ? ': ' + B('plan_instead_of', '[price] i stedet for [full]', { price: this.money(this.planTotal(total)), full: this.money(total) }) : '')) +
          btn('monthly', B('plan_monthly', 'Månedlig betaling'),
            b2b
              ? B('plan_monthly_b2b', '[price] pr. måned i [months] – betales via faktura eller EAN hver måned', { price: this.money(Math.round(total / months)), months: monthsText(months) })
              : escapeHtml(B('plan_monthly_private', 'Kun for virksomheder – betales via faktura eller EAN')),
            !b2b) +
        '</div>');
    },

    /** Erhverv explicitly chosen: prices are shown excl. VAT. Privat (default): incl. VAT. */
    isB2b: function () {
      return this.state.customer === 'erhverv';
    },

    /** Price label for the current customer type; business prices carry "ekskl. moms". */
    money: function (ore, tag) {
      if (!this.isB2b()) return kr(ore);
      return krExVat(ore) + (tag ? ' <small class="myselfie-ps__exvat">' + escapeHtml(B('ex_vat', 'ekskl. moms')) + '</small>' : '');
    },

    /** Payment method in effect: Faktura / EAN only for business customers. */
    payMethod: function () {
      this.validPay();
      return this.state.customer === 'erhverv' ? this.state.pay : 'standard';
    },

    /** A method whose option is not rendered (EAN without a discount code, e.g. SE) falls back to standard. */
    validPay: function () {
      var s = this.state;
      if (this.el.billing && !this.el.billing.querySelector('[data-ps-pay="' + String(s.pay).replace(/"/g, '') + '"]')) s.pay = 'standard';
    },

    updateBilling: function () {
      if (!this.el.billing) return;
      var s = this.state;
      var pay = this.payMethod();
      this.querySelectorAll('[data-ps-customer]').forEach(function (b) {
        var on = b.getAttribute('data-ps-customer') === s.customer;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on);
      });
      this.validPay();
      var monthly = this.monthlyPlan();
      if (monthly && s.pay === 'standard') s.pay = 'faktura';
      this.el.billing.querySelectorAll('[data-ps-pay]').forEach(function (b) {
        var on = b.getAttribute('data-ps-pay') === s.pay;
        var off = monthly && b.getAttribute('data-ps-pay') === 'standard';
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on);
        b.disabled = off;
        b.setAttribute('aria-disabled', off);
        b.title = off ? B('not_with_monthly', 'Ikke muligt ved månedlig betaling') : '';
        var small = b.querySelector('small');
        if (small) {
          if (!small.hasAttribute('data-text')) small.setAttribute('data-text', small.textContent);
          small.textContent = off ? B('not_with_monthly', 'Ikke muligt ved månedlig betaling') : small.getAttribute('data-text');
        }
      });
      // say why, right above the options
      var options = this.el.billing.querySelector('.myselfie-ps__pay-options');
      var note = this.el.billing.querySelector('[data-ps-monthly-note]');
      if (options && !note) {
        note = document.createElement('p');
        note.className = 'myselfie-ps__pay-note';
        note.setAttribute('data-ps-monthly-note', '');
        note.textContent = B('monthly_note', 'Månedlig betaling sker via faktura eller EAN. Standard betaling i kassen er ikke mulig.');
        options.parentNode.insertBefore(note, options);
      }
      if (note) note.hidden = !monthly;
      this.el.billing.hidden = s.customer !== 'erhverv';
      this.filterReasons();
      this.classList.toggle('is-b2b', this.isB2b());
      if (this.el.vat) {
        this.el.vat.innerHTML = this.isB2b()
          ? B('vat_b2b_html', '<strong>Priserne vises nu ekskl. moms.</strong> Moms ([pct]%) lægges til i kassen.', { pct: VAT_PCT })
          : escapeHtml(B('vat_private', 'Alle priser er inkl. [pct]% moms.', { pct: VAT_PCT }));
      }
      this.el.billing.querySelectorAll('[data-ps-pay-detail]').forEach(function (d) {
        d.hidden = d.getAttribute('data-ps-pay-detail') !== pay;
      });
    },

    /**
     * Occasions offered per customer type. Erhverv sees the keys in the select's
     * data-business (section setting, in that order); Privat sees every other occasion
     * except the "Erhverv - …" leasing options (settings.mys_lease_reasons, DK r21, r22). A choice that is not offered
     * for the new type is cleared, together with its recommendation.
     */
    filterReasons: function () {
      var select = this.el.reason;
      if (!select) return;
      if (!this.reasonCatalog) {
        this.reasonCatalog = Array.prototype.slice.call(select.querySelectorAll('option[data-key]')).map(function (o) {
          return { key: o.getAttribute('data-key'), value: o.value, label: o.textContent };
        });
        var placeholder = select.querySelector('option:not([data-key])');
        this.reasonPlaceholder = placeholder ? placeholder.textContent : '';
      }
      var catalog = this.reasonCatalog;
      var business = (select.getAttribute('data-business') || '').split(',')
        .map(function (k) { return k.trim(); })
        .filter(Boolean);
      var eventKeys = this.eventReasons();
      var list = KIT.reasonsFor(catalog, this.isB2b(), business, eventKeys);

      var signature = list.map(function (r) { return r.key; }).join(',');
      if (signature === this.reasonSignature) return;
      this.reasonSignature = signature;

      var current = select.value;
      var keep = list.some(function (r) { return r.value === current; });
      select.innerHTML = '';
      var first = document.createElement('option');
      first.value = '';
      first.disabled = true;
      first.textContent = this.reasonPlaceholder;
      select.appendChild(first);
      list.forEach(function (r) {
        var o = document.createElement('option');
        o.value = r.value;
        o.textContent = r.label;
        o.setAttribute('data-key', r.key);
        select.appendChild(o);
      });
      select.value = keep ? current : '';
      if (!keep && eventKeys.length && list.length) {
        // event pages start on their own occasion
        select.value = list[0].value;
        keep = true;
      }
      if (!keep) {
        first.selected = true;
        this.el.other.hidden = true;
        this.pruneRecExtras();
      }
      // one occasion: shown as a fact, not a question
      var fixed = this.querySelector('[data-ps-reason-fixed]');
      var locked = eventKeys.length === 1 && list.length === 1;
      if (fixed) {
        fixed.hidden = !locked;
        fixed.textContent = locked ? list[0].label : '';
      }
      select.parentNode.hidden = locked;
      this.classList.toggle('has-fixed-reason', locked);
    },

    eventReasons: function () {
      return (this.getAttribute('data-event-reasons') || '').split(',')
        .map(function (k) { return k.trim(); })
        .filter(Boolean);
    },

    /* ---------- remembered choices (6 hours) ---------- */

    restoreDraft: function () {
      var d = readDraft();
      this.draftData = d;
      if (!d) return;
      var s = this.state;
      var el = this.el;
      // live cart: products live in the cart, never in the draft (an empty cart after a placed
      // order must not get yesterday's add-ons back)
      var products = !(this.cartSync && this.cartSync.enabled);
      if (!s.iso && d.iso) s.iso = d.iso;
      if (typeof d.duration === 'string' && d.duration) s.duration = d.duration; // update() falls back if it is gone
      if (products) s.printer = !!d.printer;
      if (d.printType) s.printType = this.printTypeValue(d.printType);
      if (products && d.prints > 0) s.prints = d.prints;
      if (d.plan === 'full' || d.plan === 'monthly') s.plan = d.plan;
      if (d.qty && typeof d.qty === 'object') {
        var n = function (x) { return Math.max(1, Math.min(QTY_MAX, Math.round(Number(x)) || 1)); };
        s.qty.booth = n(d.qty.booth);
        if (products) {
          s.qty.printer = Math.min(n(d.qty.printer), s.qty.booth);
          if (d.qty.addons && typeof d.qty.addons === 'object') {
            Object.keys(d.qty.addons).forEach(function (k) { s.qty.addons[k] = n(d.qty.addons[k]); });
          }
        }
      }
      if (products && typeof d.logo === 'string') s.logo = d.logo;
      if (d.customer === 'privat' || d.customer === 'erhverv') s.customer = d.customer;
      if (typeof d.pay === 'string' && d.pay) s.pay = d.pay;
      if (products && d.addons && typeof d.addons === 'object') s.addons = d.addons;
      if (products && d.addonVar && typeof d.addonVar === 'object') s.addonVar = d.addonVar;
      if (d.leadText && typeof d.leadText === 'object') {
        Object.keys(d.leadText).forEach(function (k) { if (typeof d.leadText[k] === 'string') s.leadText[k] = d.leadText[k]; });
      }
      if (products && d.extras && typeof d.extras === 'object') s.extras = d.extras;
      if (d.reason && el.reason && el.reason.querySelector('option[value="' + String(d.reason).replace(/"/g, '') + '"]')) {
        el.reason.value = d.reason;
        el.other.hidden = d.reason !== 'andet';
      }
      if (d.other) el.other.value = d.other;
      ['invoice', 'ean', 'att'].forEach(function (k) {
        if (el[k] && d[k]) el[k].value = d[k];
      });
      this.restored = !!(d.iso || s.printer || Object.keys(s.addons).length || Object.keys(s.extras).length || d.reason);
      var note = this.querySelector('[data-ps-restored]');
      if (note) note.hidden = !this.restored;
    },

    leadFieldHtml: function (a) {
      var id = this.id + '-lead-' + a.key;
      var err = (this.closest('.shopify-section') || this).id + '-err-lead-' + a.key;
      return '<label class="myselfie-ps__field-label" for="' + escapeHtml(id) + '">' + escapeHtml(a.lead.label || B('consent_label', 'Samtykketekst')) + '</label>' +
        (a.lead.help ? '<p class="myselfie-ps__help">' + escapeHtml(a.lead.help) + '</p>' : '') +
        '<textarea id="' + escapeHtml(id) + '" class="myselfie-ps__input" rows="3" maxlength="500" data-ps-lead="' + escapeHtml(a.key) + '"' +
          ' aria-describedby="' + escapeHtml(err) + '" placeholder="' + escapeHtml(a.lead.placeholder || '') + '">' + escapeHtml(this.state.leadText[a.key] || '') + '</textarea>' +
        '<p class="myselfie-ps__field-error" id="' + escapeHtml(err) + '" data-ps-error-for="lead-' + escapeHtml(a.key) + '" hidden></p>';
    },

    saveDraft: function () {
      if (!this.cfg || !this.state || this.booked) return;
      var s = this.state;
      var el = this.el;
      writeDraft({
        iso: s.iso,
        duration: s.duration,
        printer: s.printer && !s.printerForced, // the sold-out earmark is worked out again
        printType: s.printType,
        prints: s.prints,
        qty: s.qty,
        plan: s.plan,
        logo: s.logo,
        customer: s.customer,
        pay: s.pay,
        addons: s.addons,
        addonVar: s.addonVar,
        leadText: s.leadText,
        extras: this.draftExtras(),
        reason: el.reason ? el.reason.value : '',
        other: el.other ? el.other.value : '',
        invoice: el.invoice ? el.invoice.value : '',
        ean: el.ean ? el.ean.value : '',
        att: el.att ? el.att.value : '',
      });
    },

    resetDraft: function () {
      clearDraft();
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(CUSTOMER_KEY);
        sessionStorage.removeItem(CARRY_KEY);
      } catch (e) {
        /* storage blocked */
      }
      this.booked = true; // no save while the page reloads
      window.location.reload();
    },

    /** Cart note, attributes and discount code for the chosen payment method. */
    billingData: function () {
      return KIT.billingData({
        customer: this.state.customer,
        pay: this.payMethod(),
        invoice: this.el.invoice ? this.el.invoice.value : '',
        ean: this.el.ean ? this.el.ean.value : '',
        att: this.el.att ? this.el.att.value : '',
        codes: { faktura: this.el.billing.getAttribute('data-code-faktura') || '', ean: this.el.billing.getAttribute('data-code-ean') || '' },
        lease: this.hasPlans() ? {
          plan: this.state.plan,
          months: this.leaseMonths(),
          total: this.total(this.lines(this.state.iso)),
          pct: this.leaseFullPct(),
          fullCode: this.getAttribute('data-lease-full-code') || '',
          monthlyCode: this.getAttribute('data-lease-monthly-code') || '',
        } : null,
      });
    },

    totalsHtml: function (total, from) {
      var prefix = escapeHtml(from ? B('total_from', 'I alt fra') : B('total', 'I alt'));
      if (!this.isB2b()) {
        return '<div class="myselfie-ps__total"><span>' + prefix + ' <small>' + escapeHtml(B('inc_vat', 'inkl. moms')) + '</small></span><strong>' + kr(total) + '</strong></div>';
      }
      return (
        '<div class="myselfie-ps__total myselfie-ps__total--b2b"><span>' + prefix + ' <small>' + escapeHtml(B('ex_vat', 'ekskl. moms')) + '</small></span><strong>' + krExVat(total) + '</strong></div>' +
        '<div class="myselfie-ps__vat-row"><span>' + escapeHtml(B('vat_pct', 'Moms ([pct]%)', { pct: VAT_PCT })) + '</span><span>' + krDecimal(total - Math.round(total / (1 + VAT_RATE))) + '</span></div>' +
        '<div class="myselfie-ps__vat-row"><span>' + escapeHtml(B('total_inc_vat', 'I alt inkl. moms')) + ' <small>' + escapeHtml(B('amount_at_checkout', '(beløbet i kassen)')) + '</small></span><span>' + kr(total) + '</span></div>'
      );
    },

    /** Add-on card: photo (opens details), title + price, text, note, Tilføj switch + Læs mere. */
    /** Extra classes for an add-on card: sold out (greyed), conflict (chosen but sold out), locked. */
    cardState: function (on, status, locked) {
      var cls = '';
      if (status === 'sold_out') cls += on ? ' is-conflict' : ' is-soldout';
      if (locked) cls += ' is-locked';
      return cls;
    },

    toggleHtml: function (o) {
      var soldOut = o.status === 'sold_out';
      var priceHtml = soldOut
        ? '<span class="myselfie-ps__soldout-tag">' + escapeHtml(B('sold_out_on', 'Udsolgt d. [date]', { date: shortDate(o.iso) })) + '</span>'
        : '<span class="myselfie-ps__toggle-price">' + (o.from ? '<span class="myselfie-ps__from">' + escapeHtml(B('from', 'fra')) + ' </span>' : '') + '+' + this.money(o.price, true) + '</span>' +
          (o.status === 'low' ? ' <span class="myselfie-ps__low-tag">' + escapeHtml(statusText('low')) + '</span>' : '') +
          (o.locked ? ' <span class="myselfie-ps__lock-tag">' + escapeHtml(B('required', 'Påkrævet')) + '</span>' : '');
      var pill;
      if (o.locked) {
        pill = '<button type="button" class="myselfie-ps__pill" role="switch" aria-checked="true" disabled>' + escapeHtml(B('included', '✓ Inkluderet')) + '</button>';
      } else if (o.needsDate && !o.on) {
        // live cart: the product gets the event date in the cart, so the date comes first
        pill = '<button type="button" class="myselfie-ps__pill myselfie-ps__pill--needs-date" role="switch" aria-checked="false" data-ps-action="' + o.action + '" data-value="' + escapeHtml(o.value) + '" disabled>' + escapeHtml(B('pick_date_first', 'Vælg dato først')) + '</button>';
      } else if (soldOut && !o.on) {
        pill = '<button type="button" class="myselfie-ps__pill" disabled>' + escapeHtml(B('sold_out', 'Udsolgt')) + '</button>';
      } else if (soldOut && o.on) {
        pill = '<button type="button" class="myselfie-ps__pill myselfie-ps__pill--remove" role="switch" aria-checked="true"' +
          ' data-ps-action="' + o.action + '" data-value="' + escapeHtml(o.value) + '" data-ps-focus="t-' + escapeHtml(o.action + o.value) + '">' + escapeHtml(B('remove', 'Fjern')) + '</button>';
      } else {
        pill = '<button type="button" class="myselfie-ps__pill" role="switch" aria-checked="' + o.on + '"' +
          ' data-ps-action="' + o.action + '" data-value="' + escapeHtml(o.value) + '" data-ps-focus="t-' + escapeHtml(o.action + o.value) + '">' +
          escapeHtml(o.on ? B('added', '✓ Tilføjet') : B('add', '+ Tilføj')) + '</button>';
      }
      var more = o.more ? ' data-ps-action="more" data-kind="' + o.kind + '" data-value="' + escapeHtml(o.more) + '"' : '';
      return (
        '<div class="myselfie-ps__toggle">' +
          (o.image
            ? '<button type="button" class="myselfie-ps__thumb-btn"' + more + ' aria-label="' + escapeHtml(B('see_more_about', 'Se mere om [title]', { title: o.title })) + '">' +
                '<img class="myselfie-ps__thumb" src="' + escapeHtml(o.image) + '" alt="" width="72" height="72" loading="lazy"></button>'
            : '<span class="myselfie-ps__thumb myselfie-ps__thumb--empty" aria-hidden="true"></span>') +
          '<span class="myselfie-ps__toggle-body">' +
            '<strong>' + escapeHtml(o.title) + ' ' + priceHtml + '</strong>' +
            (o.text ? '<small>' + escapeHtml(o.text) + '</small>' : '') +
            (o.note && !soldOut ? '<em class="myselfie-ps__toggle-note">' + escapeHtml(o.note) + '</em>' : '') +
            (soldOut && o.on ? '<em class="myselfie-ps__conflict-note">' + escapeHtml(B('conflict_note', 'Udsolgt på jeres dato – fjern den eller vælg en anden dato.')) + '</em>' : '') +
            (soldOut && !o.on ? '<em class="myselfie-ps__soldout-note">' + escapeHtml(B('not_available_on', 'Ikke ledig d. [date].', { date: longDate(o.iso) })) + '</em>' : '') +
            (o.lockNote ? '<em class="myselfie-ps__lock-note">' + escapeHtml(o.lockNote) + '</em>' : '') +
            '<span class="myselfie-ps__card-actions">' +
              pill +
              (o.more ? '<button type="button" class="myselfie-ps__more"' + more + '>' + escapeHtml(B('read_more', 'Læs mere')) + '</button>' : '') +
            '</span>' +
          '</span>' +
        '</div>'
      );
    },

    /* ---------- extras (catalogue items + recommended packages) ---------- */

    addExtra: function (product, variant, source) {
      if (!product || !variant) return;
      this.state.extras[variant.id] = {
        id: variant.id,
        handle: product.handle,
        title: product.title,
        variantTitle: variantLabel(variant, product.options ? product : this.products[product.handle]),
        price: variant.price,
        available: variant.available !== false,
        qty: 1,
        source: source,
      };
    },

    toggleExtra: function (variantId, handle) {
      var extras = this.state.extras;
      if (extras[variantId]) {
        delete extras[variantId];
        return;
      }
      var product = this.products[handle];
      if (!product) return;
      var variant = product.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
      this.addExtra(product, variant, 'catalogue');
    },

    recFor: function (id) {
      return (this.cfg.recommendations || []).filter(function (r) { return r.id === id; })[0] || null;
    },

    toggleRec: function (id) {
      var r = this.recFor(id);
      var v = r && this.recVariant(r);
      if (!v) return;
      var on = r.product.variants.filter(function (x) { return this.state.extras[x.id]; }, this)[0];
      if (on) delete this.state.extras[on.id];
      else this.addExtra({ handle: r.product.handle, title: r.product.title, options: r.product.options }, v, 'rec');
    },

    /** The product behind an extra (catalogue / detail fetch / recommendation), for its season variants. */
    extraProduct: function (x) {
      if (!x) return null;
      var p = this.products[x.handle];
      if (p && typeof p === 'object' && p.variants) return p;
      var r = (this.cfg.recommendations || []).filter(function (y) { return y.product && y.product.handle === x.handle; })[0];
      return r ? r.product : null;
    },

    /**
     * Seasons follow the date: a chosen extra or logo of a product with a "Sæson" option becomes the same variant in
     * the season of the selection's date (the live cart then swaps the line, like a booth of another price tier).
     */
    reseason: function () {
      var s = this.state;
      var self = this;
      if (!sk()) return;
      Object.keys(s.extras).forEach(function (id) {
        var x = s.extras[id];
        var p = self.extraProduct(x);
        if (!p || seasonIdx(p) < 0) return;
        var t = inSeason(p, { id: x.id }, s.iso);
        if (!t || String(t.id) === String(x.id)) return;
        delete s.extras[id];
        s.extras[t.id] = Object.assign({}, x, { id: t.id, price: t.price, available: t.available !== false, variantTitle: shownTitle(t, p) });
      });
      if (s.logo) s.logo = this.logoCode(); // the variant is picked per date and print type (logoVariant)
    },

    /** The variants the customer chooses between: one per option combination, in the season of the date. */
    visibleVariants: function (p) {
      if (!p || seasonIdx(p) < 0) return (p && p.variants) || [];
      var iso = this.state.iso;
      var seen = {};
      var out = [];
      p.variants.forEach(function (v) {
        var t = shownTitle(v, p);
        if (seen[t]) return;
        seen[t] = true;
        var x = inSeason(p, v, iso);
        if (x) out.push(Object.assign({}, x, { title: shownTitle(x, p) || x.title }));
      });
      return out;
    },

    /** Packages added from a recommendation leave when the occasion no longer recommends them. */
    pruneRecExtras: function () {
      var keep = {};
      this.recommendations().forEach(function (r) {
        (r.product ? r.product.variants : []).forEach(function (v) { keep[v.id] = true; });
      });
      var extras = this.state.extras;
      var live = this.cartSync && this.cartSync.enabled;
      Object.keys(extras).forEach(function (id) {
        if (extras[id].source !== 'rec' || keep[id]) return; // 'cart' extras stay: they are in the cart
        // live cart: the package is in the cart already; it stays and is listed with the other extras
        if (live) extras[id].source = 'catalogue';
        else delete extras[id];
      });
    },

    catalogueCardHtml: function () {
      var c = this.cfg.catalogue;
      if (!c) return '';
      var self = this;
      var chosen = Object.keys(this.state.extras)
        .map(function (id) { return self.state.extras[id]; })
        .filter(function (x) { return x.source === 'catalogue' || x.source === 'cart'; });
      return (
        '<div class="myselfie-ps__cat' + (chosen.length ? ' is-on' : '') + '">' +
          '<button type="button" class="myselfie-ps__cat-open" data-ps-action="catalogue" data-ps-focus="cat-open">' +
            '<span class="myselfie-ps__cat-thumbs" aria-hidden="true">' +
              (c.preview || []).slice(0, 3).map(function (src) { return '<img src="' + escapeHtml(src) + '" alt="" width="44" height="44" loading="lazy">'; }).join('') +
            '</span>' +
            '<span class="myselfie-ps__cat-text"><strong>' + escapeHtml(c.title || B('more_addons', 'Flere tilvalg')) + '</strong>' +
              (c.text ? '<small>' + escapeHtml(c.text) + '</small>' : '') + '</span>' +
            '<span class="myselfie-ps__cat-cta">' + escapeHtml(B('see_range', 'Se udvalget')) + (c.count ? ' <small>(' + c.count + ')</small>' : '') + '</span>' +
          '</button>' +
          (chosen.length
            ? '<ul class="myselfie-ps__chosen">' +
                chosen.map(function (x) {
                  return '<li><span>' + escapeHtml(x.title) + (x.variantTitle ? ' <small>' + escapeHtml(x.variantTitle) + '</small>' : '') + (x.source === 'cart' ? '<small class="myselfie-ps__cart-chip">' + escapeHtml(B('from_cart', 'Fra kurven')) + '</small>' : '') + '</span>' +
                    '<span class="myselfie-ps__chosen-price">' + self.money(x.price * x.qty) + '</span>' +
                    '<button type="button" data-ps-action="extra-remove" data-value="' + x.id + '" aria-label="' + escapeHtml(B('remove_item', 'Fjern [title]', { title: x.title })) + '">' + ICON_CLOSE + '</button></li>';
                }).join('') +
              '</ul>'
            : '') +
        '</div>'
      );
    },

    /* ---------- modal: product details + catalogue browser ---------- */

    modalShell: function () {
      var dialog = this.el.modal;
      if (!dialog || dialog._built) return dialog;
      dialog._built = true;
      var self = this;
      dialog.setAttribute('aria-labelledby', this.uid + '-title');
      dialog.innerHTML =
        '<div class="myselfie-ps__modal-head">' +
          '<button type="button" class="myselfie-ps__icon-btn" data-ps-action="modal-back" data-ps-back aria-label="' + escapeHtml(B('back_to_addons', 'Tilbage til alle tilvalg')) + '" hidden>' + ICON_BACK + '</button>' +
          '<h3 class="myselfie-ps__modal-title" id="' + this.uid + '-title"></h3>' +
          '<button type="button" class="myselfie-ps__icon-btn" data-ps-action="modal-close" aria-label="' + escapeHtml(B('close', 'Luk')) + '">' + ICON_CLOSE + '</button>' +
        '</div>' +
        '<div class="myselfie-ps__modal-tools" data-ps-tools hidden>' +
          '<input type="search" class="myselfie-ps__input myselfie-ps__search" placeholder="' + escapeHtml(B('search_addons', 'Søg i tilvalg')) + '" aria-label="' + escapeHtml(B('search_addons', 'Søg i tilvalg')) + '" data-ps-search>' +
          '<div class="myselfie-ps__chips" data-ps-chips></div>' +
        '</div>' +
        '<div class="myselfie-ps__modal-content" data-ps-content></div>' +
        '<div class="myselfie-ps__modal-foot" data-ps-foot></div>';
      dialog.addEventListener('close', function () {
        self.modal.open = false;
        Array.prototype.forEach.call(dialog.querySelectorAll('video'), function (v) { v.pause(); });
        document.documentElement.classList.remove('myselfie-ps-lock');
        // the opener may have been re-rendered while the modal was open: find its replacement
        var opener = self.modalOpener && self.modalOpener.isConnected ? self.modalOpener
          : self.modalOpenerKey ? self.querySelector(self.modalOpenerKey) : null;
        if (opener) opener.focus({ preventScroll: true });
      });
      // a click on the backdrop reaches the dialog itself
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });
      dialog.querySelector('[data-ps-search]').addEventListener('input', function (e) {
        self.modal.query = e.target.value;
        self.renderModal();
      });
      dialog.addEventListener('change', function (e) {
        if (e.target.matches('[data-ps-variant]')) {
          self.modal.variant = e.target.value;
          self.renderModal('m-variant');
        }
      });
      return dialog;
    },

    showModal: function () {
      var dialog = this.modalShell();
      if (!dialog) return;
      if (!this.modal.open) {
        var active = document.activeElement;
        this.modalOpener = active;
        this.modalOpenerKey = active && active.getAttribute && active.getAttribute('data-ps-action')
          ? (active.classList[0] ? '.' + active.classList[0] : '') + ['data-ps-action', 'data-kind', 'data-value'].map(function (a) {
              var v = active.getAttribute(a);
              return v === null ? '' : '[' + a + '="' + v.replace(/"/g, '\\"') + '"]';
            }).join('')
          : '';
        this.modal.open = true;
        document.documentElement.classList.add('myselfie-ps-lock');
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
    },

    closeModal: function () {
      var dialog = this.el.modal;
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else {
        dialog.removeAttribute('open');
        this.modal.open = false;
        document.documentElement.classList.remove('myselfie-ps-lock');
      }
    },

    openCatalogue: function (back) {
      var m = this.modal;
      m.view = 'catalogue';
      m.from = '';
      if (!back) {
        m.filter = '';
        m.query = '';
      }
      this.showModal();
      if (!back) this.el.modal.querySelector('[data-ps-search]').value = '';
      this.loadCatalogue();
      this.renderModal();
      var content = this.el.modal.querySelector('[data-ps-content]');
      if (!back && content) content.scrollTop = 0;
    },

    loadCatalogue: function () {
      if (this.catalogueItems && this.catalogueItems !== 'error') return this._catPromise || Promise.resolve();
      var c = this.cfg.catalogue;
      if (!c) return Promise.resolve();
      var self = this;
      this.catalogueItems = 'loading';
      this._catPromise = fetch(rootUrl('collections/' + encodeURIComponent(c.handle) + '/products.json?limit=250'), { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('catalogue ' + r.status);
          return r.json();
        })
        .then(function (json) {
          var exclude = c.exclude || [];
          self.catalogueItems = (json.products || [])
            .map(productFromJson)
            .filter(function (p) {
              if (exclude.indexOf(p.handle) > -1) return false;
              if (/^(myselfie|mygreetings|gift card)$/i.test(p.type)) return false;
              return p.variants.some(function (v) { return v.price > 0; });
            });
          self.catalogueItems.forEach(function (p) { self.products[p.handle] = p; });
          if (self.modal.open) self.renderModal();
        })
        .catch(function () {
          self.catalogueItems = 'error';
          if (self.modal.open) self.renderModal();
        });
      return this._catPromise;
    },

    /** Details for an add-on ('addon'), the printer ('printer'), a recommendation ('rec') or a catalogue product ('extra'). */
    openDetail: function (kind, value, from) {
      var m = this.modal;
      m.view = 'detail';
      m.kind = kind;
      m.value = value;
      m.from = from;
      m.image = 0;
      m.variant = '';
      this.showModal();
      this.fetchProduct(this.detailHandle());
      this.renderModal();
      var content = this.el.modal.querySelector('[data-ps-content]');
      if (content) content.scrollTop = 0;
    },

    /** Loads /products/<handle>.js once into this.products (details, photos, description). */
    fetchProduct: function (handle) {
      var self = this;
      if (!handle || this.products[handle]) return;
      this.products[handle] = 'loading';
      fetch(rootUrl('products/' + encodeURIComponent(handle) + '.js'), { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('product ' + r.status);
          return r.json();
        })
        .then(function (j) {
          self.products[handle] = productFromJs(j);
        })
        .catch(function () {
          self.products[handle] = 'error';
        })
        .then(function () {
          if (self.modal.open) self.renderModal();
        });
    },

    /** "Om Myselfie": what the product is, how it works, photos/video and common questions. No buying here. */
    openAbout: function () {
      var m = this.modal;
      m.view = 'about';
      m.from = '';
      m.image = 0;
      this.showModal();
      this.fetchProduct(this.boothHandle(this.state.iso));
      this.renderModal();
      var content = this.el.modal.querySelector('[data-ps-content]');
      if (content) content.scrollTop = 0;
    },

    aboutHtml: function () {
      var a = this.cfg.about || {};
      var product = this.products[this.boothHandle(this.state.iso)];
      var images = product && typeof product === 'object' ? product.images : [];
      var m = this.modal;
      var main = images[Math.min(m.image, images.length - 1)] || images[0];
      var media = '';
      if (a.video) {
        media += '<video class="myselfie-ps__about-video" src="' + escapeHtml(a.video) + '"' + (images[0] ? ' poster="' + escapeHtml(img(images[0], 900)) + '"' : '') +
          ' autoplay muted loop playsinline preload="metadata" aria-label="' + escapeHtml(B('video_label', 'Myselfie i brug')) + '"></video>';
      } else if (main) {
        media += '<img class="myselfie-ps__gallery-main" src="' + escapeHtml(img(main, 900)) + '" alt="' + escapeHtml(B('line_booth', 'Myselfie photo booth')) + '">';
      }
      if (images.length > 1) {
        media += '<div class="myselfie-ps__gallery-thumbs">' +
          images.slice(0, 8).map(function (src, i) {
            var on = !a.video && i === m.image;
            return (a.video ? '<span class="myselfie-ps__about-thumb">' : '<button type="button" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '" aria-label="' + escapeHtml(B('image_n', 'Billede [n]', { n: i + 1 })) + '" data-ps-action="image" data-value="' + i + '" data-ps-focus="i-' + i + '">') +
              '<img src="' + escapeHtml(img(src, 140)) + '" alt="" loading="lazy" width="64" height="64">' + (a.video ? '</span>' : '</button>');
          }).join('') + '</div>';
      }
      var steps = (a.steps || []).map(function (line) {
        var parts = String(line).split('|');
        return { title: (parts[0] || '').trim(), text: (parts[1] || '').trim() };
      }).filter(function (x) { return x.title; });
      var faq = this.querySelector('[data-ps-faq]');
      var faqHtml = faq ? faq.innerHTML.trim() : '';
      return (
        '<div class="myselfie-ps__about">' +
          (media ? '<div class="myselfie-ps__about-media">' + media + '</div>' : '') +
          '<div class="myselfie-ps__about-text">' +
            (a.lead ? '<p class="myselfie-ps__detail-lead">' + escapeHtml(a.lead) + '</p>' : '') +
            (a.included && a.included.filter(Boolean).length
              ? '<p class="myselfie-ps__about-h">' + escapeHtml(B('always_included', 'Altid inkluderet')) + '</p><ul class="myselfie-ps__pitch">' +
                  a.included.filter(function (x) { return String(x).trim(); }).map(function (x) { return '<li>' + escapeHtml(String(x).trim()) + '</li>'; }).join('') + '</ul>'
              : '') +
            (steps.length
              ? '<p class="myselfie-ps__about-h">' + escapeHtml(B('how_it_works', 'Sådan foregår det')) + '</p><ol class="myselfie-ps__how-steps">' +
                  steps.map(function (x) { return '<li><strong>' + escapeHtml(x.title) + '</strong>' + (x.text ? ' <span>' + escapeHtml(x.text) + '</span>' : '') + '</li>'; }).join('') + '</ol>'
              : '') +
            (faqHtml ? '<p class="myselfie-ps__about-h">' + escapeHtml(B('faq', 'Ofte stillede spørgsmål')) + '</p>' + faqHtml : '') +
          '</div>' +
        '</div>'
      );
    },

    detailHandle: function () {
      var m = this.modal;
      if (m.kind === 'extra') return m.value;
      if (m.kind === 'printer') return this.cfg.printer && this.cfg.printer.handle;
      if (m.kind === 'addon') {
        var a = (this.cfg.addons || []).filter(function (x) { return x.key === m.value; })[0];
        return a && a.product.handle;
      }
      if (m.kind === 'rec') {
        var r = this.recFor(m.value);
        return r && r.product ? r.product.handle : '';
      }
      return '';
    },

    renderModal: function (focusKey) {
      var dialog = this.modalShell();
      if (!dialog || !this.modal.open) return;
      var m = this.modal;
      var title = dialog.querySelector('.myselfie-ps__modal-title');
      var tools = dialog.querySelector('[data-ps-tools]');
      dialog.querySelector('[data-ps-back]').hidden = !(m.view === 'detail' && m.from === 'catalogue');
      dialog.classList.toggle('is-catalogue', m.view === 'catalogue');
      dialog.classList.toggle('is-about', m.view === 'about');
      tools.hidden = m.view !== 'catalogue';

      if (m.view === 'about') {
        title.textContent = (this.cfg.about && this.cfg.about.title) || B('about_title', 'Om Myselfie');
        setHtml(dialog.querySelector('[data-ps-content]'), this.aboutHtml());
      } else if (m.view === 'catalogue') {
        title.textContent = (this.cfg.catalogue && this.cfg.catalogue.title) || B('more_addons', 'Flere tilvalg');
        setHtml(dialog.querySelector('[data-ps-chips]'), this.chipsHtml());
        setHtml(dialog.querySelector('[data-ps-content]'), this.catalogueHtml());
      } else {
        var d = this.detailData();
        title.textContent = d.title;
        setHtml(dialog.querySelector('[data-ps-content]'), this.detailHtml(d));
      }
      setHtml(dialog.querySelector('[data-ps-foot]'), this.modalFootHtml());

      if (focusKey) {
        var target = dialog.querySelector('[data-ps-focus="' + focusKey + '"]');
        if (target && !target.disabled) target.focus({ preventScroll: true });
      }
    },

    catalogueFiltered: function () {
      var items = Array.isArray(this.catalogueItems) ? this.catalogueItems : [];
      var m = this.modal;
      var q = m.query.trim().toLowerCase();
      return items.filter(function (p) {
        if (m.filter && p.tags.indexOf(m.filter) < 0) return false;
        return !q || p.title.toLowerCase().indexOf(q) > -1;
      });
    },

    chipsHtml: function () {
      var c = this.cfg.catalogue;
      var items = Array.isArray(this.catalogueItems) ? this.catalogueItems : [];
      var m = this.modal;
      var chips = [{ tag: '', label: B('all', 'Alle') }].concat((c && c.filters) || []).filter(function (f) {
        return !f.tag || items.some(function (p) { return p.tags.indexOf(f.tag) > -1; });
      });
      if (chips.length < 2) return '';
      return chips.map(function (f) {
        var on = f.tag === m.filter;
        return '<button type="button" class="myselfie-ps__chip' + (on ? ' is-on' : '') + '" aria-pressed="' + on + '" data-ps-action="filter" data-value="' + escapeHtml(f.tag) + '" data-ps-focus="f-' + escapeHtml(f.tag) + '">' + escapeHtml(f.label) + '</button>';
      }).join('');
    },

    catalogueHtml: function () {
      if (this.catalogueItems === 'loading' || this.catalogueItems === null) {
        return '<div class="myselfie-ps__modal-state"><span class="myselfie-ps__spinner" aria-hidden="true"></span>' + escapeHtml(B('loading_addons', 'Henter tilvalg…')) + '</div>';
      }
      if (this.catalogueItems === 'error') {
        return '<div class="myselfie-ps__modal-state">' + escapeHtml(B('addons_failed', 'Tilvalgene kunne ikke hentes. Prøv igen om lidt.')) + '</div>';
      }
      var items = this.catalogueFiltered();
      if (!items.length) return '<div class="myselfie-ps__modal-state">' + escapeHtml(B('no_addons_match', 'Ingen tilvalg matcher din søgning.')) + '</div>';
      var self = this;
      var extras = this.state.extras;
      return (
        '<ul class="myselfie-ps__grid">' +
          items.map(function (p) {
            var vs = self.visibleVariants(p);
            var available = vs.filter(function (v) { return v.available; });
            var single = vs.length === 1 ? vs[0] : null;
            var chosen = p.variants.some(function (v) { return extras[v.id]; });
            var from = Math.min.apply(null, vs.map(function (v) { return v.price; }));
            var more = ' data-ps-action="more" data-kind="extra" data-from="catalogue" data-value="' + escapeHtml(p.handle) + '"';
            var button;
            if (!available.length) button = '<span class="myselfie-ps__tile-out">' + escapeHtml(B('sold_out', 'Udsolgt')) + '</span>';
            else if (single) {
              button = '<button type="button" class="myselfie-ps__pill" role="switch" aria-checked="' + chosen + '" data-ps-action="extra" data-value="' + single.id + '" data-handle="' + escapeHtml(p.handle) + '" data-ps-focus="x-' + single.id + '" aria-label="' + escapeHtml(chosen ? B('remove_item', 'Fjern [title]', { title: p.title }) : B('add_item', 'Tilføj [title]', { title: p.title })) + '">' + escapeHtml(chosen ? B('added', '✓ Tilføjet') : B('add', '+ Tilføj')) + '</button>';
            } else {
              button = '<button type="button" class="myselfie-ps__pill' + (chosen ? ' is-chosen' : '') + '"' + more + '>' + escapeHtml(chosen ? B('chosen', '✓ Valgt') : B('choose', 'Vælg')) + '</button>';
            }
            return (
              '<li class="myselfie-ps__tile' + (chosen ? ' is-on' : '') + '">' +
                '<button type="button" class="myselfie-ps__tile-media"' + more + ' aria-label="' + escapeHtml(B('see_more_about', 'Se mere om [title]', { title: p.title })) + '">' +
                  (p.images[0] ? '<img src="' + escapeHtml(img(p.images[0], 360)) + '" alt="" loading="lazy" width="180" height="180">' : '') +
                '</button>' +
                '<div class="myselfie-ps__tile-body">' +
                  '<button type="button" class="myselfie-ps__tile-title"' + more + '>' + escapeHtml(p.title) + '</button>' +
                  '<span class="myselfie-ps__tile-price">' + (single ? '' : escapeHtml(B('from', 'fra')) + ' ') + self.money(from, true) + '</span>' +
                  button +
                '</div>' +
              '</li>'
            );
          }).join('') +
        '</ul>'
      );
    },

    /** What the detail view shows and which switch it drives. */
    detailData: function () {
      var m = this.modal;
      var s = this.state;
      var handle = this.detailHandle();
      var product = handle ? this.products[handle] : null;
      var d = { pitch: [], title: '', product: null, loading: product === 'loading', error: product === 'error', text: '', link: '', linkLabel: '', variant: null, variants: [], action: '', value: '', on: false, priceNote: '' };
      if (product && typeof product === 'object') d.product = product;

      if (m.kind === 'addon') {
        var a = (this.cfg.addons || []).filter(function (x) { return x.key === m.value; })[0];
        d.title = a ? a.label : '';
        d.variant = a && this.addonVariant(a);
        d.action = 'addon';
        d.value = m.value;
        d.on = !!s.addons[m.value];
        d.pitch = pitchLines(a && a.product.short, a && a.pitch);
        d.needsDate = !!a && this.needsDate(a.dated);
        if (s.iso && a && a.component) d.soldOutOn = statusFor(s.iso, [a.component]) === 'sold_out' ? s.iso : '';
      } else if (m.kind === 'printer') {
        d.title = (this.cfg.printerCard && this.cfg.printerCard.title) || B('line_printer', 'Printer');
        d.variant = this.printerVariant();
        d.action = 'printer';
        d.on = s.printer;
        d.pitch = pitchLines(this.cfg.printer && this.cfg.printer.short, this.cfg.printerCard && this.cfg.printerCard.pitch);
        d.locked = s.printerLocked;
        d.needsDate = this.needsDate(true);
        if (s.iso) d.soldOutOn = statusFor(s.iso, ['Printer']) === 'sold_out' ? s.iso : '';
        d.priceNote = s.duration ? B('per_duration', 'pr. [duration]', { duration: s.duration.toLowerCase() }) : '';
      } else if (m.kind === 'rec') {
        var r = this.recFor(m.value) || {};
        d.title = r.title || (r.product && r.product.title) || '';
        d.text = r.text || '';
        d.variant = r.product && this.recVariant(r);
        d.action = d.variant ? 'rec' : '';
        d.value = m.value;
        d.pitch = pitchLines(r.product && r.product.short);
        d.on = !!(d.variant && s.extras[d.variant.id]);
        if (!r.product && r.link) {
          d.link = r.link;
          d.linkLabel = r.linkLabel || B('read_more', 'Læs mere');
        }
      } else if (m.kind === 'extra') {
        d.title = d.product ? d.product.title : '';
        if (d.product) {
          d.variants = this.visibleVariants(d.product);
          var chosen = d.variants.filter(function (v) { return s.extras[v.id]; })[0];
          var wanted = m.variant || (chosen && String(chosen.id)) || '';
          d.variant = d.variants.filter(function (v) { return String(v.id) === wanted; })[0] ||
            d.variants.filter(function (v) { return v.available; })[0] || d.variants[0];
          d.action = 'extra';
          d.value = d.variant ? String(d.variant.id) : '';
          d.on = !!(d.variant && s.extras[d.variant.id]);
        }
      }
      if (!d.title && d.product) d.title = d.product.title;
      return d;
    },

    detailHtml: function (d) {
      if (d.loading) return '<div class="myselfie-ps__modal-state"><span class="myselfie-ps__spinner" aria-hidden="true"></span>' + escapeHtml(B('loading', 'Henter…')) + '</div>';
      var p = d.product;
      var m = this.modal;
      var images = p ? p.images : [];
      var main = images[Math.min(m.image, images.length - 1)] || images[0];
      var html = '<div class="myselfie-ps__detail">';
      if (main) {
        html +=
          '<div class="myselfie-ps__gallery">' +
            '<img class="myselfie-ps__gallery-main" src="' + escapeHtml(img(main, 900)) + '" alt="' + escapeHtml(d.title) + '">' +
            (images.length > 1
              ? '<div class="myselfie-ps__gallery-thumbs">' +
                  images.slice(0, 8).map(function (src, i) {
                    var on = i === m.image;
                    return '<button type="button" class="' + (on ? 'is-on' : '') + '" aria-pressed="' + on + '" aria-label="' + escapeHtml(B('image_n', 'Billede [n]', { n: i + 1 })) + '" data-ps-action="image" data-value="' + i + '" data-ps-focus="i-' + i + '">' +
                      '<img src="' + escapeHtml(img(src, 140)) + '" alt="" loading="lazy" width="64" height="64"></button>';
                  }).join('') +
                '</div>'
              : '') +
          '</div>';
      }
      html += '<div class="myselfie-ps__detail-info">';
      if (d.variant) {
        html += '<p class="myselfie-ps__detail-price">' + this.money(d.variant.price, true) + (d.priceNote ? ' <small>' + escapeHtml(d.priceNote) + '</small>' : '') + '</p>';
      }
      if (d.variants.length > 1) {
        var self = this;
        html +=
          '<label class="myselfie-ps__field-label" for="' + this.uid + '-variant">' + escapeHtml(B('choose_variant', 'Vælg variant')) + '</label>' +
          '<div class="myselfie-ps__select"><select id="' + this.uid + '-variant" data-ps-variant data-ps-focus="m-variant">' +
            d.variants.map(function (v) {
              return '<option value="' + v.id + '"' + (d.variant && v.id === d.variant.id ? ' selected' : '') + (v.available ? '' : ' disabled') + '>' +
                escapeHtml(v.title + ' – ' + self.money(v.price).replace(/<[^>]+>/g, '') + (v.available ? '' : ' ' + B('sold_out_paren', '(udsolgt)'))) + '</option>';
            }).join('') +
          '</select></div>';
      }
      var infoFirst = m.kind !== 'extra';
      var cta = '';
      if (d.action) {
        var soldOut = (d.variant && d.variant.available === false) || !!d.soldOutOn;
        var noDate = d.needsDate && !d.on;
        var label = escapeHtml(d.locked ? B('required_on_date', '✓ Påkrævet på jeres dato')
          : noDate ? B('pick_date_first', 'Vælg dato først')
          : soldOut && !d.on ? (d.soldOutOn ? B('sold_out_on', 'Udsolgt d. [date]', { date: longDate(d.soldOutOn) }) : B('sold_out', 'Udsolgt'))
          : d.on ? (soldOut ? B('remove_sold_out', 'Fjern – udsolgt på jeres dato') : B('added_remove', '✓ Tilføjet – fjern igen'))
          : B('add_to_booking', '+ Tilføj til bookingen'));
        cta =
          '<button type="button" class="' + (infoFirst ? 'myselfie-ps__detail-toggle' : 'myselfie-bc__cta myselfie-ps__detail-cta') + (d.on ? ' is-on' : '') + '" data-ps-action="' + d.action + '"' +
            ' data-value="' + escapeHtml(d.value) + '"' + (p ? ' data-handle="' + escapeHtml(p.handle) + '"' : '') + ' data-ps-focus="m-cta"' +
            ((soldOut && !d.on) || d.locked || noDate ? ' disabled' : '') + '>' + label + '</button>';
      }
      // catalogue: a shop (button first); add-ons: information first, a small switch at the end
      if (!infoFirst) html += cta;
      if (infoFirst && d.pitch.length) {
        html += '<ul class="myselfie-ps__pitch">' + d.pitch.map(function (x) { return '<li>' + escapeHtml(x) + '</li>'; }).join('') + '</ul>';
      }
      if (d.text) html += '<p class="myselfie-ps__detail-lead">' + escapeHtml(d.text) + '</p>';
      if (p && p.body) {
        html += infoFirst && d.pitch.length
          ? '<details class="myselfie-ps__fulldesc"><summary>' + escapeHtml(B('read_full_description', 'Læs hele beskrivelsen')) + '</summary><div class="myselfie-ps__desc">' + p.body + '</div></details>'
          : '<div class="myselfie-ps__desc">' + p.body + '</div>';
      }
      if (infoFirst && cta && this.cfg.infoToggle !== false) html += '<div class="myselfie-ps__detail-foot">' + cta + '</div>';
      if (d.error) html += '<p class="myselfie-ps__detail-lead">' + escapeHtml(B('description_failed', 'Beskrivelsen kunne ikke hentes lige nu.')) + '</p>';
      if (d.link) {
        // a link to this page (e.g. the contact form further down) closes the modal and scrolls there
        var samePage = false;
        try {
          var url = new URL(d.link, window.location.href);
          samePage = url.origin === window.location.origin && url.pathname === window.location.pathname && !!url.hash;
        } catch (err) {
          /* keep the new tab */
        }
        html += samePage
          ? '<a class="myselfie-ps__detail-link" href="' + escapeHtml(d.link) + '" data-ps-action="modal-close">' + escapeHtml(d.linkLabel) + ' <span aria-hidden="true">→</span></a>'
          : '<a class="myselfie-ps__detail-link" href="' + escapeHtml(d.link) + '" target="_blank" rel="noopener">' + escapeHtml(d.linkLabel) + ' <span aria-hidden="true">↗</span><span class="myselfie-bc__visually-hidden"> ' + escapeHtml(B('opens_new_tab', '(åbner i en ny fane)')) + '</span></a>';
      }
      html += '</div></div>';
      return html;
    },

    modalFootHtml: function () {
      // "Om Myselfie" is information only: just the way back
      if (this.modal.view === 'about') {
        return '<span></span><button type="button" class="myselfie-ps__done" data-ps-action="modal-close">' + escapeHtml(B('back_to_booking', 'Tilbage til bookingen')) + '</button>';
      }
      var self = this;
      var ids = Object.keys(this.state.extras);
      var count = 0;
      var sum = 0;
      ids.forEach(function (id) {
        var x = self.state.extras[id];
        count += x.qty;
        sum += x.price * x.qty;
      });
      return (
        '<span class="myselfie-ps__modal-sum">' +
          (count ? escapeHtml(count === 1 ? B('extras_one', '[n] ekstra tilvalg', { n: count }) : B('extras_many', '[n] ekstra tilvalg', { n: count })) + ' · <strong>+' + this.money(sum) + '</strong>' + (this.isB2b() ? ' <small>' + escapeHtml(B('ex_vat', 'ekskl. moms')) + '</small>' : '')
            : escapeHtml(B('no_extras_yet', 'Ingen ekstra tilvalg endnu'))) +
        '</span>' +
        '<button type="button" class="myselfie-ps__done" data-ps-action="modal-close">' + escapeHtml(B('back_to_booking', 'Tilbage til bookingen')) + '</button>'
      );
    },

    /* ---------- booking ---------- */

    /* ---------- cart page (data-page="cart", _qa/cartpage/spec.md) ---------- */

    /** The cart holds a booth of this booking (pinned lines of another date do not count). */
    cartHasBooth: function (cart) {
      var self = this;
      var cs = this.cartSync;
      return !!cart && this.cartLines(cart).some(function (it) { return self.isCameraLine(it) && !(cs && cs.replaceKeys[it.key]); });
    },

    /** Cart page without a date and without a booth, but with something in the cart: checkout without a booking. */
    cartOnlyActive: function () {
      var cs = this.cartSync;
      return this.pageMode === 'cart' && !!cs && cs.enabled && cs.ready && !this.state.iso && !!cs.cart &&
        cs.cart.item_count > 0 && !this.cartHasBooth(cs.cart);
    },

    /** What blocks a checkout without a booking: the consent box and the business payment fields. */
    /** Lease page: 'Lejebetingelser for langtidsleje' replace the general terms of trade. */
    leaseTerms: function () {
      return !!(this.el.terms && this.el.terms.getAttribute('data-terms-kind') === 'lease');
    },

    termsIssue: function () {
      return KIT.termsIssue(this.leaseTerms());
    },

    /** Which terms the customer accepted (order attribute, kept as proof). */
    termsDocument: function () {
      var t = this.el.terms;
      return this.leaseTerms() ? KIT.termsDocument(true, t.getAttribute('data-terms-doc'), t.getAttribute('data-terms-url')) : KIT.termsDocument(false);
    },

    /** Payment fields in effect (business customers only). */
    billingIssues: function () {
      var pay = this.el.billing ? this.payMethod() : 'standard';
      return KIT.billingIssues(pay, this.el.invoice ? this.el.invoice.value : '', this.el.ean ? this.el.ean.value : '');
    },

    cartOnlyIssues: function () {
      var out = [];
      if (this.el.terms && !this.el.terms.checked) {
        out.push(this.termsIssue());
      }
      return out.concat(this.billingIssues());
    },

    /** Kind of every cart line: booth | live | bundle (managed), kept, auto (free props, rewards), replace (other date). */
    cartKinds: function (cart) {
      var self = this;
      var cs = this.cartSync;
      var c = this.cfg && this.cfg.checkout;
      if (!cart || !cart.items || !this.cfg) return [];
      var blocked = cs && cs.status === 'blocked';
      var plan = this.planCartImport(cart, '', cs && cs.ready && !blocked ? cs.replaceKeys : null);
      var kindOf = {};
      plan.kept.forEach(function (cl) { kindOf[cl.item.key] = 'kept'; });
      (plan.replace || []).forEach(function (cl) { kindOf[cl.item.key] = 'replace'; });
      plan.managed.forEach(function (cl) { kindOf[cl.item.key] = cl.kind === 'booth' || cl.kind === 'bundle' ? cl.kind : 'live'; });
      return cart.items.map(function (it) {
        var kind = isAutomaticLine(it, c) ? 'auto' : blocked ? 'kept' : kindOf[it.key] || 'kept';
        return { key: it.key, kind: kind, camera: self.isCameraLine(it) };
      });
    },

    /**
     * Quantity of one cart line from the cart page (kept lines, or any line while the import is blocked).
     * Serialised with the live passes; one change.js; a stale key re-reads the cart and rejects.
     */
    cartChange: function (key, quantity) {
      var self = this;
      var cs = this.cartSync;
      if (!cs || !cs.enabled) return Promise.reject({ message: B('cart_not_editable', 'Kurven kan ikke ændres her.') });
      var wasBlocked = cs.status === 'blocked';
      var apply = function (cart) {
        return self.prepareCart(cart).then(function (ready) {
          self.adoptOwnCart(ready);
          var plan = self.planCartImport(ready, '', wasBlocked ? null : cs.replaceKeys);
          self.applyCartImport(plan, wasBlocked ? 'manual' : 'resync');
          announceCart(ready, 'cart-page');
          return ready;
        });
      };
      this.flushLive();
      return this.enqueue(function () {
        return cartPost('cart/change.js', { id: key, quantity: Math.max(0, quantity | 0) }).then(apply, function (err) {
          var msg = err && err.status === 400 ? B('cart_changed_retry', 'Kurven er ændret – prøv igen.') : (err && err.message) || B('cart_update_failed', 'Kurven kunne ikke opdateres. Prøv igen.');
          return cartGet().then(apply, function () { return null; }).then(function () { throw { message: msg }; });
        });
      });
    },

    /**
     * Checkout without a booking (cart page): consent, business payment, free props, attributes and note,
     * then /checkout. opts: {attributes, noteParts}.
     */
    runCartOnlyCheckout: function (opts) {
      var self = this;
      var s = this.state;
      opts = opts || {};
      if (s.busy) return Promise.resolve();
      s.showErrors = true;
      s.fresh = {};
      this.serverError = '';
      if (this.cartOnlyIssues().length) {
        this.renderValidation();
        this.announceErrors();
        return Promise.resolve();
      }
      var billing = this.el.billing ? this.billingData() : null;
      s.busy = true;
      this.update();
      return this.whenLiveIdle()
        .then(function () { return self.addAutomaticLines(); })
        .then(function () { return KIT.ensureSeasons ? KIT.ensureSeasons() : null; })
        .then(function () {
          var attributes = KIT.checkoutAttributes({
            booking: null,
            termsDocument: self.termsDocument(),
            marketing: !!(self.el.marketing && self.el.marketing.checked),
            billing: billing,
            extra: opts.attributes,
          });
          return cartPost('cart/update.js', { attributes: attributes, note: KIT.noteFrom([billing && billing.note].concat(opts.noteParts || [])) });
        })
        .then(function (cart) {
          self.adoptOwnCart(cart);
          self.booked = true;
          clearDraft();
          // no order-number field here: a cart that needs one goes to the cart page (see submitBooking)
          window.location.href = KIT.checkoutUrl(billing && billing.code, rootUrl(KIT.checkoutAllowed && !KIT.checkoutAllowed(cart) ? 'cart' : 'checkout'));
        })
        .catch(function (err) {
          s.busy = false;
          self.update();
          self.showError(err && err.message && !(err instanceof Error) ? err.message : B('generic_error', 'Noget gik galt. Prøv igen om et øjeblik.'));
        });
    },

    /**
     * Arriving with /#book-myselfie (or #booking): land on the heading once the page has
     * settled; the legacy theme scrolls back to the top after load.
     */
    scrollToAnchor: function () {
      var anchor = this.querySelector('[data-ps-anchor]');
      if (!anchor) return;
      var ids = [anchor.id, this.id, 'book-din-myselfie']; // book-din-myselfie: legacy anchor before 3.0.8
      (this.getAttribute('data-anchor-aliases') || '').split(',').forEach(function (a) {
        a = a.trim().replace(/^#/, '');
        if (a) ids.push(a);
      });
      var matches = function (hash) {
        var id = decodeURIComponent(String(hash || '').slice(1));
        return !!id && ids.indexOf(id) > -1;
      };
      var go = function (smooth) { anchor.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto' }); };
      var show = function () {
        if (window.location.hash.slice(1) !== anchor.id && window.history.replaceState) {
          window.history.replaceState(null, '', '#' + anchor.id);
        }
      };
      if (matches(window.location.hash)) {
        show();
        go();
        // the legacy theme scrolls to the top ~0.7 s after load: keep landing here until the
        // page has settled, unless the customer has started scrolling themselves
        var touched = false;
        var stop = function () { touched = true; };
        ['wheel', 'touchmove'].forEach(function (t) {
          window.addEventListener(t, stop, { once: true, passive: true });
        });
        var settle = function () {
          [50, 400, 900, 1600].forEach(function (ms) {
            setTimeout(function () { if (!touched) go(); }, ms);
          });
        };
        if (document.readyState === 'complete') settle();
        else window.addEventListener('load', settle, { once: true });
        // the cookie banner locks scrolling and drops the page to the top when it closes
        if (window.MutationObserver) {
          var off = function () { observer.disconnect(); };
          var observer = new MutationObserver(function () {
            if (touched) return off();
            var locked = window.getComputedStyle(document.documentElement).overflow === 'hidden' ||
              window.getComputedStyle(document.body).overflow === 'hidden';
            var top = anchor.getBoundingClientRect().top;
            if (!locked && (top < 0 || top > 200)) go();
          });
          [document.documentElement, document.body].forEach(function (n) {
            observer.observe(n, { attributes: true, attributeFilter: ['class', 'style'] });
          });
          setTimeout(off, 20000);
        }
      }
      // same-page links (the old "#packages" buttons included) scroll here without a jump elsewhere first
      document.addEventListener('click', function (e) {
        var a = e.target.closest && e.target.closest('a[href*="#"]');
        if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
        var url;
        try {
          url = new URL(a.getAttribute('href'), window.location.href);
        } catch (err) {
          return;
        }
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !matches(url.hash)) return;
        e.preventDefault();
        if (window.history.pushState) window.history.pushState(null, '', '#' + anchor.id);
        go(true);
      });
      window.addEventListener('hashchange', function () {
        if (matches(window.location.hash)) go(true);
      });
    },

    /* ---------- checkout without the cart page ---------- */

    /** ViaBill instalments follow the total; items already in the cart are shown, not hidden. */
    renderCheckoutExtras: function (total) {
      var vb = this.el.viabill;
      if (vb && this.pageMode === 'cart') {
        // cart page: instalments for what will be paid - the whole cart once it is known (gift cards, other lines)
        var cs0 = this.cartSync;
        if (cs0 && cs0.ready && cs0.cart && cs0.cart.item_count > 0 && !this.state.iso) total = cs0.cart.total_price;
        else if (cs0 && cs0.ready && cs0.cart) total = Math.max(total, cs0.cart.total_price);
        vb.hidden = !(total > 0); // no "0 kr" instalments on an empty cart page
      }
      if (vb) {
        var text = withCurrency((total / 100).toLocaleString(numLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        if (vb.getAttribute('data-price') !== text) {
          // a fresh tag per price: ViaBill's observer picks up new .viabill-pricetag elements
          vb.setAttribute('data-price', text);
          vb.innerHTML = '<div class="viabill-pricetag" data-view="basket" data-price="' + escapeHtml(text) + '"' +
            (vb.getAttribute('data-language') ? ' data-language="' + escapeHtml(vb.getAttribute('data-language')) + '"' : '') +
            ' data-currency="' + escapeHtml(vb.getAttribute('data-currency') || '') + '"></div>';
        }
      }
      var note = this.el.cartnote;
      if (!note) return;
      var items = this.existingItems || [];
      if (!items.length || this.state.busy) {
        note.hidden = true;
        return;
      }
      var names = items.map(function (it) { return it.quantity > 1 ? it.quantity + ' × ' + it.product_title : it.product_title; });
      var cs = this.cartSync;
      if (cs && cs.enabled && cs.status !== 'blocked') {
        // live cart: only the lines the selection does not show are listed; "Tøm kurven" is in the summary
        var redate = (cs.plan && cs.plan.redate || []).map(function (cl) { return cl.item.key; });
        var movable = items.filter(function (it) { return redate.indexOf(it.key) > -1; });
        var label = function (list) {
          return list.map(function (it) { return it.quantity > 1 ? it.quantity + ' × ' + it.product_title : it.product_title; }).join(', ');
        };
        setHtml(note,
          '<p><strong>' + escapeHtml(B('also_in_cart', 'Også i kurven:')) + '</strong> ' + escapeHtml(label(items)) + '. ' +
            escapeHtml(this.cfg.checkout && this.cfg.checkout.skipCart ? B('they_join_order', 'De kommer med i bestillingen.') : B('they_stay_in_cart', 'De bliver i kurven.')) + '</p>' +
          (movable.length ? '<p>' + escapeHtml(B('follow_your_date', '[items] følger jeres dato.', { items: label(movable) })) + '</p>' : ''));
        note.hidden = false;
        return;
      }
      setHtml(note,
        '<p><strong>' + escapeHtml(items.length === 1 ? B('already_in_cart_one', 'Du har allerede 1 vare i kurven:') : B('already_in_cart_many', 'Du har allerede [n] varer i kurven:', { n: items.length })) + '</strong> ' + escapeHtml(names.join(', ')) + '. ' +
        escapeHtml(this.cfg.checkout && this.cfg.checkout.skipCart ? B('they_join_order', 'De kommer med i bestillingen.') : B('they_stay_in_cart', 'De bliver i kurven.')) + '</p>' +
        '<button type="button" class="myselfie-ps__more" data-ps-action="cart-clear">' + escapeHtml(cs && cs.enabled ? B('clear_cart_restart', 'Tøm kurven og start forfra') : B('remove_them_from_cart', 'Fjern dem fra kurven')) + '</button>');
      note.hidden = false;
    },

    loadExistingCart: function () {
      var self = this;
      var c = this.cfg.checkout || {};
      fetch(rootUrl('cart.js'), { headers: { Accept: 'application/json' } })
        .then(cartJson)
        .then(function (cart) {
          // automatic lines (free props, app rewards) are not the customer's choice
          self.existingItems = (cart.items || []).filter(function (it) {
            return it.variant_id !== c.freeProps && !(it.properties && it.properties.__upcartFreeGift);
          });
          announceCart(cart);
          self.update();
        })
        .catch(function () {});
    },

    /** Setting "Hent varer fra kurven" off: "Fjern dem fra kurven" empties the cart. */
    clearExistingCart: function () {
      var self = this;
      fetch(rootUrl('cart/clear.js'), { method: 'POST', headers: { Accept: 'application/json' } })
        .then(cartJson)
        .then(function (cart) {
          announceCart(cart);
          self.existingItems = [];
          self.update();
        })
        .catch(function () {});
    },

    /**
     * What the cart page does before checkout (sections/main-cart.liquid): one Myselfie in the
     * cart gets the free props, and the free prop holder above the configured cart value.
     * The Upcart app adds the same free props as a reward (~1-2 s after a cart change), so when
     * Upcart is on the page we wait for it and only add them ourselves if it did not; a
     * duplicate line of ours is removed again.
     */
    addAutomaticLines: function () {
      return KIT.addAutomaticLines(this.cfg.checkout);
    },

    /* ---------- validation ---------- */

    /** Server / network problems while booking (shown in the error summary). */
    showError: function (message) {
      this.serverError = message;
      this.renderValidation();
      this.announceErrors();
    },

    /** Called on every change: drops a stale server message and re-checks the form. */
    clearError: function () {
      this.serverError = '';
      this.renderValidation();
    },

    eanProblem: function () {
      return KIT.eanProblem(this.el.ean.value);
    },

    /** Everything that blocks "Book nu", in step order. */
    issues: function () {
      var s = this.state;
      var out = [];
      if (this.cartOnlyActive()) return this.cartOnlyIssues();
      if (!s.iso) {
        out.push({ key: 'date', step: 'date', label: B('issue_date_label', 'Vælg eventdato'), message: B('issue_date', 'Vælg datoen for jeres event i kalenderen.') });
      } else {
        // urgent: shown at once, also when the date was changed after products were chosen
        var a = this.dayAvailability(s.iso);
        var printerChosen = s.printer && !s.printerForced;
        var boothOk = printerChosen ? a.printer !== 'sold_out' : a.booth !== 'sold_out';
        if (!boothOk && !(printerChosen && a.booth !== 'sold_out')) {
          out.push({ key: 'date', step: 'date', urgent: true, label: B('issue_free_date_label', 'Vælg en ledig dato'), message: B('issue_booth_sold_out', 'Myselfie er udsolgt d. [date]. Vælg en anden dato.', { date: alertDate(s.iso) }) });
        } else if (s.printer && a.printer === 'sold_out') {
          out.push({ key: 'printer', step: 'addons', urgent: true, label: B('remove_printer', 'Fjern printeren'), message: B('issue_printer_sold_out', 'Printeren er udsolgt d. [date]. Fjern printeren eller vælg en anden dato.', { date: alertDate(s.iso) }) });
        }
        (this.cfg.addons || []).forEach(function (x) {
          if (s.addons[x.key] && a.addons[x.key] === 'sold_out') {
            out.push({ key: 'addon-' + x.key, step: 'addons', urgent: true, label: B('remove_item', 'Fjern [title]', { title: x.label }), message: B('issue_addon_sold_out', '[title] er udsolgt d. [date]. Fjern [title] eller vælg en anden dato.', { title: x.label, date: alertDate(s.iso) }) });
          }
        });
      }
      (this.cfg.addons || []).forEach(function (x) {
        if (!x.lead || !s.addons[x.key]) return;
        var t = (s.leadText[x.key] || '').trim();
        if (t.length < 10) {
          out.push({ key: 'lead-' + x.key, step: 'addons', label: B('issue_consent_label', 'Skriv samtykketeksten'), message: t
            ? B('issue_consent_short', 'Skriv lidt mere – samtykketeksten skal fortælle gæsterne, hvem der må kontakte dem.')
            : B('issue_consent_missing', 'Skriv den samtykketekst, gæsterne accepterer, når de giver deres e-mail til [title].', { title: x.label }) });
        }
      });
      var unavailable = this.lines(s.iso).filter(function (l) { return !l.variant.available; });
      if (unavailable.length) {
        out.push({ key: 'lines', step: 'addons', label: B('issue_lines_label', 'Fjern udsolgte tilvalg'), message: B('issue_lines', '[items] kan ikke bookes lige nu. Fjern det for at fortsætte.', { items: unavailable.map(function (l) { return l.label; }).join(', ') }) });
      }
      out = out.concat(KIT.reasonIssues(this.el.reason.value, this.el.other.value));
      if (this.el.terms && !this.el.terms.checked) {
        out.push(this.termsIssue());
      }
      return out.concat(this.billingIssues());
    },

    fieldFor: function (key) {
      if (key === 'printer') return this.querySelector('.myselfie-ps__pill[data-ps-action="printer"]');
      if (key === 'terms') return this.el.terms;
      if (key.indexOf('addon-') === 0) return this.querySelector('.myselfie-ps__pill[data-ps-action="addon"][data-value="' + key.slice(6) + '"]');
      if (key.indexOf('lead-') === 0) return this.querySelector('[data-ps-lead="' + key.slice(5) + '"]');
      return { date: this.calendar, reason: this.el.reason, 'reason-other': this.el.other, invoice: this.el.invoice, ean: this.el.ean, lines: this.el.catalogue }[key] || null;
    },

    /** Scroll a problem into view and put the cursor where it can be fixed. */
    focusIssue: function (key) {
      var el = this.fieldFor(key);
      if (key === 'date' && this.calendar) {
        el = this.calendar.querySelector('.myselfie-bc__day.is-selected:not([disabled])') ||
          this.calendar.querySelector('.myselfie-bc__day:not([disabled])') || this.calendar;
      }
      if (key === 'lines') el = this.querySelector('[data-ps-step="addons"] .myselfie-ps__pill[aria-checked="true"]') || el;
      if (!el) return;
      var block = el.closest('[data-ps-step]') || el;
      block.scrollIntoView({ behavior: scrollBehavior(), block: key === 'date' ? 'start' : 'center' });
      if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    },

    /** Moves attention to the error summary (after a failed "Book nu"). */
    announceErrors: function () {
      var box = this.el.error;
      if (!box || box.hidden) return;
      box.classList.remove('is-shake');
      void box.offsetWidth; // restart the animation
      box.classList.add('is-shake');
      box.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
      box.focus({ preventScroll: true });
    },

    /**
     * Field messages, aria-invalid, step badges (✓ done / ! problem) and the summary above
     * "Book nu". Problems are only shown after the first "Book nu" (then they update live and
     * disappear as they are fixed); a wrong EAN is flagged as soon as the field is left.
     */
    renderValidation: function () {
      if (!this.el || !this.el.error || !this.state) return;
      var s = this.state;
      var self = this;
      var issues = this.issues();
      var shown = s.showErrors ? issues.filter(function (i) { return !s.fresh[i.key]; }) : issues.filter(function (i) { return i.urgent; });
      if (!s.showErrors && s.eanTouched && this.el.ean && this.el.ean.value.trim()) {
        issues.forEach(function (i) { if (i.key === 'ean') shown.push(i); });
      }
      var byKey = {};
      var errorSteps = {};
      var openSteps = {};
      shown.forEach(function (i) { byKey[i.key] = i; errorSteps[i.step] = true; });
      issues.forEach(function (i) { openSteps[i.step] = true; });

      this.querySelectorAll('[data-ps-error-for]').forEach(function (p) {
        var i = byKey[p.getAttribute('data-ps-error-for')];
        p.hidden = !i;
        p.innerHTML = i ? ICON_ALERT + '<span>' + escapeHtml(i.message) + '</span>' : '';
      });
      ['date', 'reason', 'reason-other', 'invoice', 'ean', 'terms'].concat((this.cfg.addons || []).filter(function (x) { return x.lead; }).map(function (x) { return 'lead-' + x.key; })).forEach(function (k) {
        var el = self.fieldFor(k);
        if (!el) return;
        if (byKey[k]) el.setAttribute('aria-invalid', 'true');
        else el.removeAttribute('aria-invalid');
      });

      this.querySelectorAll('[data-ps-step]').forEach(function (block) {
        var step = block.getAttribute('data-ps-step');
        var badge = block.querySelector('.myselfie-bc__step-label [data-n]');
        var err = !!errorSteps[step];
        var done = false;
        if (step === 'date') done = !!s.iso && !openSteps.date;
        else if (step === 'reason') done = !!self.el.reason.value && !openSteps.reason;
        else if (step === 'billing') done = !openSteps.billing;
        else if (step === 'customer' || step === 'duration') done = true;
        else if (step === 'addons') done = !openSteps.addons && self.lines(s.iso).length > 1;
        block.classList.toggle('has-error', err);
        if (!badge) return;
        badge.setAttribute('aria-hidden', 'true');
        badge.classList.toggle('is-error', err);
        badge.classList.remove('is-done'); // every step marker looks the same; only a problem shows (red)
        badge.textContent = badge.getAttribute('data-n'); // always the number; done and error show by colour
      });

      var box = this.el.error;
      var list = shown.filter(function (i) { return s.showErrors ? true : i.urgent; });
      if (this.serverError) {
        box.innerHTML =
          '<div class="myselfie-ps__errors-head">' + ICON_ALERT + '<strong>' + escapeHtml(B('booking_failed', 'Bookingen kunne ikke gennemføres')) + '</strong></div>' +
          '<p>' + escapeHtml(this.serverError) + '</p>' +
          '<p class="myselfie-ps__errors-help">' + escapeHtml(B('booking_failed_help', 'Prøv igen om et øjeblik. Bliver det ved, så kontakt os – vi hjælper gerne.')) + '</p>';
        box.hidden = false;
      } else if (list.length) {
        box.innerHTML =
          '<div class="myselfie-ps__errors-head">' + ICON_ALERT +
            '<strong>' + escapeHtml(list.length === 1 ? B('missing_one', 'Der mangler én ting, før I kan booke') : B('missing_many', 'Der mangler [n] ting, før I kan booke', { n: list.length })) + '</strong></div>' +
          '<ul>' + list.map(function (i) {
            return '<li><button type="button" data-ps-goto="' + i.key + '">' + escapeHtml(i.label) + '<span aria-hidden="true"> →</span></button></li>';
          }).join('') + '</ul>';
        box.hidden = false;
      } else {
        box.hidden = true;
        box.innerHTML = '';
      }
      box.setAttribute('role', box.hidden ? 'region' : 'alert');
    },

    book: function () {
      var s = this.state;
      if (s.busy) return;
      if (this.cartOnlyActive()) {
        // cart page, no booking in the cart: gift cards, accessories for an existing order, ... (mys-cart.js)
        var ev = new CustomEvent('myselfie:checkout-cart-only', { bubbles: true, cancelable: true,
          detail: { cart: this.cartSync.cart, terms: !!(this.el.terms && this.el.terms.checked), marketing: !!(this.el.marketing && this.el.marketing.checked) } });
        if (!this.dispatchEvent(ev)) return;
      }
      s.showErrors = true;
      s.fresh = {};
      this.serverError = '';
      if (this.issues().length) {
        this.renderValidation();
        this.announceErrors();
        return;
      }
      var self = this;
      var notices = this.cartSync.noticeSeq;
      s.busy = true;
      this.update();
      // availability may have changed since the page was opened
      this.refreshAvailability().then(function () {
        s.busy = false;
        self.update();
        if (self.issues().length) {
          self.renderValidation();
          self.announceErrors();
          return;
        }
        // live cart: something was sold out meanwhile and taken out: let the customer see it first
        if (self.cartSync.noticeSeq !== notices) {
          var box = self.el.livecart;
          if (box && box.scrollIntoView) box.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
          return;
        }
        self.submitBooking();
      });
    },

    /** The cart items "Book nu" wants in the cart for a date (unchanged output, shared by both paths). */
    desiredItems: function (display, reason, other) {
      return this.lines(this.state.iso).map(function (l) {
        var item = { id: l.variant.id, quantity: l.qty };
        var props = {};
        if (l.dated) props.Date = display;
        if (l.props) Object.keys(l.props).forEach(function (k) { props[k] = l.props[k]; });
        if (l.reason) {
          props._reason = reason;
          props._reason_andet = reason === 'andet' ? other : '';
        }
        if (Object.keys(props).length) item.properties = props;
        return item;
      });
    },

    submitBooking: function () {
      var s = this.state;
      if (s.busy) return;
      var reason = this.el.reason.value;
      var other = (this.el.other.value || '').trim();
      var billing = this.el.billing ? this.billingData() : null;
      var display = isoToDisplay(s.iso);
      var items = this.desiredItems(display, reason, other);
      var syncOn = !!(this.cartSync && this.cartSync.enabled);

      var self = this;
      s.busy = true;
      this.clearError();
      this.update();
      store(display);
      this._clearOrderNo = '';

      var writes;
      if (syncOn) {
        // live cart: the add-ons are written already; wait for a pass that is still running
        writes = this.whenLiveIdle().then(function () {
          // the customer sees what was set back first; the next press goes on with the cart as it is
          var problem = self.cartSync.error;
          self.cartSync.error = '';
          if (problem) throw { message: problem };
          return self.commitBooking(items, display);
        });
      } else {
        writes = fetch(rootUrl('cart.js'), { headers: { Accept: 'application/json' } })
          .then(cartJson)
          .then(function (cart) {
            // The cart page empties a cart with two booking dates; ask before replacing.
            var clash = (cart.items || []).filter(function (it) {
              return it.properties && it.properties.Date && it.properties.Date !== display;
            })[0];
            if (!clash) return;
            var ok = window.confirm(
              B('replace_booking_1', 'Din kurv har allerede en booking d. [date].', { date: clash.properties.Date }) + '\n' +
              B('replace_booking_2', 'Vil du erstatte den med din nye booking d. [date]?', { date: display })
            );
            if (!ok) throw { cancelled: true };
            return fetch(rootUrl('cart/clear.js'), { method: 'POST', headers: { Accept: 'application/json' } });
          })
          .then(function () {
            return fetch(rootUrl(QUIET_ADD), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: items }),
            });
          })
          .then(cartJson);
      }
      writes
        .then(function () {
          return self.addAutomaticLines();
        })
        .then(function () {
          return KIT.ensureSeasons ? KIT.ensureSeasons() : null;
        })
        .then(function () {
          // the same attributes the cart form posts (main-cart.liquid), plus consent (assets/myselfie-checkout.js)
          var attributes = KIT.checkoutAttributes({
            booking: { display: display, reason: reason, other: other },
            termsDocument: self.termsDocument(),
            marketing: !!(self.el.marketing && self.el.marketing.checked),
            billing: billing,
            // a new booking must not be filed under an old order ("Evt. tilhørende ordrenummer")
            extra: self._clearOrderNo ? { orderNo: '' } : null,
          });
          var update = { attributes: attributes };
          // hook for the cart page (order note, orderNo): listeners add attributes and note parts
          var hook = { attributes: attributes, noteParts: billing && billing.note !== null ? [billing.note] : [], setNote: false, mode: 'booking' };
          try {
            self.dispatchEvent(new CustomEvent('myselfie:before-checkout', { bubbles: true, detail: hook }));
          } catch (e) {
            /* old browser */
          }
          var parts = hook.noteParts.filter(Boolean);
          if (parts.length || hook.setNote) update.note = KIT.noteFrom(parts);
          return cartPost('cart/update.js', update);
        })
        .then(function (cart) {
          var c = self.cfg.checkout || {};
          // the cart page never sends the booking back to itself
          var target = rootUrl(c.skipCart || self.pageMode === 'cart' ? 'checkout' : 'cart');
          // order-number rule (theme settings "Ordrenummer-regel"): this module has no order-number field, so a cart that
          // still needs one (e.g. a gallery extension in the cart) goes to the cart page, where the field and the message are
          if (KIT.checkoutAllowed && !KIT.checkoutAllowed(cart)) target = rootUrl('cart');
          announceCart(cart, 'booking');
          self.booked = true;
          clearDraft();
          if (syncOn) {
            self.adoptOwnCart(cart);
            self.cartSync.touched = false;
          }
          // /discount/CODE stores the code, then redirects on
          window.location.href = KIT.checkoutUrl(billing && billing.code, target);
        })
        .catch(function (err) {
          s.busy = false;
          self.update();
          if (err && err.stale) {
            // the cart changed under us: the selection was refreshed, the customer checks and presses again
            var box = self.el.livecart || self.el.fromcart;
            if (box && box.scrollIntoView) box.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
            return;
          }
          if (err && err.cancelled) return;
          // only our own messages reach the customer, never a technical exception
          self.showError(err && err.message && !(err instanceof Error) ? err.message : B('generic_error', 'Noget gik galt. Prøv igen om et øjeblik.'));
          if (syncOn) self.syncFromCart('error');
        });
    },

    /* ---------- cart sync: the cart -> the selection ---------- */

    /** Wires the re-sync triggers and runs the first import. */
    startCartSync: function () {
      var self = this;
      var cs = this.cartSync;
      cs.status = 'loading';
      this._onVisible = function () {
        if (document.visibilityState === 'visible') self.syncFromCart('focus');
      };
      this._onPageShow = function (e) {
        if (!e || !e.persisted) return;
        // back from checkout (bfcache): the page is exactly as we left it, mid-booking
        self.booked = false;
        self.state.busy = false;
        self.update();
        self.syncFromCart('pageshow');
      };
      document.addEventListener('visibilitychange', this._onVisible);
      window.addEventListener('pageshow', this._onPageShow);
      var timer = null;
      var subscribe = function () {
        if (self._upcartBound || typeof window.upcartSubscribeCartUpdated !== 'function') return;
        self._upcartBound = true;
        try {
          window.upcartSubscribeCartUpdated(function () {
            // Upcart also announces the refresh it does after our own writes: those are known already
            if (Date.now() - cs.lastWrite < 3000 || cs.jobs || cs.timer) return;
            clearTimeout(timer);
            timer = setTimeout(function () { self.syncFromCart('upcart'); }, 800);
          });
        } catch (e) {
          /* app API changed: the focus/pageshow triggers still work */
        }
      };
      subscribe();
      window.addEventListener('load', subscribe);
      this.restorePendingTxn().then(function () {
        return self.syncFromCart('load');
      });
    },

    /** Customer changed products before the first cart read: kept when the cart holds none. */
    markTouched: function () {
      var cs = this.cartSync;
      if (!cs || !cs.enabled) return;
      cs.message = '';
      cs.error = '';
      if (!cs.ready) cs.touched = true;
    },

    /** Cart lines that are the customer's choice (no free props, prop holder or app rewards). */
    cartLines: function (cart) {
      var c = this.cfg.checkout || {};
      return (cart.items || []).filter(function (it) { return !isAutomaticLine(it, c); });
    },

    /** Lines the selection never takes apart or manages: gift cards, fixed-date SKUs, the trade fair offer, SKU-less products. */
    isKeepLine: function (item) {
      var sku = String(item.sku || '').trim();
      if (item.gift_card || !sku) return true;
      if (this.cartSync.keep.indexOf(item.handle) > -1) return true;
      if (item.variant_id === ADD_TO_ORDER_VARIANT) return true;
      if (/^QP-/i.test(sku)) return true;
      return sku.split('+').some(function (p) { return /^D-/i.test(p.trim()); });
    },

    isCameraLine: function (item) {
      var type = this.cfg.checkout && this.cfg.checkout.cameraType;
      return !!type && item.product_type === type;
    },

    /**
     * Rental period of a booth line or bundle part: by variant id (any booth product, any season), else by SKU (the
     * booth product of the date first), else by the duration code at the front of the SKU (M-W-HIGH -> M-W).
     */
    boothDurationForSku: function (sku, iso, variantId) {
      if (variantId) {
        var hit = this.boothById(variantId);
        if (hit) return durOf(hit.variant, hit.product);
      }
      var want = String(sku || '').toLowerCase();
      if (!want) return '';
      var cfg = this.cfg;
      var products = [this.boothProduct(iso)].concat(Object.keys(cfg.booth).map(function (h) { return cfg.booth[h]; }));
      var code = function (x) { var m = /^m-([a-z0-9]+)/i.exec(String(x || '')); return m ? m[1].toLowerCase() : ''; };
      for (var pass = 0; pass < 2; pass++) {
        for (var i = 0; i < products.length; i++) {
          var p = products[i];
          if (!p) continue;
          for (var j = 0; j < p.variants.length; j++) {
            var vs = String(p.variants[j].sku || '').toLowerCase();
            if (pass === 0 ? vs === want : (!!code(want) && code(vs) === code(want))) return durOf(p.variants[j], p);
          }
        }
      }
      return '';
    },

    /** Logo state value for a bundle's logo code: '' (Myselfie Logo), 'NL' / 'EL', or null (the product lacks it). */
    logoIdForCode: function (code) {
      code = String(code || '').toUpperCase();
      if (code === 'ML') return '';
      var logo = this.cfg.logo;
      if (!logo || !KIT || !KIT.logoCode) return null;
      for (var i = 0; i < logo.variants.length; i++) if (KIT.logoCode(logo.variants[i], logo) === code) return code;
      return null;
    },

    /** The configured add-on a cart line belongs to (variant id, else a non-empty SKU), or null. */
    addonForLine: function (item) {
      var list = this.cfg.addons || [];
      var sku = String(item.sku || '').toLowerCase();
      for (var i = 0; i < list.length; i++) {
        var vs = (list[i].product && list[i].product.variants) || [];
        for (var j = 0; j < vs.length; j++) {
          if (vs[j].id === item.variant_id) return list[i];
          if (sku && String(vs[j].sku || '').toLowerCase() === sku) return list[i];
        }
      }
      return null;
    },

    addonForSku: function (sku) {
      var list = this.cfg.addons || [];
      for (var i = 0; i < list.length; i++) {
        var vs = (list[i].product && list[i].product.variants) || [];
        for (var j = 0; j < vs.length; j++) if (String(vs[j].sku || '').toLowerCase() === String(sku).toLowerCase()) return list[i];
      }
      return null;
    },

    /** Parts of a bundle SKU ("M-W+MYG+MYP-2-S-NL"); null when a part is not understood. */
    bundleParts: function (sku) {
      var parts = String(sku || '').split('+').map(function (p) { return p.trim(); });
      var out = { booth: '', myg: false, print: null };
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        var m = BUNDLE_PRINTER.exec(p);
        if (BOOTH_SKU.test(p) && !out.booth) out.booth = p;
        else if (/^MYG$/i.test(p) && !out.myg) out.myg = true;
        else if (m && !out.print) out.print = { rolls: Number(m[1]), type: printTypesDefault()[m[2].toUpperCase() === 'P' ? 0 : 1], logo: m[3].toUpperCase() };
        else return null;
      }
      return out.booth ? out : null;
    },

    /** What a cart line is, for the selection: booth|bundle|printer|logo|rolls|addon|extra|keep. */
    classifyLine: function (item) {
      var cfg = this.cfg;
      var props = noInfo(cleanProps(item.properties)); // print info is not part of what a line is
      var sku = String(item.sku || '').trim();
      var out = { item: item, kind: 'keep', iso: displayToIso(props.Date), sku: sku, props: props };
      if (this.isKeepLine(item)) return out;
      var id = item.variant_id;
      var has = function (product) {
        return !!product && product.variants.some(function (v) { return v.id === id; });
      };
      var plain = !Object.keys(props).length;
      // season bundle (Varighed x Season, owner 2026-10-08): sold whole, never split - the selection cannot show it
      var k0 = sk();
      if (k0 && k0.seasonBundleLine && k0.seasonBundleLine(item) && this.isCameraLine(item)) {
        out.kind = 'whole';
        return out;
      }
      if (/\+/.test(sku)) {
        var parts = this.bundleParts(sku);
        if (parts && this.isCameraLine(item)) {
          out.kind = 'bundle';
          out.parts = parts;
        }
        return out;
      }
      if (has(cfg.printer)) {
        out.kind = 'printer';
      } else if (has(cfg.logo) && plain) {
        out.kind = 'logo';
      } else if (this.productHas(cfg.printRoll, id) && plain) {
        out.kind = 'rolls';
      } else if (this.addonForLine(item)) {
        out.kind = 'addon';
        out.addon = this.addonForLine(item);
      } else if ((this.boothById(id) || BOOTH_SKU.test(sku)) && this.isCameraLine(item)) {
        out.kind = 'booth';
      } else if (plain && this.isExtraLine(item)) {
        out.kind = 'extra';
      }
      return out;
    },

    /** A recommended package or a catalogue product (not excluded) in the cart. */
    isExtraLine: function (item) {
      var id = item.variant_id;
      var rec = (this.cfg.recommendations || []).some(function (r) {
        return !!r.product && r.product.variants.some(function (v) { return v.id === id; });
      });
      if (rec) return true;
      if (!Array.isArray(this.catalogueItems)) return false;
      return this.catalogueItems.some(function (p) { return p.handle === item.handle; });
    },

    /**
     * A booking of its own: the booth, a bundle or another camera-type line. With the live cart a
     * printer or Mygreetings line without a booth is normal, so only these lines can be "another
     * booking" on another date; every other dated line follows the selection's date.
     */
    isBookingLine: function (cl) {
      return cl.kind === 'booth' || cl.kind === 'bundle' || this.isCameraLine(cl.item) || /^M-/i.test(cl.sku);
    },

    /** Dated lines that belong to a booking (they go with that booking). */
    isRentalLine: function (cl) {
      if (cl.kind === 'booth' || cl.kind === 'bundle' || cl.kind === 'printer') return true;
      if (cl.kind === 'addon' && cl.addon && cl.addon.dated) return true;
      if (this.isCameraLine(cl.item)) return true;
      return /^(M-|MYG|GAL-.*-SA$)/i.test(cl.sku);
    },

    /**
     * Date the selection takes from the cart on the first read: the cart's (future) date, else a date
     * the customer picked this session (not one only remembered by the draft), else '' (past date).
     */
    targetIso: function (cartIso, forceIso) {
      var t = tomorrowIso();
      if (forceIso) return forceIso >= t ? forceIso : '';
      if (cartIso && cartIso >= t) return cartIso;
      var stored = readStored();
      return stored && stored >= t && !DATE_FROM_DRAFT ? stored : '';
    },

    /** The booth product of a date offers this duration. */
    boothProductHas: function (iso, duration) {
      var p = this.boothProduct(iso);
      return !!p && p.variants.some(function (v) { return durOf(v, p) === duration; });
    },

    isKeepFixedDate: function (cl) {
      return cl.sku.split('+').some(function (p) { return /^D-/i.test(p.trim()); });
    },

    /** Kept lines that carry a date the selection can move: not a booking, not fixed-date. */
    redatable: function (kept) {
      var self = this;
      return kept.filter(function (cl) {
        return cl.iso && !self.isCameraLine(cl.item) && !/^(booth|bundle|printer)$/.test(cl.kind) && !self.isKeepFixedDate(cl);
      });
    },

    /**
     * Pure: maps /cart.js onto the selection. Returns the plan, never touches state or the cart.
     * Lists hold classified lines {item, kind, iso, ...}.
     * replaceKeys (live cart, after the first read): the other booking's lines, pinned by line key,
     * so a later date change can never make them part of this booking.
     */
    planCartImport: function (cart, forceIso, replaceKeys) {
      var self = this;
      var cfg = this.cfg;
      var items = this.cartLines(cart);
      var all = items.map(function (it) { return self.classifyLine(it); });
      var pinned = replaceKeys || null;
      var isPinned = function (cl) { return !!(pinned && pinned[cl.item.key]); };
      var plan = {
        status: 'none', fingerprint: lineFingerprint(items), cartIso: '', forceIso: forceIso || '', otherDates: [], patch: null,
        managed: [], replace: [], kept: all.slice(), redate: [], all: all, blockReason: '', blockedTitle: '',
        orderNo: (cart.attributes && cart.attributes.orderNo) || '', oldTotal: 0, hasBundle: false,
      };
      var uniq = function (list) { return list.filter(function (x, n, a) { return a.indexOf(x) === n; }); };

      // primary booking date
      var distinct = function (list) {
        var seen = [];
        list.forEach(function (cl) { if (cl.iso && seen.indexOf(cl.iso) < 0) seen.push(cl.iso); });
        return seen.sort();
      };
      var open = all.filter(function (cl) { return !isPinned(cl); });
      var candidates = distinct(open.filter(function (cl) { return cl.kind === 'booth' || cl.kind === 'bundle'; }));
      if (!candidates.length) candidates = distinct(open.filter(function (cl) { return cl.kind !== 'keep'; }));
      if (!candidates.length) candidates = distinct(open);
      var tomorrow = tomorrowIso();
      var cartIso = '';
      var stored = readStored();
      if (forceIso && candidates.indexOf(forceIso) > -1) cartIso = forceIso;
      else if (stored && !DATE_FROM_DRAFT && candidates.indexOf(stored) > -1) cartIso = stored;
      else {
        for (var i = 0; i < candidates.length; i++) {
          if (candidates[i] >= tomorrow) {
            cartIso = candidates[i];
            break;
          }
        }
        if (!cartIso) cartIso = candidates[0] || '';
      }
      plan.cartIso = cartIso;

      // the other bookings in the cart: pinned by key after the first read; on the first read a booth
      // (or bundle) on another date plus that date's rental lines. A printer or Mygreetings line on
      // another date without a booth of its own is not a booking: it follows the selection's date.
      var otherIsos = distinct(all.filter(function (cl) { return cl.iso && cl.iso !== cartIso && self.isBookingLine(cl); }));
      var replace = pinned
        ? all.filter(isPinned)
        : all.filter(function (cl) { return cl.iso && otherIsos.indexOf(cl.iso) > -1 && self.isRentalLine(cl); });
      plan.otherDates = distinct(replace);
      var inReplace = function (cl) { return replace.indexOf(cl) > -1; };
      var groupB = all.filter(function (cl) { return /^(booth|bundle|printer|logo|rolls)$/.test(cl.kind) && !inReplace(cl); });
      var target = this.targetIso(cartIso, forceIso) || cartIso;
      var titles = function (list) { return uniq(list.map(function (cl) { return cl.item.product_title; })).join(', '); };
      var block = function (text, list) {
        plan.status = 'blocked';
        plan.blockReason = text;
        plan.blockedTitle = titles(list);
        plan.replace = [];
        plan.otherDates = [];
        plan.kept = all.slice();
        plan.managed = [];
        plan.redate = self.redatable(plan.kept);
        return plan;
      };
      // a season bundle in the cart is a booking the selection cannot show or change (it would add a second booth and
      // drop its logo/rolls lines): live cart off here, the bundle stays as it is (changed on its product page / /cart)
      var wholes = all.filter(function (cl) { return cl.kind === 'whole'; });
      if (wholes.length) return block(B('whole_bundle_block', 'Pakken ændres på pakkens egen side eller i kurven.'), wholes);

      var patch = {
        duration: '', qtyBooth: 0, qtyPrinter: 0, prints: 0, printer: false, printType: '', logo: '',
        addons: {}, addonVar: {}, addonQty: {}, leadText: {}, extras: {}, reason: '', other: '',
      };
      var used = [];
      if (groupB.length) {
        var durations = [];
        var types = [];
        var logos = [];
        var logoQty = 0;
        var printerDur = [];
        var mygQty = 0;
        var problem = '';
        groupB.forEach(function (cl) {
          var q = cl.item.quantity;
          var v;
          if (cl.kind === 'booth') {
            var d = self.boothDurationForSku(cl.sku, target, cl.item.variant_id);
            if (!d) problem = problem || 'unknown';
            durations.push(d);
            patch.qtyBooth += q;
          } else if (cl.kind === 'bundle') {
            var bp = cl.parts;
            var bd = self.boothDurationForSku(bp.booth, target);
            if (!bd) problem = problem || 'unknown';
            durations.push(bd);
            patch.qtyBooth += q;
            plan.hasBundle = true;
            if (bp.myg) {
              if (self.addonForSku('MYG')) mygQty += q;
              else problem = problem || 'bundle';
            }
            if (bp.print) {
              if (!cfg.printer) problem = problem || 'bundle';
              patch.qtyPrinter += q;
              patch.prints += q * bp.print.rolls * PRINTS_PER_ROLL;
              types.push(self.printTypeValue(bp.print.type));
              var lid = self.logoIdForCode(bp.print.logo);
              if (lid === null) problem = problem || 'bundle';
              else logos.push(lid);
              logoQty += 1;
              printerDur.push(bd);
            }
          } else if (cl.kind === 'printer') {
            patch.qtyPrinter += q;
            patch.prints += q * PRINTS_PER_ROLL;
            var pt = self.printTypes();
            var mode = printModeType(cl.props.print_mode);
            var type = mode && pt.values.indexOf(mode) > -1 ? mode : pt.values[0] || '';
            v = cfg.printer.variants.filter(function (x) { return x.id === cl.item.variant_id; })[0];
            if (pt.index > -1 && v && v.options[pt.index]) type = v.options[pt.index];
            types.push(self.printTypeValue(type));
            printerDur.push(v ? durOf(v, cfg.printer) : '');
          } else if (cl.kind === 'logo') {
            // the logo line's logo (ML/NL/EL); its print type follows the printer (the next pass swaps P/S)
            var lv0 = cfg.logo.variants.filter(function (x) { return x.id === cl.item.variant_id; })[0];
            var lc = lv0 && KIT && KIT.logoCode ? KIT.logoCode(lv0, cfg.logo) : '';
            logos.push(lc === 'ML' ? '' : lc || String(cl.item.variant_id));
            logoQty += 1; // one logo line per booking, whatever its quantity
          } else if (cl.kind === 'rolls') {
            patch.prints += q * PRINTS_PER_ROLL;
          }
          used.push(cl);
        });
        var dur = uniq(durations);
        var maxRolls = Number(self.getAttribute('data-max-rolls')) || 5;
        var maxPrints = PRINTS_PER_ROLL * Math.max(1, patch.qtyPrinter) + (cfg.printRoll && cfg.printRoll.variants[0] ? PRINTS_PER_ROLL * maxRolls : 0);
        var typeList = uniq(types);
        var logoList = uniq(logos);
        var addonSum = {};
        var addonTooMany = false;
        if (mygQty) addonSum[self.addonForSku('MYG').key] = mygQty;
        all.forEach(function (cl) {
          if (cl.kind === 'addon' && !inReplace(cl)) addonSum[cl.addon.key] = (addonSum[cl.addon.key] || 0) + cl.item.quantity;
        });
        Object.keys(addonSum).forEach(function (k) { if (addonSum[k] > QTY_MAX) addonTooMany = true; });
        // Live cart: a printer (with logo and rolls) is in the cart before the booth, and the booth in
        // the cart is only brought in line at "Gå til betaling" - so printers without a booth, more
        // printers than booths in the cart and a printer duration other than the cart booth's are
        // normal. Without a booth the printer's duration is the rental period. Logo or rolls without
        // a printer are imported as "printer off", so the next live pass removes them.
        if (!dur.length) dur = uniq(printerDur);
        var text = '';
        if (problem === 'unknown') text = B('cart_block_unknown', 'Myselfie-varianten i kurven findes ikke her.');
        else if (problem === 'bundle') text = B('cart_block_bundle', 'Pakken i kurven indeholder noget, vælgeren ikke kan vise her.');
        else if (dur.length > 1) text = patch.qtyBooth ? B('cart_block_booth_durations', 'Kurven har Myselfie-bookinger med forskellig lejeperiode.') : B('cart_block_printer_durations', 'Kurven har printere med forskellig lejeperiode.');
        else if (dur.length === 1 && (!dur[0] || !self.durationAllowed(dur[0]) || self.hasPlans() !== (leaseDays(self.boothProduct(target), dur[0]) !== 0) || !self.boothProductHas(target, dur[0]))) {
          text = B('cart_block_duration', 'Lejeperioden »[duration]« kan ikke vælges på denne side.', { duration: dur[0] || '?' });
        } else if (patch.qtyBooth > QTY_MAX || patch.qtyPrinter > QTY_MAX || addonTooMany) text = B('cart_block_qty', 'Der er flere end [n] af samme vare i kurven.', { n: QTY_MAX });
        else if (typeList.length > 1) text = B('cart_block_print_types', 'Kurven har både Polaroid- og Fotostribe-printere.');
        else if (logoList.length > 1 || logoQty > 1) text = B('cart_block_logos', 'Kurven har flere forskellige logoer.');
        else if (patch.qtyPrinter && patch.prints > maxPrints) text = B('cart_block_prints', 'Der er flere prints i kurven, end vælgeren kan vise.');
        if (text) return block(text, groupB);
        patch.duration = dur[0] || '';
        patch.boothFromCart = patch.qtyBooth > 0;
        patch.printer = patch.qtyPrinter > 0;
        patch.printType = typeList[0] || '';
        patch.logo = logoList[0] || '';
        if (mygQty) {
          var myg = self.addonForSku('MYG').key;
          patch.addons[myg] = true;
          patch.addonQty[myg] = mygQty;
        }
        // occasion of the first booth/bundle line
        var first = groupB.filter(function (cl) { return cl.kind === 'booth' || cl.kind === 'bundle'; })[0];
        if (first && first.props._reason) {
          patch.reason = first.props._reason;
          patch.other = first.props._reason_andet || '';
        }
      }

      // add-ons
      var leadSeen = {};
      all.forEach(function (cl) {
        if (cl.kind !== 'addon' || inReplace(cl)) return;
        var key = cl.addon.key;
        var extraProps = Object.keys(cl.props).filter(function (k) { return k !== 'Date' && !(cl.addon.lead && k === 'Samtykketekst'); });
        var total = (patch.addonQty[key] || 0) + cl.item.quantity;
        if (extraProps.length || total > QTY_MAX) {
          cl.kind = 'keep'; // cannot be shown faithfully: stays in the cart as it is
          return;
        }
        // a choice add-on shows one variant: a line of another variant stays in the cart as it is
        if (cl.addon.chooseVariant && patch.addonVar[key] && patch.addonVar[key] !== cl.item.variant_id) {
          cl.kind = 'keep';
          return;
        }
        if (cl.addon.chooseVariant) patch.addonVar[key] = cl.item.variant_id;
        patch.addons[key] = true;
        patch.addonQty[key] = total;
        if (cl.addon.lead && cl.props.Samtykketekst && !leadSeen[key]) patch.leadText[key] = String(cl.props.Samtykketekst);
        leadSeen[key] = true;
        used.push(cl);
      });
      // recommended packages and catalogue products
      all.forEach(function (cl) {
        if (cl.kind !== 'extra' || inReplace(cl)) return;
        var it = cl.item;
        var x = patch.extras[it.variant_id];
        if (x) {
          x.qty += it.quantity;
        } else {
          patch.extras[it.variant_id] = {
            id: it.variant_id,
            handle: it.handle,
            title: it.product_title,
            variantTitle: sk() ? sk().lineTitle(it) : it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '',
            price: it.price,
            available: true,
            qty: it.quantity,
            source: 'cart',
          };
        }
        used.push(cl);
      });

      plan.patch = patch;
      plan.managed = used;
      plan.replace = replace;
      plan.kept = all.filter(function (cl) { return used.indexOf(cl) < 0 && !inReplace(cl); });
      plan.redate = this.redatable(plan.kept);
      plan.oldTotal = used.reduce(function (n, cl) { return n + cl.item.price * cl.item.quantity; }, 0);
      plan.status = used.length || replace.length ? 'import' : 'none';
      if (plan.status === 'import' && !patch.reason && cart.attributes && cart.attributes.booking_reason) {
        patch.reason = cart.attributes.booking_reason;
        patch.other = cart.attributes.booking_reason_other || '';
      }
      return plan;
    },

    /** Loads what classification needs (the catalogue) when a line is not one of ours. */
    prepareCart: function (cart) {
      var self = this;
      var k = sk();
      var fixed = k && cart && cart.items
        ? k.fixCart(cart).then(function (r) {
          // a line of a season product whose season was not the season of its date (price manipulation, a date moved
          // elsewhere): it was swapped for the right variant
          (r.notes || []).forEach(function (n) { self.addNotice(n); });
          return r.cart;
        }, function () { return cart; })
        : Promise.resolve(cart);
      return fixed.then(function (c) {
        var needs = self.cfg.catalogue && !Array.isArray(self.catalogueItems) && self.cartLines(c).some(function (it) {
          return self.classifyLine(it).kind === 'keep' && !self.isKeepLine(it);
        });
        return needs ? self.loadCatalogue().then(function () { return c; }, function () { return c; }) : c;
      });
    },

    /**
     * The cart -> the selection. mode: 'load' (first read), 'manual' (the other booking date was
     * chosen), 'resync' (the cart changed elsewhere) or 'rollback' (a live pass failed). The cart wins
     * for the live products; the booth fields, the occasion and the customer's fields are only taken
     * from the cart on the first read, the date also when a dated line in the cart moved.
     */
    applyCartImport: function (plan, mode) {
      var cs = this.cartSync;
      var s = this.state;
      var first = mode === 'load' || mode === 'manual';
      cs.plan = plan;
      cs.ready = true;
      if (first) {
        cs.replaceKeys = {};
        plan.replace.forEach(function (cl) { cs.replaceKeys[cl.item.key] = true; });
      }
      if (plan.status === 'blocked') {
        // live cart off for this page view: the old behaviour (fresh selection, added at booking)
        cs.status = 'blocked';
        clearTimeout(cs.timer);
        cs.timer = null;
        this.existingItems = plan.all.map(function (cl) { return cl.item; });
        this.hideRestoredNote();
        this.update();
        if (this.pageMode === 'cart' && cs.cart) announceCart(cs.cart, 'import');
        return;
      }
      var patch = plan.patch;
      var hasLive = plan.managed.some(function (cl) { return LIVE_KINDS.test(cl.kind) || cl.kind === 'bundle'; });
      var hasBooth = plan.managed.some(function (cl) { return cl.kind === 'booth' || cl.kind === 'bundle'; });
      // products: the cart wins - except clicks made before the first read when the cart has none
      var keepClicks = mode === 'load' && cs.touched && !hasLive;
      if (!keepClicks) {
        if (mode === 'load' && cs.touched) this.addNotice(B('notice_cart_had_addons', 'Der lå allerede tilvalg i jeres kurv – valgene herunder er hentet fra kurven.'));
        this.applyProducts(patch);
      }
      // the booth is a live line too: its duration and quantity come from the cart on every read
      // (a printer's duration stands in for it on the first read when there is no booth)
      if (!keepClicks && patch.duration && (patch.boothFromCart || first)) {
        s.duration = patch.duration;
        if (patch.boothFromCart) s.qty.booth = Math.max(1, Math.min(QTY_MAX, patch.qtyBooth));
      }
      if (s.printer && s.qty.booth < s.qty.printer) s.qty.booth = s.qty.printer;

      // date
      var emptied = this.takeEmptied();
      var target = s.iso;
      if (first) {
        target = plan.cartIso ? this.targetIso(plan.cartIso, plan.forceIso) : s.iso;
        if (!target && s.iso && s.iso >= tomorrowIso()) target = s.iso;
        // cart page: opening the cart never puts a Myselfie in it - the date comes only from a booth that
        // is already in the cart (or from the customer's click); gift cards, Mygreetings, Mygallery stay as they are
        if (this.pageMode === 'cart' && mode === 'load' && !hasBooth) target = '';
      } else if (plan.cartIso && plan.cartIso >= tomorrowIso() && plan.cartIso !== s.iso && hasBooth) {
        target = plan.cartIso; // moved in another window, or a re-date that did not go through
      } else if (!hasBooth && this.hadBooth) {
        target = ''; // the booth was taken out of the cart (Upcart drawer, another window): no date any more
      }
      if (!first && emptied) target = ''; // the cart was emptied: the date goes with it
      var moved = target !== s.iso;
      s.iso = target;
      if (moved && !target) {
        if (this.pageMode !== 'cart' || mode !== 'load') {
          // the shared date survives a /cart load; otherwise it is forgotten everywhere (header, product page)
          this.forgetDate(true);
        } else if (this.calendar && this.calendar._ready) {
          this.calendar.selected = '';
          this.calendar.render();
        }
      } else if (emptied && !first && readStored()) {
        this.forgetDate(true); // nothing was selected here, but the shared date is still remembered
      }

      if (first) {
        cs.found = hasLive || hasBooth || plan.replace.length > 0;
        cs.touched = false;
        this.fieldsFromCart(patch);
      }
      cs.status = cs.found || hasLive || hasBooth ? 'imported' : 'none';
      this.existingItems = plan.kept.map(function (cl) { return cl.item; });
      if (cs.found) this.hideRestoredNote();

      if (target && (first || moved)) {
        var display = isoToDisplay(target);
        store(display);
        if (this.calendar && this.calendar._ready) {
          var d = isoToDate(target);
          this.calendar.selected = target;
          this.calendar.view = new Date(d.getFullYear(), d.getMonth(), 1);
          this.calendar.render();
        }
        document.dispatchEvent(new CustomEvent('myselfie:date', { detail: { iso: target, display: display, source: CART_SOURCE } }));
      }
      this.update();
      if (this.pageMode === 'cart' && cs.cart) announceCart(cs.cart, 'import');
    },

    /** The live products of a plan onto the selection (the booth, date and fields stay). */
    applyProducts: function (patch) {
      var s = this.state;
      var forced = s.printerForced;
      s.printer = !!patch.printer;
      s.printerForced = s.printer && !!forced; // still the earmark's printer
      if (s.printer) {
        if (patch.printType) s.printType = patch.printType;
        s.qty.printer = Math.max(1, Math.min(QTY_MAX, patch.qtyPrinter || 1));
        if (s.qty.booth < s.qty.printer) s.qty.booth = s.qty.printer;
        s.logo = patch.logo || '';
        s.prints = Math.min(this.maxPrints(), Math.max(this.includedPrints(), patch.prints));
      } else {
        s.logo = '';
        s.prints = PRINTS_PER_ROLL;
      }
      s.addons = {};
      s.addonVar = {};
      s.qty.addons = {};
      Object.keys(patch.addonVar || {}).forEach(function (key) { s.addonVar[key] = patch.addonVar[key]; });
      Object.keys(patch.addons).forEach(function (key) {
        s.addons[key] = true;
        s.qty.addons[key] = Math.max(1, Math.min(QTY_MAX, patch.addonQty[key] || 1));
        if (patch.leadText[key]) s.leadText[key] = patch.leadText[key];
      });
      s.extras = {};
      Object.keys(patch.extras).forEach(function (id) { s.extras[id] = patch.extras[id]; });
    },

    /** First read: customer type, payment, lease plan and occasion from the cart where still empty. */
    fieldsFromCart: function (patch) {
      var s = this.state;
      var el = this.el;
      var attrs = this.lastCartAttributes || {};
      var presetCustomer = this.getAttribute('data-preset-customer');
      var draft = this.draftData || {};
      if (presetCustomer !== 'privat' && presetCustomer !== 'erhverv' && !draft.customer && !sget(CUSTOMER_KEY) && attrs.customer_type) {
        s.customer = attrs.customer_type === 'Erhverv' ? 'erhverv' : 'privat';
      }
      if (s.customer === 'erhverv' && s.pay === 'standard' && !draft.pay) {
        if (attrs.payment_method === 'Faktura') s.pay = 'faktura';
        else if (attrs.payment_method === 'EAN') s.pay = 'ean';
      }
      if (el.ean && !el.ean.value && attrs.ean_number) el.ean.value = attrs.ean_number;
      if (el.att && !el.att.value && attrs.ean_att) el.att.value = attrs.ean_att;
      if (el.invoice && !el.invoice.value && /^Betaling: Faktura\n/.test(this.lastCartNote || '')) {
        el.invoice.value = this.lastCartNote.replace(/^Betaling: Faktura\n/, '').split('\n')[0].trim();
      }
      if (this.hasPlans() && !draft.plan && attrs.lease_plan) {
        if (attrs.lease_plan === 'Månedlig faktura') s.plan = 'monthly';
        else if (/^Fuld betaling/.test(attrs.lease_plan)) s.plan = 'full';
      }
      this.updateBilling(); // also refreshes the occasion list for the customer type
      if (patch.reason && el.reason && !el.reason.value && el.reason.querySelector('option[value="' + String(patch.reason).replace(/"/g, '') + '"]')) {
        el.reason.value = patch.reason;
        el.other.hidden = patch.reason !== 'andet';
        if (patch.reason === 'andet' && !el.other.value) el.other.value = patch.other;
      }
    },

    hideRestoredNote: function () {
      var note = this.querySelector('[data-ps-restored]');
      if (note) note.hidden = true;
      this.restored = false;
    },

    /** The last known cart (customer lines fingerprinted; pinned keys that left the cart are dropped). */
    rememberCart: function (cart) {
      var cs = this.cartSync;
      var self = this;
      this.hadBooth = !!cs.cart && this.cartLines(cs.cart).some(function (it) { return self.isCameraLine(it) && !cs.replaceKeys[it.key]; });
      // the cart went from something to nothing (not mid-booking): the date is forgotten (takeEmptied)
      this._emptied = !!cs.cart && this.cartLines(cs.cart).length > 0 && !!cart && this.cartLines(cart).length === 0;
      cs.cart = cart;
      cs.fingerprint = lineFingerprint(this.cartLines(cart));
      this.lastCartAttributes = cart.attributes || {};
      this.lastCartNote = cart.note || '';
      var keys = {};
      (cart.items || []).forEach(function (it) { if (cs.replaceKeys[it.key]) keys[it.key] = true; });
      cs.replaceKeys = keys;
    },

    /** A cart the module just wrote: remembered and announced to the header. */
    adoptOwnCart: function (cart) {
      if (!cart || !cart.items) return;
      this.rememberCart(cart);
      this.cartSync.lastWrite = Date.now();
      announceCart(cart);
      // our own write emptied the cart (the last line removed here or on /cart): forget the date
      if (this.takeEmptied() && (this.state.iso || readStored())) {
        this.forgetDate(true);
        this.update();
      }
    },

    /** True once after rememberCart saw the cart become empty - never mid-booking or while clearing. */
    takeEmptied: function () {
      var on = !!this._emptied;
      this._emptied = false;
      return on && !this.state.busy && !this.booked && !this.cartSync.clearing;
    },

    /**
     * Forgets the booking date everywhere: the selection, the calendar, wdn_bookingdate, the draft (saved by
     * the next update) and the header / product page ("myselfie:date" with cleared:true).
     * quiet = the caller renders (update) itself.
     */
    forgetDate: function (quiet) {
      this.state.iso = '';
      this._weekendAuto = null;
      DATE_FROM_DRAFT = false;
      sdel(STORAGE_KEY);
      sdel(CARRY_KEY);
      if (this.calendar && this.calendar._ready) {
        this.calendar.selected = '';
        this.calendar.render();
      }
      document.dispatchEvent(new CustomEvent('myselfie:date', { detail: { iso: '', display: '', source: CART_SOURCE, cleared: true } }));
      if (!quiet) this.update();
    },

    /**
     * Reads the cart and brings the selection in line with it (reasons: load, focus, pageshow,
     * upcart, error, manual). Never runs mid-booking; waits for live passes in the queue.
     */
    syncFromCart: function (reason, forceIso) {
      var self = this;
      var cs = this.cartSync;
      if (!this.cfg || !cs) return Promise.resolve();
      if (!cs.enabled) return Promise.resolve(this.loadExistingCart());
      if (this.booked || this.state.busy || cs.clearing) return Promise.resolve();
      if (reason === 'load' || !cs.ready) {
        if (cs.reading) return Promise.resolve();
        cs.reading = true;
        if (cs.status === 'idle') cs.status = 'loading';
        return cartGet()
          .then(function (cart) { return self.prepareCart(cart); })
          .then(function (cart) {
            cs.reading = false;
            if (self.booked || cs.ready) return;
            self.rememberCart(cart);
            announceCart(cart);
            self.applyCartImport(self.planCartImport(cart, ''), 'load');
          }, function () {
            // 429 budget used up or offline: live writes wait; the next change or focus tries again
            cs.reading = false;
            cs.loadFailedAt = Date.now();
            self.update();
          });
      }
      this.flushLive();
      return this.enqueue(function () {
        if (self.booked || self.state.busy || cs.clearing) return;
        return cartGet()
          .then(function (cart) { return self.prepareCart(cart); })
          .then(function (cart) {
            if (self.booked || self.state.busy || cs.clearing) return;
            var changed = lineFingerprint(self.cartLines(cart)) !== cs.fingerprint;
            self.rememberCart(cart);
            if (!changed && reason !== 'manual') return;
            announceCart(cart);
            var plan = self.planCartImport(cart, forceIso || '', reason === 'manual' ? null : cs.replaceKeys);
            self.applyCartImport(plan, reason === 'manual' ? 'manual' : 'resync');
          });
      });
    },

    draftExtras: function () {
      var out = {};
      var extras = this.state.extras;
      Object.keys(extras).forEach(function (id) {
        if (extras[id].source !== 'cart') out[id] = extras[id];
      });
      return out;
    },

    /**
     * "Tøm kurven og start forfra" (one action for the old Fortryd / Fjern dem fra kurven / Start
     * forfra): empties the whole cart - gift cards and other products too - after one question,
     * then the selection starts over (draft, date and customer type are forgotten; page reloads).
     */
    forgetCart: function () {
      var self = this;
      var cs = this.cartSync;
      var cart = cs.cart;
      var hasItems = !cart || cart.item_count > 0; // an unknown cart is emptied too
      if (hasItems && !window.confirm(B('confirm_clear_title', 'Tøm kurven?') + '\n\n' + B('confirm_clear_text', 'Alle varer i kurven fjernes – også gavekort og andre varer – og jeres valg nulstilles.'))) return;
      clearTimeout(cs.timer);
      cs.timer = null;
      cs.clearing = true;
      cs.error = '';
      this.renderLiveStatus();
      return cs.chain
        .then(function () { return hasItems ? cartPost('cart/clear.js', {}) : cart; })
        .then(function (emptied) {
          announceCart(emptied && typeof emptied.item_count === 'number' ? emptied : { item_count: 0, items_subtotal_price: 0, paid_count: 0 });
          sdel(TXN_KEY); // nothing may be "restored" into the emptied cart on the next load
          self.resetDraft();
        }, function () {
          cs.clearing = false;
          cs.error = B('clear_failed', 'Kurven kunne ikke tømmes lige nu. Prøv igen om et øjeblik.');
          self.update();
        });
    },

    /* ---------- live cart: the selection -> the cart ---------- */

    liveActive: function () {
      var cs = this.cartSync;
      return !!(cs && cs.enabled && cs.status !== 'blocked' && !cs.clearing);
    },

    /** Live cart without a date: a dated product (printer, dated add-on) cannot be switched on yet. */
    needsDate: function (dated) {
      return !!dated && this.liveActive() && !this.state.iso;
    },

    addonDated: function (key) {
      var a = (this.cfg.addons || []).filter(function (x) { return x.key === key; })[0];
      return !!(a && a.dated);
    },

    /**
     * What the cart must hold for the selection right now - every line, the booth included, with the
     * same properties "Gå til betaling" writes (so the booking step has nothing left to change).
     * hold: the cart line is left exactly as it is - a dated line while there is no date, and a line
     * that is sold out on the chosen date (it is not moved there; the alert asks the customer).
     */
    liveItems: function () {
      var iso = this.state.iso;
      var display = iso ? isoToDisplay(iso) : '';
      var el = this.el || {};
      var reason = el.reason ? el.reason.value : '';
      var other = (el.other && el.other.value || '').trim();
      var out = this.conflicts();
      return this.lines(iso).map(function (l) {
        var props = {};
        if (l.dated && display) props.Date = display;
        if (l.props) Object.keys(l.props).forEach(function (k) { props[k] = l.props[k]; });
        if (l.reason) {
          props._reason = reason;
          props._reason_andet = reason === 'andet' ? other : '';
        }
        var hold = !!l.dated && (!display || !!out[l.key]);
        return { role: l.key, id: l.variant.id, quantity: l.qty, properties: props, hold: hold };
      });
    },

    /**
     * Live cart: what is sold out on the chosen date for the current selection (dayAvailability,
     * so the printer earmark and the lease window apply). {booth, printer, <add-on key>: true}
     */
    conflicts: function () {
      var s = this.state;
      var out = {};
      if (!this.liveActive() || !s.iso) return out;
      var av = availability();
      if (!av || !av.data) return out;
      var a = this.dayAvailability(s.iso);
      var printerChosen = s.printer && !s.printerForced;
      var boothOk = printerChosen ? a.printer !== 'sold_out' : a.booth !== 'sold_out';
      if (!boothOk && !(printerChosen && a.booth !== 'sold_out')) out.booth = true;
      if (s.printer && a.printer === 'sold_out') out.printer = true;
      (this.cfg.addons || []).forEach(function (x) {
        if (s.addons[x.key] && x.component && a.addons[x.key] === 'sold_out') out[x.key] = true;
      });
      return out;
    },

    liveSig: function () {
      return JSON.stringify([this.state.iso, this.liveItems()]);
    },

    /** After every render (and while typing a consent text): schedules a pass when the lines changed. */
    liveCheck: function (delay) {
      var cs = this.cartSync;
      if (!cs || !this.liveActive() || this.booked || this.state.busy) return;
      if (!cs.ready) {
        // the first read failed (429 budget used up, offline): try again, at most every 10 s
        if (!cs.reading && cs.loadFailedAt && Date.now() - cs.loadFailedAt > 10000) this.syncFromCart('load');
        return;
      }
      var sig = this.liveSig();
      if (sig === cs.sig) {
        if (cs.timer && delay != null && delay < 350) this.scheduleLive(delay); // leaving the text field: now
        return;
      }
      cs.sig = sig;
      this.scheduleLive(delay == null ? 350 : delay);
    },

    /** Coalesces rapid clicks: one pass shortly after the last change. */
    scheduleLive: function (delay) {
      var self = this;
      var cs = this.cartSync;
      clearTimeout(cs.timer);
      cs.timer = setTimeout(function () {
        cs.timer = null;
        self.enqueue(self.livePass);
      }, delay);
      this.renderLiveStatus();
    },

    /** A scheduled pass starts now (before a re-sync or the booking). */
    flushLive: function () {
      var cs = this.cartSync;
      if (!cs.timer) return;
      clearTimeout(cs.timer);
      cs.timer = null;
      this.enqueue(this.livePass);
    },

    /** Cart jobs (live passes, re-syncs) run one at a time, in order. */
    enqueue: function (job) {
      var self = this;
      var cs = this.cartSync;
      cs.jobs++;
      this.renderLiveStatus();
      var run = cs.chain.then(function () { return job.call(self); });
      cs.chain = run.catch(function (e) {
        if (window.console) console.warn('[myselfie] cart', e);
      }).then(function () {
        cs.jobs--;
        // idle again: the summary's "I kurven" chips and the status follow the cart
        if (!cs.jobs && !cs.timer && !self.booked) self.update();
        else self.renderLiveStatus();
      });
      return run;
    },

    /** Resolves when no pass is scheduled or running. A pass that failed is tried once more. */
    whenLiveIdle: function (retried) {
      var self = this;
      var cs = this.cartSync;
      if (!this.liveActive() || !cs.ready) return Promise.resolve();
      // a pass that failed: try once more (it clears the error only if it gets something written)
      if (!retried && cs.error) {
        cs.sig = this.liveSig();
        this.enqueue(this.livePass);
      }
      this.flushLive();
      return cs.chain.then(function () {
        if (cs.timer || cs.jobs) return self.whenLiveIdle(true);
      });
    },

    /** One live pass: the cart is brought in line with the selection. */
    livePass: function () {
      var self = this;
      var cs = this.cartSync;
      if (!this.liveActive() || !cs.ready || this.booked || !cs.cart) return Promise.resolve();
      var before = cs.cart;
      return this.reconcile(before, 0, false).then(function (cart) {
        if (cart !== before) {
          self.adoptOwnCart(cart);
          cs.error = '';
        }
        // Shopify may not take everything (stock, merged lines): then the cart is the truth
        if (!cs.timer && cs.jobs <= 1 && !self.state.busy) {
          var ops = self.liveOps(cart);
          if (ops.blocked || ops.count) {
            self.applyCartImport(ops.plan, 'resync');
            if (!ops.blocked) self.addNotice(B('notice_cart_partial', 'Kurven kunne ikke tage alt, som I valgte – valgene herunder er sat efter kurven.'));
          }
        }
        self.update();
      }, function (err) {
        return self.liveFailed(err);
      });
    },

    /** A pass failed: the selection goes back to what the cart really holds, with a message. */
    liveFailed: function (err) {
      var self = this;
      var cs = this.cartSync;
      var why = err && err.message && !(err instanceof Error) ? String(err.message) : '';
      return cartGet()
        .then(function (cart) { return self.prepareCart(cart); })
        .then(function (cart) {
          self.adoptOwnCart(cart);
          self.applyCartImport(self.planCartImport(cart, '', cs.replaceKeys), 'rollback');
          cs.error = (why ? why + ' ' : B('cart_update_failed_now', 'Kurven kunne ikke opdateres lige nu.') + ' ') + B('choices_reset', 'Jeres valg er sat tilbage, så de passer med kurven – prøv igen om et øjeblik.');
          self.update();
        }, function () {
          cs.error = B('cart_not_responding', 'Kurven svarer ikke lige nu, så jeres seneste valg er ikke lagt i kurven. Prøv igen om et øjeblik.');
          self.update();
        });
    },

    /**
     * The requests that bring a cart in line with the selection (pure). Live lines are matched by
     * variant (first line); a missing one is added, a different quantity or different properties are
     * changed in place, a line that is not chosen (or a duplicate) is removed. With a date, the booth
     * line(s) and dated lines the selection does not show get that date (one date per cart).
     */
    liveOps: function (cart) {
      var self = this;
      var cs = this.cartSync;
      var s = this.state;
      var plan = this.planCartImport(cart, '', cs.replaceKeys);
      var out = { plan: plan, adds: [], removes: [], changes: [], count: 0, blocked: plan.status === 'blocked', split: false };
      if (out.blocked) return out;
      var display = s.iso ? isoToDisplay(s.iso) : '';
      var bundles = plan.managed.filter(function (cl) { return cl.kind === 'bundle'; });
      if (bundles.length && display) {
        out.split = true;
        out.count = 1;
        return out;
      }
      var desired = this.liveItems();
      var boothHeld = desired.some(function (d) { return d.role === 'booth' && d.hold; });
      var pool = plan.managed.filter(function (cl) { return LIVE_KINDS.test(cl.kind) || (cl.kind === 'booth' && !boothHeld); }).map(function (cl) { return cl.item; });
      if (boothHeld) desired = desired.filter(function (d) { return d.role !== 'booth'; }); // the booth line(s) stay as they are
      if (bundles.length) {
        // no date yet: the bundle still holds its printer, logo, rolls and Mygreetings
        var covered = {};
        bundles.forEach(function (cl) {
          covered.booth = true;
          if (cl.parts.print) covered.printer = covered.logo = covered.rolls = true;
          var myg = cl.parts.myg && self.addonForSku('MYG');
          if (myg) covered[myg.key] = true;
        });
        desired = desired.filter(function (d) { return !covered[d.role]; });
      }
      desired.forEach(function (d) {
        var at = -1;
        for (var i = 0; i < pool.length; i++) {
          if (pool[i].variant_id === d.id) {
            at = i;
            break;
          }
        }
        if (at < 0) {
          if (!d.hold) out.adds.push(d);
          return;
        }
        var m = pool.splice(at, 1)[0];
        var propsDiffer = !d.hold && !sameProps(m.properties, d.properties);
        if (m.quantity !== d.quantity || propsDiffer) {
          out.changes.push({ key: m.key, id: m.variant_id, quantity: d.quantity, properties: propsDiffer ? withPrivateProps(m.properties, d.properties) : null });
        }
      });
      out.removes = pool;
      if (display) {
        plan.redate.forEach(function (cl) {
          if (cl.props.Date === display) return;
          var props = {};
          Object.keys(cl.item.properties || {}).forEach(function (k) { props[k] = cl.item.properties[k]; });
          props.Date = display;
          out.changes.push({ key: cl.item.key, id: cl.item.variant_id, quantity: cl.item.quantity, properties: props });
        });
      }
      out.count = out.adds.length + out.removes.length + out.changes.length;
      return out;
    },

    /**
     * Applies liveOps to the cart. A failure is retried once against a freshly read cart (a line key
     * may have changed in another window); a 422 on an add (sold out, stock) is final.
     */
    reconcile: function (cart, attempt, splitDone) {
      var self = this;
      var ops = this.liveOps(cart);
      if (ops.blocked || !ops.count) return Promise.resolve(cart);
      if (ops.split && splitDone) return Promise.reject({ final: true, message: B('split_failed', 'Pakken i kurven kunne ikke deles op.') });
      var run = ops.split ? this.splitBundles() : this.runOps(ops);
      return run.then(function (next) {
        return ops.split ? self.reconcile(next, attempt, true) : next;
      }, function (err) {
        if (attempt > 0 || (err && err.final)) throw err;
        return cartGet().then(function (fresh) { return self.reconcile(fresh, 1, splitDone); }, function () { throw err; });
      });
    },

    /**
     * Adds first (one quiet add.js), then removals, then changes - each by line key with change.js,
     * which fails safely on a stale key (update.js would fall back to the variant). Resolves the cart.
     */
    runOps: function (ops) {
      var latest = null;
      var chain = Promise.resolve();
      if (ops.adds.length) {
        chain = chain.then(function () {
          var items = ops.adds.map(function (d) {
            var it = { id: d.id, quantity: d.quantity };
            if (Object.keys(d.properties || {}).length) it.properties = d.properties;
            return it;
          });
          return cartPost(QUIET_ADD, { items: items }).catch(function (err) {
            if (err && err.status === 422) err.final = true; // sold out / not enough stock: a retry will not help
            throw err;
          });
        });
      }
      ops.removes.forEach(function (line) {
        chain = chain.then(function () {
          return cartPost('cart/change.js', { id: line.key, quantity: 0 }).then(function (c) { latest = c; });
        });
      });
      ops.changes.forEach(function (ch) {
        chain = chain.then(function () {
          var body = { id: ch.key, quantity: ch.quantity };
          if (ch.properties) body.properties = ch.properties;
          return cartPost('cart/change.js', body).then(function (c) { latest = c; });
        });
      });
      return chain.then(function () { return latest || cartGet(); });
    },

    /**
     * A bundle in the cart (M-…+MYP…, M-…+MYG) is split as soon as there is a date: the booth part
     * becomes a normal M- line (untouched until "Gå til betaling"), the printer, logo, rolls and
     * Mygreetings become live lines. One transaction: the bundle comes back if the add fails.
     */
    splitBundles: function () {
      var self = this;
      var cs = this.cartSync;
      var s = this.state;
      var display = isoToDisplay(s.iso);
      return cartGet().then(function (cart) { return self.prepareCart(cart); }).then(function (cart) {
        var plan = self.planCartImport(cart, '', cs.replaceKeys);
        var bundles = plan.status === 'blocked' ? [] : plan.managed.filter(function (cl) { return cl.kind === 'bundle'; });
        if (!bundles.length) return cart;
        var adds = [];
        var oldTotal = 0;
        var roles = { booth: true };
        for (var i = 0; i < bundles.length; i++) {
          var cl = bundles[i];
          if (!self.boothVariant(s.iso, self.boothDurationForSku(cl.parts.booth, s.iso))) return Promise.reject({ final: true, message: B('split_failed', 'Pakken i kurven kunne ikke deles op.') });
          oldTotal += cl.item.final_line_price != null ? cl.item.final_line_price : cl.item.price * cl.item.quantity;
          if (cl.parts.print) roles.printer = roles.logo = roles.rolls = true;
          var myg = cl.parts.myg && self.addonForSku('MYG');
          if (myg) roles[myg.key] = true;
        }
        // every live line the cart does not hold yet (the booth part, the bundle's printer, logo, rolls, Mygreetings)
        var pool = plan.managed.filter(function (c) { return LIVE_KINDS.test(c.kind) || c.kind === 'booth'; }).map(function (c) { return c.item; });
        self.liveItems().forEach(function (d) {
          for (var j = 0; j < pool.length; j++) {
            if (pool[j].variant_id === d.id) {
              pool.splice(j, 1);
              return;
            }
          }
          if (d.hold) return;
          var it = { id: d.id, quantity: d.quantity };
          if (Object.keys(d.properties).length) it.properties = d.properties;
          adds.push(it);
        });
        var newTotal = self.lines(s.iso).filter(function (l) { return roles[l.key]; })
          .reduce(function (n, l) { return n + l.variant.price * l.qty; }, 0);
        return self.writeCart(bundles.map(function (c) { return c.item; }), adds, [], display).then(function () {
          cs.splitNote = B('split_note', 'Pakken i kurven er delt op i de enkelte varer, så I kan ændre dem.') +
            (newTotal && newTotal !== oldTotal ? ' ' + B('split_prices', 'Pakken kostede [old] – de enkelte varer koster [new].', { old: kr(oldTotal), new: kr(newTotal) }) : '');
          return cartGet();
        });
      });
    },

    /** Summary chip "I kurven": the line is in the cart as shown (dated lines with this date). */
    lineInCart: function (l) {
      var cs = this.cartSync;
      if (!this.liveActive() || !cs.ready || !cs.cart) return false;
      var display = this.state.iso ? isoToDisplay(this.state.iso) : '';
      return (cs.cart.items || []).some(function (it) {
        if (it.variant_id !== l.variant.id || it.quantity !== l.qty || isAutomaticLine(it, this.cfg.checkout)) return false;
        return !l.dated || (it.properties || {}).Date === display;
      }, this);
    },

    /** Automatic changes the customer should know about (mint note, not an error). */
    addNotice: function (text) {
      var cs = this.cartSync;
      if (cs.notices.indexOf(text) < 0) cs.notices.push(text);
      cs.noticeSeq++;
    },

    clearNotices: function () {
      if (this.cartSync) this.cartSync.notices = [];
    },

    /** Sold out on the chosen date: say what and offer the two ways out (the line keeps its old date meanwhile). */
    conflictHtml: function () {
      var out = this.conflicts();
      var s = this.state;
      var self = this;
      if (!s.iso) return '';
      var day = alertDate(s.iso);
      var html = '';
      var alert = function (text, actions) {
        html += '<div class="myselfie-ps__livecart-error myselfie-ps__livecart-conflict" role="alert">' + ICON_ALERT +
          '<div><p>' + escapeHtml(text) + '</p><div class="myselfie-ps__livecart-actions">' + actions +
          '<button type="button" class="myselfie-ps__more" data-ps-goto="date">' + escapeHtml(B('pick_other_date', 'Vælg anden dato')) + '</button></div></div></div>';
      };
      var remove = function (action, value, label) {
        return '<button type="button" class="myselfie-ps__more" data-ps-action="' + action + '"' + (value ? ' data-value="' + escapeHtml(value) + '"' : '') + '>' + escapeHtml(label) + '</button>';
      };
      if (out.booth) alert(B('alert_booth_sold_out', 'Myselfie er udsolgt d. [date] – vælg en anden dato.', { date: day }), '');
      if (out.printer) {
        // the card title is marketing copy ("Med udskrifter"): the alert names the product
        alert(B('alert_printer_sold_out', 'Printeren er udsolgt d. [date] – vælg en anden dato eller fjern printeren.', { date: day }), remove('printer', '', B('remove_printer', 'Fjern printeren')));
      }
      (this.cfg.addons || []).forEach(function (x) {
        if (out[x.key]) alert(B('alert_addon_sold_out', '[title] er udsolgt d. [date] – vælg en anden dato eller fjern [title].', { title: x.label, date: day }), remove('addon', x.key, B('remove_item', 'Fjern [title]', { title: x.label })));
      });
      return html;
    },

    ctaLabel: function () {
      var label = (this.el.book && this.el.book.getAttribute('data-label')) || B('go_to_payment', 'Gå til betaling');
      return label.replace(/[\s→›»>]+$/, '');
    },

    /** Status under the summary: syncing, what is in the cart, notices, errors, "Tøm kurven". */
    liveStatusHtml: function () {
      var cs = this.cartSync;
      var self = this;
      if (!cs.enabled || cs.status === 'blocked') return '';
      var cart = cs.cart;
      var lines = cart ? this.cartLines(cart) : [];
      var cta = B('quoted', '»[text]«', { text: this.ctaLabel() });
      var spinner = '<span class="myselfie-ps__livecart-spin" aria-hidden="true"></span>';
      var check = '<svg class="myselfie-ps__livecart-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.2 10.5a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L20.5 8H6.2"/><circle cx="9.5" cy="19.5" r="1.2"/><circle cx="17" cy="19.5" r="1.2"/></svg>';
      var row = function (icon, text) { return '<p class="myselfie-ps__livecart-row">' + icon + '<span>' + escapeHtml(text) + '</span></p>'; };
      var html = '';
      if (cs.error) html += '<p class="myselfie-ps__livecart-error" role="alert">' + ICON_ALERT + '<span>' + escapeHtml(cs.error) + '</span></p>';
      var conflicts = this.conflictHtml();
      html += conflicts;
      cs.notices.concat(cs.message ? [cs.message] : []).forEach(function (n) {
        html += '<p class="myselfie-ps__livecart-note">' + escapeHtml(n) + '</p>';
      });
      if (cs.clearing) html += row(spinner, B('clearing_cart', 'Tømmer kurven…'));
      else if (cs.ready && (cs.timer || cs.jobs)) html += row(spinner, B('updating_cart', 'Opdaterer kurven…'));
      else if (cs.ready && lines.length) {
        var hasBooth = lines.some(function (it) { return self.isCameraLine(it) && !cs.replaceKeys[it.key]; });
        html += row(check, hasBooth
          ? (conflicts ? B('live_in_cart_conflict', 'Jeres valg ligger i kurven. Løs det udsolgte herover, før I går til betaling.') : B('live_in_cart', 'Jeres valg ligger i kurven. Tryk [cta], når I er klar.', { cta: cta }))
          : B('live_addons_in_cart', 'Tilvalgene ligger i kurven. Vælg en dato, så lægger vi Myselfie i kurven.'));
      }
      if (cs.ready && cart && cart.item_count > 0 && !cs.clearing) {
        html += '<button type="button" class="myselfie-ps__more myselfie-ps__forget" data-ps-action="cart-forget">' + escapeHtml(B('clear_cart_restart', 'Tøm kurven og start forfra')) + '</button>';
      }
      return html;
    },

    renderLiveStatus: function () {
      var box = this.el && this.el.livecart;
      if (!box || !this.cartSync) return;
      var html = this.liveStatusHtml();
      setHtml(box, html);
      box.hidden = !html;
      // one wording for starting over: the remembered-choices note says what its button does
      var restart = this.querySelector('[data-ps-restored] [data-ps-action="draft-reset"]');
      var cart = this.cartSync.cart;
      if (restart && this.cartSync.enabled) restart.textContent = cart && cart.item_count > 0 ? B('clear_cart_restart', 'Tøm kurven og start forfra') : B('start_over', 'Start forfra');
    },

    /* ---------- cart sync: the booking transaction ---------- */

    /** Persisted snapshot of what is about to leave the cart (survives a closed tab). */
    writeTxn: function (data) {
      data.v = 1;
      data.at = Date.now();
      sset(TXN_KEY, JSON.stringify(data));
    },

    /** Re-adds removed lines in one request, then line by line. Resolves the snapshots that could not come back. */
    rollbackRemoved: function (removed) {
      if (!removed.length) return Promise.resolve([]);
      return cartPost(QUIET_ADD, { items: removed.map(snapshotToAdd) })
        .then(function () { return []; })
        .catch(function () {
          var failed = [];
          return removed.reduce(function (chain, sn) {
            return chain.then(function () {
              return cartPost(QUIET_ADD, { items: [snapshotToAdd(sn)] }).catch(function () { failed.push(sn); });
            });
          }, Promise.resolve()).then(function () { return failed; });
        });
    },

    /**
     * Looks at the real cart and puts back what is missing of `removed`. When `added` is given and
     * the cart already holds all of it, the add went through (a lost answer): nothing is restored.
     * Resolves {addWent, failed}; a cart that cannot be read counts as "nothing restored".
     */
    restoreMissing: function (removed, added) {
      var self = this;
      return cartGet().then(function (cart) {
        if (added && added.length && cartHasAll(cart, added)) return { addWent: true, failed: [] };
        var missing = removed.filter(function (sn) {
          return !(cart.items || []).some(function (it) { return it.variant_id === sn.id && sameProps(it.properties, sn.properties); });
        });
        return self.rollbackRemoved(missing).then(function (failed) { return { addWent: false, failed: failed }; });
      }, function () {
        return { addWent: false, failed: removed };
      });
    },

    /**
     * On load: a tab closed between "remove" and "add" left the cart without the old booking and
     * without the new one. Put the removed lines back (unless the add went through).
     */
    restorePendingTxn: function () {
      var raw = sget(TXN_KEY);
      if (!raw) return Promise.resolve();
      var txn = null;
      try {
        txn = JSON.parse(raw);
      } catch (e) {
        /* unreadable */
      }
      if (!txn || txn.v !== 1 || !txn.removed || Date.now() - txn.at > TXN_MAX_AGE || (txn.phase !== 'removing' && txn.phase !== 'removed')) {
        sdel(TXN_KEY);
        return Promise.resolve();
      }
      return this.restoreMissing(txn.removed, txn.added).then(function (res) {
        if (!res.failed.length) sdel(TXN_KEY); // otherwise the next load tries again, until it is too old
      });
    },

    /**
     * "Book nu" with cart sync: read the cart, check it is the one the customer saw, then remove
     * exactly the lines the selection manages (plus the other booking), add the selection and move
     * the dated leftovers.
     */
    commitBooking: function (desired, display) {
      var self = this;
      var cs = this.cartSync;
      var displayIso = displayToIso(display);
      return cartGet()
        .then(function (cart) { return self.prepareCart(cart); })
        .then(function (cart) {
          var blocked = cs.status === 'blocked';
          var plan2 = self.planCartImport(cart, '', blocked ? null : cs.replaceKeys);
          // live cart: every line the selection shows is ours (blocked = the old "on top" behaviour)
          var managedMode = !blocked && plan2.status === 'import';
          var fresh = lineFingerprint(self.cartLines(cart)) !== cs.fingerprint;
          var known = cs.ready;
          self.rememberCart(cart);
          // the cart changed since the customer saw it: refresh the selection and let them look
          if (fresh) {
            if (!blocked) self.applyCartImport(plan2, known ? 'resync' : 'load');
            cs.message = B('cart_stale', 'Kurven er ændret, siden I åbnede siden. Vi har hentet den igen – tjek jeres valg, og tryk [cta] igen.', { cta: B('quoted', '»[text]«', { text: self.ctaLabel() }) });
            throw { cancelled: true, stale: true };
          }
          var managed = managedMode ? plan2.managed.map(function (cl) { return cl.item; }) : [];
          var fixes = self.dateFixups(managedMode ? plan2.kept : plan2.all, displayIso);
          if (fixes.conflicts.length) {
            var ok = window.confirm(
              B('replace_booking_1', 'Din kurv har allerede en booking d. [date].', { date: fixes.conflicts[0].item.properties.Date }) + '\n' +
              B('replace_booking_2', 'Vil du erstatte den med din nye booking d. [date]?', { date: display })
            );
            if (!ok) throw { cancelled: true };
          }
          var diff = diffCart(managed, desired);
          var removeLines = diff.remove.slice();
          var seen = {};
          removeLines.forEach(function (it) { seen[it.key] = true; });
          (managedMode ? plan2.replace : []).concat(fixes.conflicts).forEach(function (cl) {
            if (seen[cl.item.key]) return;
            seen[cl.item.key] = true;
            removeLines.push(cl.item);
          });
          var redate = fixes.redate.filter(function (cl) { return !seen[cl.item.key]; });
          if (plan2.orderNo && desired.length) self._clearOrderNo = plan2.orderNo;
          return self.writeCart(removeLines, diff.add, redate, display);
        });
    },

    /** Dated lines against the booking date: bookings that must go (after a confirm) and lines that move along. */
    dateFixups: function (pool, displayIso) {
      var self = this;
      var conflicts = [];
      var redate = [];
      pool.forEach(function (cl) {
        if (!cl.iso || cl.iso === displayIso) return;
        var fixed = self.isCameraLine(cl.item) || /^(booth|bundle|printer)$/.test(cl.kind) || self.isKeepFixedDate(cl);
        if (fixed) conflicts.push(cl);
        else redate.push(cl);
      });
      return { conflicts: conflicts, redate: redate };
    },

    /**
     * Remove (one request), add (one request), re-date. Remove first, add second: add.js merges
     * identical lines and is all-or-nothing, so a failed add leaves a clean cart to restore from
     * the snapshot (sessionStorage, so a closed tab is covered too).
     */
    writeCart: function (removeLines, add, redate, display) {
      var self = this;
      var removed = removeLines.map(lineSnapshot);
      var added = add.map(function (a) { return { id: a.id, quantity: a.quantity, properties: cleanProps(a.properties) }; });
      var updates = {};
      removeLines.forEach(function (it) { updates[it.key] = 0; });
      var failedRestore = function (failed) {
        return {
          message: B('restore_failed', 'Vi kunne ikke lægge alle varer tilbage i kurven ([items]). Prøv igen – eller kontakt os, så hjælper vi.', { items: failed.map(function (sn) { return self.nameForVariant(sn.id); }).join(', ') }),
        };
      };
      var chain = Promise.resolve();
      if (removeLines.length) {
        chain = chain.then(function () {
          self.writeTxn({ phase: 'removing', removed: removed, added: added });
          return cartPost('cart/update.js', { updates: updates }).then(function () {
            self.writeTxn({ phase: 'removed', removed: removed, added: added });
          }, function (err) {
            // an answer that got lost may still have removed the lines: look and restore
            return self.restoreMissing(removed, null).then(function (res) {
              if (res.failed.length) throw failedRestore(res.failed);
              sdel(TXN_KEY);
              throw err;
            });
          });
        });
      }
      if (add.length) {
        chain = chain.then(function () {
          return cartPost(QUIET_ADD, { items: add }).then(function () {
            sdel(TXN_KEY);
          }, function (err) {
            // a network error may hide that the add went through; an API answer never does
            return self.restoreMissing(removed, err instanceof Error ? added : null).then(function (res) {
              if (res.addWent) {
                sdel(TXN_KEY);
                return;
              }
              if (res.failed.length) throw failedRestore(res.failed);
              sdel(TXN_KEY);
              throw { message: B('cart_write_failed', 'Vi kunne ikke opdatere kurven. Jeres varer ligger i kurven som før.') };
            });
          });
        });
      } else {
        chain = chain.then(function () { sdel(TXN_KEY); });
      }
      // dated lines the selection cannot represent follow the new date
      return chain.then(function () {
        return redate.reduce(function (c, cl) {
          return c.then(function () {
            var props = {};
            var src = cl.item.properties || {};
            Object.keys(src).forEach(function (k) { props[k] = src[k]; });
            props.Date = display;
            return cartPost('cart/change.js', { id: cl.item.key, quantity: cl.item.quantity, properties: props }).catch(function () {
              throw { message: B('redate_failed', '[title] kunne ikke flyttes til d. [date]. Prøv igen.', { title: cl.item.product_title, date: longDate(displayToIso(display)) }) };
            });
          });
        }, Promise.resolve());
      });
    },

    nameForVariant: function (id) {
      var found = '';
      var cfg = this.cfg;
      var scan = function (p) {
        if (p && !found && p.variants.some(function (v) { return v.id === id; })) found = p.title;
      };
      Object.keys(cfg.booth).forEach(function (h) { scan(cfg.booth[h]); });
      scan(cfg.printer);
      scan(cfg.logo);
      scan(cfg.printRoll);
      (cfg.addons || []).forEach(function (a) { scan(a.product); });
      var x = this.state.extras[id];
      return found || (x && x.title) || B('item', 'vare');
    },

    /* ---------- cart sync: banner ---------- */

    cartBannerHtml: function () {
      var cs = this.cartSync;
      var s = this.state;
      var self = this;
      var plan = cs.plan;
      var st = cs.status;
      if (!cs.enabled || !plan || (st !== 'blocked' && !cs.found)) return '';
      var icon = '<span class="myselfie-ps__fromcart-icon"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.2 10.5a1.5 1.5 0 0 0 1.5 1.2h8.6a1.5 1.5 0 0 0 1.5-1.2L20.5 8H6.2"/><circle cx="9.5" cy="19.5" r="1.2"/><circle cx="17" cy="19.5" r="1.2"/></svg></span>';
      var wrap = function (title, body, actions) {
        return icon + '<div class="myselfie-ps__fromcart-body"><p><strong>' + escapeHtml(title) + '</strong></p>' + body +
          (actions ? '<div class="myselfie-ps__fromcart-actions">' + actions + '</div>' : '') + '</div>';
      };
      var btn = function (action, label, value) {
        return '<button type="button" class="myselfie-ps__more" data-ps-action="' + action + '"' + (value ? ' data-value="' + escapeHtml(value) + '"' : '') + '>' + escapeHtml(label) + '</button>';
      };
      var note = function (text) { return '<p class="myselfie-ps__fromcart-note">' + escapeHtml(text) + '</p>'; };
      var cta = B('quoted', '»[text]«', { text: this.ctaLabel() });
      var orderNote = plan.orderNo ? note(B('banner_order_no', 'Ordrenummeret #[order] i kurven fjernes, når I trykker [cta] – en ny booking bliver sin egen ordre.', { order: plan.orderNo, cta: cta })) : '';

      if (st === 'blocked') {
        return wrap(B('banner_blocked_title', 'Jeres booking i kurven kan ikke ændres her'),
          '<p>' + escapeHtml(B('banner_blocked', '[items] bevares i kurven. Det, I vælger her, kommer oveni.', { items: plan.blockedTitle || B('the_items', 'Varerne') })) + '</p>' +
          (plan.blockReason ? note(plan.blockReason) : '') + orderNote, '');
      }
      var cart = cs.cart;
      var hasBooth = !!cart && this.cartLines(cart).some(function (it) { return self.isCameraLine(it) && !cs.replaceKeys[it.key]; });
      var body = '<p>' + escapeHtml(B('banner_live', 'Det, der ligger i kurven, er valgt herunder. Når I ændrer dato, lejeperiode eller tilvalg, ændres kurven med det samme')) +
        escapeHtml(hasBooth ? ' ' + B('banner_live_cta', '– tryk [cta], når I er klar.', { cta: cta }) : '.') + '</p>';
      var actions = '';
      if (plan.cartIso && plan.cartIso < tomorrowIso() && !s.iso) {
        body += note(B('banner_past_date', 'Datoen i kurven (d. [date]) er passeret. Vælg en ny dato i kalenderen – vi flytter varerne med.', { date: longDate(plan.cartIso) }));
      }
      var pinned = Object.keys(cs.replaceKeys).length;
      (pinned ? plan.otherDates || [] : []).forEach(function (iso) {
        body += note(B('banner_other_date', 'Kurven har også en booking d. [date]. En ordre kan kun have én dato, så den fjernes fra kurven, når I trykker [cta].', { date: longDate(iso), cta: cta }));
        actions += btn('cart-use-date', B('banner_use_date', 'Brug d. [date] i stedet', { date: longDate(iso) }), iso);
      });
      if (cs.splitNote) body += note(cs.splitNote);
      body += orderNote;
      // cart page: the cart is the page itself - the banner only speaks when it has something to say
      if (this.pageMode === 'cart' && body.indexOf('myselfie-ps__fromcart-note') < 0 && !actions) return '';
      return wrap(B('banner_title', 'Vi har hentet jeres kurv'), body, actions);
    },

    renderCartBanner: function () {
      var box = this.el && this.el.fromcart;
      if (!box) return;
      var html = this.cartBannerHtml();
      setHtml(box, html);
      box.hidden = !html;
    },
  });
})();
